import React from 'react';
import type { PricingTierId } from '../constants/pricingTiers';
import { scrollElementWithinConsole } from '../consoleScroll';
import {
  type ChecklistDependencyState,
  type ComplianceChecklistItemDef,
  type ComplianceConsoleDefinition,
  type ComplianceConsoleKind,
  type ComplianceConsoleState,
  type ComplianceItemStatus,
  type CompliancePillarDef,
  type ComplianceStepEvidence,
  type WebsiteComplianceLastScan,
  type WebsiteComplianceScanHistoryEntry,
  type WebsiteScanCadence,
  checklistDependencyDepth,
  checklistDependencyStates,
  checklistItemWaiting,
  isDodWaitingOnExternal,
  outsideWaitFor,
  dodGateLabel,
  dodItemsForGate,
  emptyComplianceState,
  isItemCompleteLocked,
  itemCompleteLabel,
  itemCompleteLockedReason,
  itemIncludedInPlan,
  itemPlanMin,
  bootstrapCompanyFromTargetName,
  BUSINESS_CREATION_COMPLIANCE_CONSOLE,
  COMPLIANCE_CONSOLES,
  loadComplianceConsoleState,
  PLAN_PUBLIC_NAME,
  planFromFeatures,
  nextDodDecisions,
  normalizeComplianceConsoleState,
  saveComplianceConsoleState,
  selectDodCompany,
  shortDependencyLabel,
  syncActiveCompanyCheck,
  taskCredentialsNeed,
  verifiedStepCounts,
  websiteScanUrlFromName,
  workflowStatusLabel,
  queueCloudReadOnlyReview,
} from '../constants/complianceChecklists';
import {
  CMMC_L1_PRACTICES,
  CMMC_L2_PRACTICES,
  CMMC_LEVEL_LABELS,
  assessmentFromGateway,
  classifyDodLevel,
  emptyDodEvidenceConnectors,
  emptyDodTaxonomy,
  newDodId,
  type DataHandling,
  type PracticeDisposition,
} from '../constants/dodCmmcCatalog';
import DodLevelWorkbench from './DodLevelWorkbench';
import ComplianceCalendar from './ComplianceCalendar';
import ComplianceKpiDashboard from './ComplianceKpiDashboard';
import type { PortfolioCalendarEvent } from './ComplianceCalendar';
import {
  calendarSuggestionsFor,
  calendarTaskId,
  parseCalendarEvents,
  upsertCalendarDate,
  type ComplianceCalendarEvent,
} from '../complianceCalendar';
import { publishedDeadlinesFor } from '../publishedDeadlines';
import DodConsoleGuide from './DodConsoleGuide';
import FeatureAgentGates from './FeatureAgentGates';
import AgentRunBadges from './AgentRunBadges';
import WebsiteAgentInstallCard from './WebsiteAgentInstallCard';
import ControlProofProposals, { type RegistryProposal } from './ControlProofProposals';
import DodVerifySteps from './DodVerifySteps';
import { GlanceBars, GlanceColumns, GlanceDonut, GlanceFlow, GlanceTimeline } from './GlanceVisuals';
import { TaskAssistButtons, TaskAssistPanel, taskFileInputId } from './TaskAssist';
import DashboardWidgetEditor from './DashboardWidgetEditor';
import {
  COMPLIANCE_DASHBOARD_WIDGETS,
  normalizeLayout,
  readAccountPreference,
  readLayout,
  writeAccountPreference,
  writeLayout,
  type DashboardLayout,
} from '../dashboardWidgets';
import ComplianceWorkflowLegend, { WorkflowLaneChip } from './ComplianceWorkflowLegend';
import type { WorkflowLane } from '../constants/complianceWorkflowGuardrails';
import HelpBadge from './HelpBadge';
import ComplianceCoach from './ComplianceCoach';
import { COACH_SCRIPTS, itemCoachMode } from '../constants/complianceCoach';
import DodTermText from '../DodTermText';
import { FeatureMark } from './FeatureMark';
import BusinessCreationInterview, {
  BusinessCreationSummary,
  formationBackFrom,
  formationDraftFrom,
} from './BusinessCreationInterview';
import {
  alignBusinessCreationToCompany,
  businessCreationPillars,
  ensureBusinessCompany,
  isBusinessCreationComplete,
  type BusinessQuestionId,
} from '../constants/businessCreationInterview';
import WillsTrustsInterview, { willsBackFrom, WillsTrustsSummary } from './WillsTrustsInterview';
import {
  answerWillsQuestion,
  isWillsTrustsComplete,
  willsTrustsPillars,
  type WillsQuestionId,
} from '../constants/willsTrustsInterview';
import { type DodGlossaryTermId } from '../dodGlossary';
import { localizeComplianceConsole } from '../i18n/complianceCatalogLocale';
import './ComplianceConsolePanel.css';

type SamMonitorSnapshot = {
  configured?: boolean;
  registration_status?: string;
  exclusion_status?: string;
  uei_sam?: string;
  cage_code?: string;
  expiration_date?: string;
  found?: boolean;
  error?: string;
  fetched_at?: string;
};

type SamOpportunity = {
  notice_id?: string;
  title?: string;
  solicitation_number?: string;
  posted_date?: string;
  notice_type?: string;
  naics?: string;
  set_aside?: string;
  department?: string;
  defense_related?: boolean;
  public_url?: string;
};

type SamNotice = {
  subject?: string;
  from_addr?: string;
  snippet?: string;
  received_at?: string;
};

type SamMonitorPayload = {
  catalog?: { configured?: boolean; scraping?: boolean };
  snapshot?: SamMonitorSnapshot | null;
  poll?: { last_error?: string | null; requests_remaining?: number };
  opportunities?: SamOpportunity[];
  notices?: SamNotice[];
};

type ComplianceConsolePanelProps = {
  definition: ComplianceConsoleDefinition;
  language?: 'en' | 'es';
  reviewerLabel?: string;
  gatewayBaseUrl?: string;
  accessToken?: string | null;
  platformAdmin?: boolean;
  effectiveFeatures?: string[];
  /** Viewer job: open entitled consoles read-only (Admin/Contributor write). */
  workspaceReadOnly?: boolean;
  /** Open one step on top of the page that listed it. */
  taskFocus?: { companyId: number; projectId: string; taskId: string; companyName?: string; entityName?: string } | null;
};

const STATUS_OPTIONS: { value: ComplianceItemStatus; labelEn: string; labelEs: string }[] = [
  { value: 'not_started', labelEn: 'Not started', labelEs: 'Sin iniciar' },
  { value: 'in_progress', labelEn: 'In progress', labelEs: 'En progreso' },
  { value: 'reviewed', labelEn: 'Reviewed', labelEs: 'Revisado' },
  { value: 'gap', labelEn: 'Gap / needs work', labelEs: 'Brecha / pendiente' },
];

const statusOptionsFor = (kind: ComplianceConsoleKind): { value: ComplianceItemStatus; labelEn: string; labelEs: string }[] => {
  if (kind === 'dod-contracts-compliance') {
    return STATUS_OPTIONS.map((option) => (
      option.value === 'reviewed'
        ? { ...option, labelEn: 'Finished', labelEs: 'Terminado' }
        : option
    ));
  }
  if (kind === 'website-compliance') {
    return STATUS_OPTIONS.map((option) => {
      if (option.value === 'reviewed') {
        return { ...option, labelEn: 'Compliant', labelEs: 'Cumple' };
      }
      if (option.value === 'gap') {
        return { ...option, labelEn: 'Not compliant', labelEs: 'No cumple' };
      }
      return option;
    });
  }
  return STATUS_OPTIONS;
};

const statusLabel = (
  status: ComplianceItemStatus,
  language: 'en' | 'es',
  kind: ComplianceConsoleKind = 'website-compliance',
): string => workflowStatusLabel(status, language, kind);

const filedStamp = (language: 'en' | 'es', kind: ComplianceConsoleKind): string => (
  kind === 'dod-contracts-compliance'
    ? (language === 'es' ? 'Terminado' : 'Finished')
    : (language === 'es' ? 'Revisado' : 'Last reviewed')
);

const pillarProgress = (
  pillar: CompliancePillarDef,
  state: ComplianceConsoleState,
  plan: PricingTierId = 'enterprise',
): { reviewed: number; gaps: number; total: number } => {
  let reviewed = 0;
  let gaps = 0;
  const items = pillar.items.filter((item) => itemIncludedInPlan(item, plan));
  items.forEach((item) => {
    const status = state.items[item.id]?.status || 'not_started';
    if (status === 'reviewed') {
      reviewed += 1;
    }
    if (status === 'gap') {
      gaps += 1;
    }
  });
  return { reviewed, gaps, total: items.length };
};

const normalizeRemoteState = (payload: unknown): ComplianceConsoleState | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const maybe = payload as { state?: unknown };
  const state = maybe.state && typeof maybe.state === 'object'
    ? maybe.state as ComplianceConsoleState
    : payload as ComplianceConsoleState;
  if (!state || typeof state !== 'object' || !state.items || typeof state.items !== 'object') {
    return null;
  }
  return normalizeComplianceConsoleState(state);
};

const formatScanTime = (iso: string | undefined, language: 'en' | 'es'): string => {
  if (!iso) {
    return language === 'es' ? 'Desconocido' : 'Unknown';
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return iso;
  }
  return parsed.toLocaleString(language === 'es' ? 'es-ES' : 'en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const formatReviewed = (iso: string | undefined, language: 'en' | 'es'): string => {
  if (!iso) {
    return '';
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return iso;
  }
  return parsed.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { dateStyle: 'medium' });
};

const actionPoints = (action: string): string[] => (
  action
    .split(/(?<=[.!?])\s+(?=[A-Z“"])/)
    .map((point) => point.trim())
    .filter(Boolean)
);

const DodHowTo: React.FC<{
  item: ComplianceChecklistItemDef;
  language: 'en' | 'es';
  waiting?: boolean;
  itemState?: { status: ComplianceItemStatus; notes: string; stepProgress?: Record<string, ComplianceStepEvidence> };
  seenTerms?: Set<DodGlossaryTermId>;
  onStepProgressChange?: (progress: Record<string, ComplianceStepEvidence>) => void;
  apiBase?: string;
  accessToken?: string;
  onUseAnswer?: (note: string) => void;
  assist?: 'explain' | 'do' | null;
  brief?: boolean;
  calendarDate?: string;
  onCalendarDate?: (date: string) => void;
  agentSkill?: string;
  consoleKind?: string;
  companyId?: string | number;
  legalName?: string;
  ein?: string;
  onRegistryChanged?: () => void;
  onAddCredentials?: (portalId: string) => void;
  /** Merge proof steps + mark the parent task finished after a successful Do-it-for-me. */
  onMarkComplete?: (patch: Record<string, ComplianceStepEvidence>) => void;
  onApplyCalendarDates?: (events: Array<{ id: string; title: string; date: string }>) => void;
  existingCalendarEvents?: ComplianceCalendarEvent[];
}> = ({ item, language, waiting, itemState, seenTerms, onStepProgressChange, apiBase, accessToken, onUseAnswer, assist, brief = false, calendarDate, onCalendarDate, agentSkill, consoleKind, companyId, legalName, ein, onRegistryChanged, onAddCredentials, onMarkComplete, onApplyCalendarDates, existingCalendarEvents }) => {
  const steps = waiting && item.waitingSteps?.length ? item.waitingSteps : item.actionSteps;
  const spanish = language === 'es';
  const partCount = item.verifySteps?.length || 0;
  const proofItem: ComplianceChecklistItemDef = partCount || !item.evidenceHint
    ? item
    : {
      ...item,
      verifySteps: [{
        id: `${item.id}-proof`,
        kind: 'proof',
        text: item.evidenceHint,
        evidenceHint: item.evidenceHint,
      }],
    };
  const points = actionPoints(item.action || '');
  const showBackground = !brief;
  const decideCoach = itemCoachMode(item.id) === 'decide' && Boolean(COACH_SCRIPTS[item.id]);
  const showCoach = Boolean(steps?.length || decideCoach) && (showBackground || decideCoach || Boolean(assist));
  const coachDefaultOpen = Boolean(brief || assist);
  const showAssistPanel = Boolean(assist) && !(assist === 'do' && decideCoach);
  const officialLinks = item.sourceUrl || item.docsUrl ? (
    <>
      {item.sourceUrl ? (
        <a className="compliance-console-source" href={item.sourceUrl} target="_blank" rel="noopener noreferrer">
          {item.sourceLabel || item.sourceUrl}
        </a>
      ) : null}
      {item.docsUrl && item.docsUrl !== item.sourceUrl ? (
        <a className="compliance-console-source" href={item.docsUrl} target="_blank" rel="noopener noreferrer">
          {item.docsLabel || (spanish ? 'Guía' : 'Guide')}
        </a>
      ) : null}
    </>
  ) : null;
  const assistPanel = showAssistPanel && assist ? (
    <TaskAssistPanel
      item={item}
      language={language}
      mode={assist}
      seenTerms={seenTerms}
      apiBase={apiBase}
      accessToken={accessToken}
      agentSkill={agentSkill}
      consoleKind={consoleKind}
      companyId={companyId}
      legalName={legalName}
      ein={ein}
      onRegistryChanged={onRegistryChanged}
      onAddCredentials={onAddCredentials}
      onSaveStep={onStepProgressChange ? (stepId, evidence) => {
        const current = itemState?.stepProgress || {};
        onStepProgressChange({ ...current, [stepId]: evidence });
      } : undefined}
      onMergeSteps={onStepProgressChange ? (patch) => {
        onStepProgressChange({ ...(itemState?.stepProgress || {}), ...patch });
      } : undefined}
      onAllEvidenceReady={onMarkComplete}
      onApplyCalendarDates={
        onApplyCalendarDates && consoleKind && item.id === calendarTaskId(consoleKind)
          ? onApplyCalendarDates
          : undefined
      }
      consoleKindForCalendar={
        onApplyCalendarDates && consoleKind && item.id === calendarTaskId(consoleKind)
          ? consoleKind
          : undefined
      }
      existingCalendarEvents={existingCalendarEvents}
    />
  ) : null;
  // Do-it-for-me owns the workflow (lookup → vault → approve). Hiding the twin
  // "Finish these parts" checklist stops the same franchise steps from appearing twice.
  const showManualParts = assist !== 'do';
  return (
  <>
    {/* Explain sits under the subject / action row, not after the step list. */}
    {assist === 'explain' ? assistPanel : null}
    {assist === 'do' ? assistPanel : null}
    {showManualParts && brief && onStepProgressChange && (proofItem.verifySteps?.length || 0) > 0 ? (
      <DodVerifySteps
        item={proofItem}
        itemState={itemState}
        language={language}
        waiting={waiting}
        seenTerms={seenTerms}
        plain
        showEveryStep
        projectIdentity={{ legalName, ein }}
        calendarDate={calendarDate}
        onCalendarDate={onCalendarDate}
        onStepProgressChange={onStepProgressChange}
      />
    ) : null}
    {showManualParts && showBackground ? (
    <section className="task-fold" aria-label={spanish ? 'Qué significa' : 'What this means'}>
      <h4>{spanish ? 'Qué significa' : 'What this means'}</h4>
      {points.length >= 3 ? (
        <ul className="task-fold-points">
          {points.map((point) => (
            <li key={point}>
              <DodTermText text={point} language={language} seen={seenTerms} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="task-fold-copy">
          <DodTermText text={item.action} language={language} seen={seenTerms} />
        </p>
      )}
    </section>
    ) : null}
    {showManualParts && showCoach ? (
      <ComplianceCoach
        itemId={item.id}
        steps={steps || []}
        language={language}
        apiBase={apiBase}
        accessToken={accessToken}
        seenTerms={seenTerms}
        onUseAnswer={onUseAnswer}
        defaultOpen={coachDefaultOpen}
      />
    ) : null}
    {showManualParts && officialLinks ? (
      brief ? (
        <details className="task-fold">
          <summary>{spanish ? 'Página oficial' : 'Official page'}</summary>
          {officialLinks}
        </details>
      ) : (
        <section className="task-fold" aria-label={spanish ? 'Página oficial' : 'Official page'}>
          <h4>{spanish ? 'Página oficial' : 'Official page'}</h4>
          {officialLinks}
        </section>
      )
    ) : null}
    {showManualParts && !brief && onStepProgressChange && (proofItem.verifySteps?.length || 0) > 0 ? (
      <section className="task-fold" aria-label={spanish ? 'Termine estas partes' : 'Finish these parts'}>
        <h4>{spanish ? 'Termine estas partes' : 'Finish these parts'}</h4>
        <p className="task-fold-copy">
          {spanish
            ? 'Marque una parte cuando el papel o la nota verdadera esté guardada. No invente un papel.'
            : 'Mark a part when the paper or the true note is saved. Do not make up a paper.'}
        </p>
        <DodVerifySteps
          item={proofItem}
          itemState={itemState}
          language={language}
          waiting={waiting}
          seenTerms={seenTerms}
          showEveryStep={false}
          projectIdentity={{ legalName, ein }}
          calendarDate={calendarDate}
          onCalendarDate={onCalendarDate}
          onStepProgressChange={onStepProgressChange}
        />
      </section>
    ) : null}
  </>
  );
};

const DodPrerequisites: React.FC<{
  deps: ChecklistDependencyState[];
  language: 'en' | 'es';
  facts?: { companyName?: string; ein?: string };
  waitingNote?: string;
}> = ({ deps, language, facts, waitingNote }) => {
  if (!deps.length) {
    return null;
  }
  const ready = deps.every((row) => row.done);
  return (
    <div className={`dod-prereq${ready ? ' is-ready' : ''}`}>
      <ul className="dod-prereq-list" aria-label={language === 'es' ? 'Depende de' : 'Depends on'}>
        {deps.map((row) => {
          const label = shortDependencyLabel(row.item, language, facts);
          return (
            <li key={row.item.id} className={row.done ? 'is-done' : 'is-open'}>
              <span className="dod-prereq-mark" aria-hidden="true">{row.done ? '✓' : '○'}</span>
              <span className="dod-prereq-name">
                <DodTermText text={label} language={language} />
              </span>
              <span className="dod-prereq-state">
                {row.done
                  ? (language === 'es' ? 'Hecho' : 'Done')
                  : (language === 'es' ? 'Pendiente' : 'Needed')}
              </span>
            </li>
          );
        })}
      </ul>
      {!ready && waitingNote ? (
        <p className="dod-decision-dep">
          <DodTermText text={waitingNote} language={language} />
        </p>
      ) : null}
    </div>
  );
};

const taskLane = (status: ComplianceItemStatus, waiting: boolean): WorkflowLane => {
  if (status === 'reviewed') return 'done';
  if (waiting || status === 'gap') return 'blocked';
  return 'ready';
};

const complianceWaitLabel = (
  item: ComplianceChecklistItemDef,
  state: ComplianceConsoleState,
  language: 'en' | 'es',
): string => {
  if (isDodWaitingOnExternal(item, state)) {
    return language === 'es' ? 'En espera de GSA' : 'Waiting on GSA';
  }
  if (item.id === 'formation-filing' && state.businessCreation?.stage === 'pending') {
    return language === 'es' ? 'En espera del estado' : 'Waiting on the state';
  }
  return language === 'es' ? 'Después de un paso anterior' : 'After an earlier step';
};

const ComplianceItemRow: React.FC<{
  item: ComplianceChecklistItemDef;
  status: ComplianceItemStatus;
  notes: string;
  reviewedAt?: string;
  language: 'en' | 'es';
  kind?: ComplianceConsoleKind;
  forceOpen?: boolean;
  highlighted?: boolean;
  hideGateChip?: boolean;
  currentJob?: boolean;
  primaryReview?: boolean;
  simpleNotes?: boolean;
  showStatusMark?: boolean;
  prerequisites?: ChecklistDependencyState[];
  dependencyDepth?: number;
  prereqFacts?: { companyName?: string; ein?: string };
  waiting?: boolean;
  waitLabel?: string;
  reviewDisabled?: boolean;
  reviewDisabledReason?: string;
  planLocked?: boolean;
  planLabel?: string;
  itemState?: {
    status: ComplianceItemStatus;
    notes: string;
    stepProgress?: Record<string, ComplianceStepEvidence>;
    copySavedAt?: string;
  };
  onStatusChange: (status: ComplianceItemStatus) => void;
  onNotesChange: (notes: string) => void;
  onMarkReviewed: () => void;
  onToggleCopySaved?: () => void;
  onStepProgressChange?: (progress: Record<string, ComplianceStepEvidence>) => void;
  /** One update: merge Do-it-for-me proof steps and mark the task finished. */
  onCompleteWithEvidence?: (progress: Record<string, ComplianceStepEvidence>) => void;
  calendarDate?: string;
  onCalendarDate?: (date: string) => void;
  apiBase?: string;
  accessToken?: string;
  registryProposals?: RegistryProposal[];
  onRegistryChanged?: () => void;
  onAddCredentials?: (portalId: string) => void;
  writeLocked?: boolean;
  agentSkill?: string;
  companyId?: string | number;
  legalName?: string;
  ein?: string;
  onApplyCalendarDates?: (events: Array<{ id: string; title: string; date: string }>) => void;
  existingCalendarEvents?: ComplianceCalendarEvent[];
}> = ({
  item,
  status,
  notes,
  reviewedAt,
  language,
  kind = 'website-compliance',
  forceOpen = false,
  highlighted = false,
  hideGateChip = false,
  currentJob = false,
  primaryReview = false,
  simpleNotes = false,
  showStatusMark = false,
  prerequisites = [],
  prereqFacts,
  waiting = false,
  waitLabel = '',
  reviewDisabled = false,
  reviewDisabledReason = '',
  planLocked = false,
  planLabel = '',
  itemState,
  onStatusChange,
  onNotesChange,
  onMarkReviewed,
  onToggleCopySaved,
  onStepProgressChange,
  onCompleteWithEvidence,
  calendarDate,
  onCalendarDate,
  apiBase,
  accessToken,
  registryProposals = [],
  onRegistryChanged,
  onAddCredentials,
  writeLocked = false,
  agentSkill,
  companyId,
  legalName,
  ein,
  onApplyCalendarDates,
  existingCalendarEvents,
}) => {
  const [open, setOpen] = React.useState(forceOpen);
  const [assist, setAssist] = React.useState<'explain' | 'do' | null>(null);
  const panelId = `compliance-item-${item.id}`;
  const options = statusOptionsFor(kind);
  const seenTerms = new Set<DodGlossaryTermId>();
  const statusLabelText = waiting
    ? (waitLabel || (language === 'es' ? 'Después de un paso anterior' : 'After an earlier step'))
    : statusLabel(status, language, kind);

  React.useEffect(() => {
    if (forceOpen) {
      setOpen(true);
    }
  }, [forceOpen]);

  return (
    <div
      id={`dod-check-item-${item.id}`}
      className={`compliance-console-item status-${status}${open ? ' is-open' : ''}${highlighted ? ' is-focused' : ''}`}
    >
      <div className="compliance-item-top">
      <h4 className="compliance-console-item-heading">
        <div className="compliance-console-item-heading-row">
          <button
            type="button"
            className="compliance-console-item-trigger"
            aria-expanded={open}
            aria-controls={panelId}
            id={`${panelId}-btn`}
            onClick={() => setOpen((current) => !current)}
          >
            <span className="compliance-console-item-trigger-copy">
              {showStatusMark ? (
                <span className={`compliance-console-item-mark status-${status}`} aria-hidden="true">
                  {status === 'reviewed' ? '✓' : status === 'gap' ? '!' : status === 'in_progress' ? '•' : ''}
                </span>
              ) : null}
              <strong>{item.title}</strong>
              {taskCredentialsNeed(item) === 'vault' ? (
                <span className="compliance-task-cred-chip">
                  {language === 'es' ? 'Credenciales' : 'Credentials'}
                </span>
              ) : taskCredentialsNeed(item) === 'operator' ? (
                <span className="compliance-task-cred-chip">
                  {language === 'es' ? 'Inicio de sesión' : 'Login'}
                </span>
              ) : null}
              {currentJob ? (
                <WorkflowLaneChip lane={waiting ? 'blocked' : 'ready'} language={language} />
              ) : null}
              {!hideGateChip && item.gate ? (
                <span className="compliance-console-item-gate-chip">{dodGateLabel(item.gate, language)}</span>
              ) : null}
              {planLabel ? (
                <span className={`compliance-console-item-plan-chip${planLocked ? ' is-locked' : ''}`}>
                  {planLabel}
                </span>
              ) : null}
              {reviewedAt ? (
                <span className="compliance-console-item-reviewed">
                  {filedStamp(language, kind)} {formatReviewed(reviewedAt, language)}
                </span>
              ) : null}
            </span>
            <span className="compliance-console-item-chevron" aria-hidden="true">{open ? '▾' : '▸'}</span>
          </button>
          {waiting ? (
            <span className={`compliance-console-item-status-chip status-blocked`} title={statusLabelText}>
              {statusLabelText}
            </span>
          ) : (
            <label className={`compliance-console-item-status-select-wrap status-${status}`}>
              <span className="visually-hidden">{language === 'es' ? 'Estado de la tarea' : 'Task status'}</span>
              <select
                className={`compliance-console-item-status-select status-${status}`}
                value={status}
                onChange={(event) => onStatusChange(event.target.value as ComplianceItemStatus)}
                aria-label={`${item.title} status`}
                disabled={planLocked || reviewDisabled || writeLocked}
              >
                {options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {language === 'es' ? option.labelEs : option.labelEn}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      </h4>
      <div className="compliance-task-top-actions">
        <TaskAssistButtons
          language={language}
          mode={assist}
          onExplain={() => {
            setOpen(true);
            setAssist((current) => (current === 'explain' ? null : 'explain'));
          }}
          onDo={() => {
            setOpen(true);
            setAssist((current) => (current === 'do' ? null : 'do'));
          }}
        />
        {open && status !== 'reviewed' ? (
          <button
            type="button"
            className={primaryReview ? 'dashboard-btn dashboard-btn-primary compliance-task-complete' : 'dashboard-security-inline-btn compliance-task-complete'}
            onClick={onMarkReviewed}
            disabled={planLocked || reviewDisabled || writeLocked}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
              <path d="M6 12.5 10 16.5 18 8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {itemCompleteLabel(item, language, kind)}
          </button>
        ) : null}
      </div>
      </div>
      {prerequisites.length ? (
        <p className="compliance-dep-after">
          <DodTermText
            text={`${language === 'es' ? 'Después de' : 'After'} ${prerequisites.map((row) => shortDependencyLabel(row.item, language, prereqFacts)).join(' · ')}`}
            language={language}
            seen={seenTerms}
          />
        </p>
      ) : null}
      {open ? (
        <div
          id={panelId}
          role="region"
          aria-labelledby={`${panelId}-btn`}
          className="compliance-console-item-panel"
        >
          <div className="compliance-console-item-main">
            <div>
              <DodHowTo
                item={item}
                language={language}
                waiting={waiting}
                itemState={itemState}
                seenTerms={seenTerms}
                assist={assist}
                calendarDate={calendarDate}
                onCalendarDate={onCalendarDate}
                onStepProgressChange={onStepProgressChange}
                apiBase={apiBase}
                accessToken={accessToken}
                agentSkill={agentSkill}
                consoleKind={kind}
                companyId={companyId}
                legalName={legalName}
                ein={ein}
                onRegistryChanged={onRegistryChanged}
                onAddCredentials={onAddCredentials}
                onApplyCalendarDates={onApplyCalendarDates}
                existingCalendarEvents={existingCalendarEvents}
                onMarkComplete={status === 'reviewed' || planLocked || reviewDisabled || writeLocked || !onCompleteWithEvidence
                  ? undefined
                  : onCompleteWithEvidence}
                onUseAnswer={(note) => {
                  onNotesChange(note);
                  if (status === 'not_started') {
                    onStatusChange('in_progress');
                  }
                }}
              />
              <ControlProofProposals
                controlId={item.id}
                proposals={registryProposals}
                language={language}
                gatewayBaseUrl={apiBase}
                accessToken={accessToken}
                readOnly={writeLocked}
                onChanged={onRegistryChanged}
                onAddCredentials={onAddCredentials}
              />
              <DodPrerequisites
                deps={prerequisites}
                language={language}
                facts={prereqFacts}
                waitingNote={item.dependencyNote}
              />
              {item.verifySteps?.length ? null : (
                <span className="compliance-console-evidence">
                  {`${language === 'es' ? 'Evidencia sugerida' : 'Suggested evidence'}: `}
                  <DodTermText text={item.evidenceHint} language={language} seen={seenTerms} />
                </span>
              )}
            </div>
            {planLocked ? (
              <p className="dashboard-panel-subtitle">
                {language === 'es'
                  ? `Incluido en ${planLabel}. Actualice el plan para marcar evidencia en este control.`
                  : `Included in ${planLabel}. Upgrade to file evidence on this control.`}
              </p>
            ) : null}
            <div className="compliance-console-item-controls">
              {item.keepCopy && onToggleCopySaved ? (
                <button
                  type="button"
                  className={`dod-keep-copy-chip${itemState?.copySavedAt || status === 'reviewed' ? ' is-saved' : ''}`}
                  onClick={onToggleCopySaved}
                  disabled={planLocked}
                >
                  {itemState?.copySavedAt || status === 'reviewed'
                    ? (language === 'es' ? 'Guardado' : 'Saved')
                    : (language === 'es' ? 'Guardar una copia' : 'Save a copy')}
                </button>
              ) : null}
              {!planLocked && reviewDisabled ? (
                <p className="dashboard-panel-subtitle">{reviewDisabledReason}</p>
              ) : null}
            </div>
          </div>
          <label className="compliance-console-notes">
            <span>
              {simpleNotes
                ? (language === 'es' ? 'Sus notas (opcional)' : 'Your notes (optional)')
                : (language === 'es' ? 'Notas' : 'Notes')}
            </span>
            <textarea
              value={notes}
              rows={2}
              placeholder={
                simpleNotes
                  ? (language === 'es' ? 'Qué hizo, o un enlace a la prueba…' : 'What you did, or a link to the proof…')
                  : (language === 'es' ? 'Hallazgos, dueños, enlaces a evidencia…' : 'Findings, owners, evidence links…')
              }
              onChange={(event) => onNotesChange(event.target.value)}
            />
          </label>
        </div>
      ) : null}
    </div>
  );
};

const ScanFindingsPanel: React.FC<{
  scan: WebsiteComplianceLastScan;
  history?: WebsiteComplianceScanHistoryEntry[];
  language: 'en' | 'es';
}> = ({ scan, history = [], language }) => {
  const cmpVendors = scan.cmp_vendors || [];
  const trackers = scan.tracker_hosts || [];
  const overlays = scan.overlay_widgets || [];
  const trackerCategories = scan.tracker_categories || {};
  const trackerCategoryDetail = Object.entries(trackerCategories)
    .map(([category, hosts]) => `${category} (${hosts.length})`)
    .join(' · ');
  const risk = scan.privacy_risk;
  const riskLabel = risk === 'low'
    ? (language === 'es' ? 'Bajo' : 'Low')
    : risk === 'medium'
      ? (language === 'es' ? 'Medio' : 'Medium')
      : risk === 'high'
        ? (language === 'es' ? 'Alto' : 'High')
        : '';
  const ccpaChecks = scan.ccpa_checks || [];
  const headerTests = scan.security_header_tests || [];
  const findings = [
    {
      id: 'policy',
      label: language === 'es' ? 'Enlaces de políticas' : 'Policy links',
      verdict: Boolean(scan.policy_links_ok) ? 'compliant' : 'not_compliant',
      detail: language === 'es'
        ? `${(scan.privacy_links || []).length} privacidad · ${(scan.cookie_links || []).length} cookies · ${(scan.terms_links || []).length} términos`
        : `${(scan.privacy_links || []).length} privacy · ${(scan.cookie_links || []).length} cookie · ${(scan.terms_links || []).length} terms`,
    },
    {
      id: 'consent',
      label: language === 'es' ? 'Banner de consentimiento' : 'Consent banner',
      verdict: Boolean(scan.consent_banner_heuristic) ? 'compliant' : 'not_compliant',
      detail: language === 'es'
        ? (scan.consent_banner_heuristic ? 'Heurística detectó controles de cookies' : 'No se detectó banner de consentimiento')
        : (scan.consent_banner_heuristic ? 'Cookie consent controls detected' : 'No consent banner detected'),
    },
    {
      id: 'cmp',
      label: language === 'es' ? 'CMP detectado' : 'CMP detected',
      verdict: cmpVendors.length > 0 ? 'compliant' : 'not_compliant',
      detail: cmpVendors.length
        ? cmpVendors.join(', ')
        : (language === 'es' ? 'Ningún CMP conocido en el HTML' : 'No known CMP in homepage HTML'),
    },
    {
      id: 'trackers',
      label: language === 'es' ? 'Inventario de rastreadores' : 'Tracker inventory',
      verdict: trackers.length > 0 ? 'found' : 'none_found',
      detail: trackers.length
        ? (trackerCategoryDetail ? `${trackers.join(', ')} · ${trackerCategoryDetail}` : trackers.join(', '))
        : (language === 'es' ? 'Sin hosts de rastreo conocidos' : 'No known tracker hosts'),
    },
    {
      id: 'gpc',
      label: language === 'es' ? 'No vender / GPC / SPI' : 'Do Not Sell / GPC / Sensitive PI',
      verdict: Boolean(scan.gpc_heuristic) ? 'compliant' : 'not_compliant',
      detail: language === 'es'
        ? `${(scan.do_not_sell_links || []).length} enlaces · GCM ${scan.google_consent_mode_heuristic ? 'sí' : 'no'} · bloqueo ${scan.script_blocking_heuristic ? 'sí' : 'no'}`
        : `${(scan.do_not_sell_links || []).length} links · Consent Mode ${scan.google_consent_mode_heuristic ? 'yes' : 'no'} · script-block ${scan.script_blocking_heuristic ? 'yes' : 'no'}`,
    },
    {
      id: 'a11y',
      label: language === 'es' ? 'Declaración de accesibilidad' : 'Accessibility statement',
      verdict: (scan.accessibility_links || []).length > 0 ? 'compliant' : 'not_compliant',
      detail: overlays.length
        ? (language === 'es'
          ? `Overlay: ${overlays.join(', ')} — no sustituye WCAG`
          : `Overlay: ${overlays.join(', ')} — not a WCAG substitute`)
        : `${(scan.accessibility_links || []).length} ${language === 'es' ? 'enlaces' : 'links'}`,
    },
    {
      id: 'ip',
      label: language === 'es' ? 'Enlaces DMCA / copyright' : 'DMCA / copyright links',
      verdict: Boolean(scan.ip_links_ok) ? 'compliant' : 'not_compliant',
      detail: language === 'es'
        ? `${(scan.dmca_links || []).length} DMCA / copyright`
        : `${(scan.dmca_links || []).length} DMCA / copyright`,
    },
    ...(typeof scan.http_status === 'number' && scan.http_status > 0 ? [{
      id: 'http-status',
      label: language === 'es' ? 'La página responde' : 'Page answers',
      verdict: (scan.http_status >= 200 && scan.http_status < 400 ? 'compliant' : 'not_compliant') as 'compliant' | 'not_compliant',
      detail: scan.http_status >= 500
        ? (language === 'es'
          ? `HTTP ${scan.http_status}. Un 504 significa que el servidor no contestó a tiempo.`
          : `HTTP ${scan.http_status}. A 504 means the server did not answer in time.`)
        : `HTTP ${scan.http_status}`,
    }] : []),
    {
      id: 'https',
      label: 'HTTPS',
      verdict: scan.https_ok !== false ? 'compliant' : 'not_compliant',
      detail: scan.https_ok === false
        ? (language === 'es' ? 'La URL final no es HTTPS' : 'Final URL is not HTTPS')
        : (language === 'es' ? 'La URL final usa HTTPS' : 'Final URL uses HTTPS'),
    },
    ...(headerTests.length
      ? headerTests.map((test) => ({
        id: `header-${test.id}`,
        label: test.label,
        verdict: (test.pass ? 'compliant' : 'not_compliant') as 'compliant' | 'not_compliant',
        extra: !test.core && !test.pass,
        detail: test.pass
          ? (test.core
            ? (language === 'es' ? 'Encabezado base presente' : 'Core header present')
            : (language === 'es' ? 'Presente' : 'Present'))
          : test.fix,
      }))
      : [{
        id: 'headers',
        label: language === 'es' ? 'Encabezados de seguridad' : 'Security headers',
        verdict: (Boolean(scan.security_headers_ok) ? 'compliant' : 'not_compliant') as 'compliant' | 'not_compliant',
        extra: false,
        detail: [
          scan.security_headers?.hsts ? 'HSTS' : null,
          scan.security_headers?.csp ? 'CSP' : null,
          scan.security_headers?.x_content_type_options ? 'X-Content-Type-Options' : null,
          scan.security_headers?.referrer_policy ? 'Referrer-Policy' : null,
        ].filter(Boolean).join(' · ') || (language === 'es' ? 'Faltan HSTS o nosniff' : 'Missing HSTS or nosniff'),
      }]),
    {
      id: 'email-auth',
      label: language === 'es' ? 'Autenticación de correo' : 'Email authentication',
      verdict: Boolean(scan.email_auth_ok) ? 'compliant' : 'not_compliant',
      detail: (() => {
        const domain = scan.checked_domain || '';
        const hosts = (scan.mail_hosts || []).join(', ');
        const listed = typeof scan.blocklist_listed === 'number' ? scan.blocklist_listed : null;
        const checked = typeof scan.blocklist_checked === 'number' ? scan.blocklist_checked : null;
        const block = listed === null
          ? ''
          : (language === 'es'
            ? ` DNSBL: ${listed} listados en ${checked} consultas${hosts ? ` de ${hosts}` : ''}.`
            : ` DNSBL: ${listed} listings in ${checked} checks${hosts ? ` of ${hosts}` : ''}.`);
        const reports = scan.dmarc_rua
          ? (language === 'es'
            ? ' El buzón de informes está publicado. NextCompli todavía no lee esos informes.'
            : ' The report mailbox is published. NextCompli does not read those reports yet.')
          : '';
        const auth = language === 'es'
          ? `SPF ${!scan.spf_present ? 'no' : scan.spf_aligned === false ? 'no incluye el servidor de correo' : 'sí'} · DKIM ${(scan.dkim_selectors || []).join(', ') || 'no'} · DMARC ${scan.dmarc_policy ? `${scan.dmarc_policy}${scan.dmarc_rua === false ? ', sin buzón de informes' : ''}` : 'no'}`
          : `SPF ${!scan.spf_present ? 'no' : scan.spf_aligned === false ? 'missing mail host' : 'yes'} · DKIM ${(scan.dkim_selectors || []).join(', ') || 'none'} · DMARC ${scan.dmarc_policy ? `${scan.dmarc_policy}${scan.dmarc_rua === false ? ', no report mailbox' : ''}` : 'none'}`;
        const where = domain
          ? (language === 'es' ? `Dominio revisado: ${domain}. ` : `Domain checked: ${domain}. `)
          : '';
        return `${where}${auth}.${block}${reports}`.replace(/\.\./g, '.');
      })(),
    },
  ];

  const findingResultLabel = (verdict: 'compliant' | 'not_compliant' | 'found' | 'none_found'): string => {
    if (language === 'es') {
      if (verdict === 'compliant') return 'Cumple';
      if (verdict === 'not_compliant') return 'No cumple';
      if (verdict === 'found') return 'Encontrados';
      return 'Ninguno';
    }
    if (verdict === 'compliant') return 'Compliant';
    if (verdict === 'not_compliant') return 'Not compliant';
    if (verdict === 'found') return 'Found';
    return 'None found';
  };

  const linkGroups: Array<{ label: string; links: string[] }> = [
    { label: language === 'es' ? 'Privacidad' : 'Privacy', links: scan.privacy_links || [] },
    { label: language === 'es' ? 'Cookies' : 'Cookie', links: scan.cookie_links || [] },
    { label: language === 'es' ? 'Términos' : 'Terms', links: scan.terms_links || [] },
    { label: language === 'es' ? 'No vender / SPI' : 'Do Not Sell / Sensitive PI', links: scan.do_not_sell_links || [] },
    { label: language === 'es' ? 'Accesibilidad' : 'Accessibility', links: scan.accessibility_links || [] },
    { label: 'DMCA', links: scan.dmca_links || [] },
  ].filter((group) => group.links.length > 0);

  return (
    <div className="compliance-console-findings" aria-live="polite">
      <div className="compliance-console-findings-header">
        <div className="dod-scope-picker-label">
          <h3>{language === 'es' ? 'Resultados del escaneo' : 'Scan findings'}</h3>
          <HelpBadge label={language === 'es' ? 'Qué significa Cumple' : 'What Compliant means'} wide>
            <p>
              {language === 'es'
                ? 'Cumple significa que este escaneo de homepage encontró el control. No cumple significa que no lo encontró. Inventario de rastreadores dice Encontrados o Ninguno, no es un veredicto. Esto no es un certificado legal.'
                : 'Compliant means this homepage scan found the control. Not compliant means it did not. Tracker inventory says Found or None found — that is a count, not a verdict. This is not a legal certificate.'}
            </p>
          </HelpBadge>
        </div>
        <p className="dashboard-panel-subtitle">
          {language === 'es' ? 'Último escaneo' : 'Last scan'}: {formatScanTime(scan.scannedAt, language)}
          {scan.final_url ? ` · ${scan.final_url}` : ''}
          {typeof scan.link_count === 'number'
            ? ` · ${scan.link_count} ${language === 'es' ? 'enlaces' : 'links'}`
            : ''}
          {typeof scan.script_src_count === 'number'
            ? ` · ${scan.script_src_count} ${language === 'es' ? 'scripts' : 'scripts'}`
            : ''}
          {typeof scan.pages_crawled === 'number' && scan.pages_crawled > 1
            ? ` · ${scan.pages_crawled} ${language === 'es' ? 'páginas' : 'pages'}`
            : ''}
        </p>
        {scan.contractor_identity && (scan.contractor_identity.uei?.length || scan.contractor_identity.cage?.length || scan.contractor_identity.naics?.length || scan.contractor_identity.psc?.length) ? (
          <p className="dashboard-panel-subtitle">
            <DodTermText
              text={[
                scan.contractor_identity.uei?.length ? `UEI ${scan.contractor_identity.uei.join(', ')}` : '',
                scan.contractor_identity.cage?.length ? `CAGE ${scan.contractor_identity.cage.join(', ')}` : '',
                scan.contractor_identity.naics?.length ? `NAICS ${scan.contractor_identity.naics.join(', ')}` : '',
                scan.contractor_identity.psc?.length ? `PSC ${scan.contractor_identity.psc.join(', ')}` : '',
              ].filter(Boolean).join(' · ')}
              language={language}
            />
          </p>
        ) : null}
      </div>
      {riskLabel ? (
        <div className={`compliance-console-risk is-${risk}`}>
          <div className="dod-scope-picker-label">
            <strong>
              {language === 'es' ? 'Riesgo de privacidad' : 'Privacy risk'}: {riskLabel}
            </strong>
          <HelpBadge label={language === 'es' ? 'Cómo se calcula el riesgo' : 'How this risk is scored'} wide>
            <p>
              <DodTermText
                text={language === 'es'
                  ? 'Bajo si los cuatro controles CCPA pasan: herramienta de consentimiento, enlace No vender, scripts retenidos hasta el consentimiento, y un archivo GPC en /.well-known/gpc.json. Medio si fallan uno o dos. Alto si fallan tres o más, o si HTTPS falla. El bloqueo de scripts es una lectura del HTML, no una sesión GPC en el navegador.'
                  : 'Low when all four CCPA checks pass: a consent tool, a Do Not Sell link, scripts held until consent, and a GPC file at /.well-known/gpc.json. Medium when one or two fail. High when three or more fail, or HTTPS fails. Script blocking is an HTML reading, not a live browser GPC session.'}
                language={language}
              />
            </p>
          </HelpBadge>
          </div>
          {ccpaChecks.length ? (
            <ul className="compliance-console-ccpa-list">
              {ccpaChecks.map((check) => (
                <li key={check.id} className={check.pass ? 'is-pass' : 'is-fail'}>
                  {check.label}: {check.pass
                    ? (language === 'es' ? 'Cumple' : 'Compliant')
                    : (language === 'es' ? 'No cumple' : 'Not compliant')}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      <div className="compliance-console-findings-grid">
        {findings.map((finding) => (
          <div
            key={finding.id}
            className={`compliance-console-finding-card ${finding.verdict === 'not_compliant' && !finding.extra ? 'is-fail' : finding.extra ? 'is-extra' : 'is-pass'}`}
          >
            <strong>{finding.label}</strong>
            <span className="compliance-console-finding-result">
              {findingResultLabel(finding.verdict)}
            </span>
            <span className="compliance-console-finding-detail">
              {finding.id === 'email-auth'
                ? <DodTermText text={finding.detail} language={language} />
                : finding.detail}
            </span>
          </div>
        ))}
      </div>
      {linkGroups.length > 0 ? (
        <div className="compliance-console-findings-links">
          {linkGroups.map((group) => (
            <div key={group.label}>
              <h4>{group.label}</h4>
              <ul>
                {group.links.slice(0, 5).map((link) => (
                  <li key={link}>
                    <a href={link} target="_blank" rel="noopener noreferrer">{link}</a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
      {history.length > 0 ? (
        <div className="compliance-console-scan-history">
          <h4>{language === 'es' ? 'Historial de escaneos' : 'Scan history'}</h4>
          <ul>
            {history.slice(0, 5).map((entry) => (
              <li key={entry.scannedAt || entry.final_url}>
                {formatScanTime(entry.scannedAt, language)}
                {entry.final_url ? ` · ${entry.final_url}` : ''}
                {` · CMP ${(entry.cmp_vendors || []).join(', ') || '—'}`}
                {` · ${(entry.tracker_hosts || []).length} ${language === 'es' ? 'rastreadores' : 'trackers'}`}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

const SectionProgressRing: React.FC<{
  done: number;
  total: number;
  blocked: number;
  language: 'en' | 'es';
}> = ({ done, total, blocked, language }) => {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const radius = 15.5;
  const circ = 2 * Math.PI * radius;
  const dash = (pct / 100) * circ;
  const label = language === 'es'
    ? `${done} de ${total} hechos, ${blocked} bloqueados`
    : `${done} of ${total} done, ${blocked} blocked`;
  return (
    <svg className="compliance-section-meter" viewBox="0 0 36 36" role="img" aria-label={label}>
      <circle className="compliance-ring-track" cx="18" cy="18" r={radius} />
      <circle
        className={blocked > 0 && done < total ? 'compliance-ring-value is-blocked' : 'compliance-ring-value'}
        cx="18"
        cy="18"
        r={radius}
        strokeDasharray={`${dash} ${circ - dash}`}
        transform="rotate(-90 18 18)"
      />
      <text x="18" y="18">{pct}%</text>
    </svg>
  );
};

const legalNamesForAccount = (accountName: string): Set<string> => {
  const name = accountName.trim().toLowerCase();
  if (!name) {
    return new Set();
  }
  return new Set(name.endsWith(' llc') ? [name, name.slice(0, -4)] : [name, `${name} llc`]);
};

const formationAnswerScore = (answers: ComplianceConsoleState['businessCreation']): number => {
  if (!answers) {
    return 0;
  }
  return Object.entries(answers).filter(([key, value]) => key !== 'legalName' && value !== undefined && value !== null && String(value).trim() !== '').length;
};

const richestFormation = (state: ComplianceConsoleState): ComplianceConsoleState['businessCreation'] => {
  const ranked = [
    state.businessCreation,
    ...Object.values(state.companyChecks || {}).map((row) => row?.businessCreation),
  ];
  return ranked.reduce<ComplianceConsoleState['businessCreation']>((best, row) => (
    formationAnswerScore(row) > formationAnswerScore(best) ? row : best
  ), undefined);
};

/** A legal name such as "Canyon Foundry LLC" is not a website until it has a public address. */
const websitesWithAddresses = (source: ComplianceConsoleState): ComplianceConsoleState => {
  const taxonomy = source.taxonomy || emptyDodTaxonomy();
  const companies = taxonomy.companies.filter((row) => websiteScanUrlFromName(row.name));
  const activeCompanyId = companies.some((row) => row.id === taxonomy.activeCompanyId)
    ? taxonomy.activeCompanyId
    : '';
  if (companies.length === taxonomy.companies.length && activeCompanyId === taxonomy.activeCompanyId) {
    return source;
  }
  return {
    ...source,
    taxonomy: {
      ...taxonomy,
      companies,
      activeCompanyId,
    },
  };
};

const localFormationCopies = (storageKey: string): ComplianceConsoleState[] => {
  const copies = [loadComplianceConsoleState(storageKey)];
  if (typeof window === 'undefined') {
    return copies;
  }
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index) || '';
    if (key.startsWith(`${storageKey}:`)) {
      copies.push(loadComplianceConsoleState(key));
    }
  }
  return copies;
};

const adoptUnscopedBinder = (
  remote: ComplianceConsoleState,
  accountName: string,
  storageKey: string,
): ComplianceConsoleState => {
  const names = legalNamesForAccount(accountName);
  if (names.size === 0) {
    return remote;
  }
  const copies = [
    ...localFormationCopies(storageKey),
    ...(storageKey === BUSINESS_CREATION_COMPLIANCE_CONSOLE.storageKey
      ? []
      : localFormationCopies(BUSINESS_CREATION_COMPLIANCE_CONSOLE.storageKey)),
  ];
  const local = copies.reduce((best, row) => (
    formationAnswerScore(richestFormation(row)) > formationAnswerScore(richestFormation(best)) ? row : best
  ));
  const localAnswers = richestFormation(local);
  const localCompany = copies
    .flatMap((row) => row.taxonomy?.companies || [])
    .find((company) => names.has(company.name.trim().toLowerCase()));
  const localName = String(localAnswers?.legalName || local.businessCreation?.legalName || localCompany?.name || '').trim().toLowerCase();
  if (!localCompany && !names.has(localName)) {
    return remote;
  }
  const remoteCompany = (remote.taxonomy?.companies || []).find((company) => names.has(company.name.trim().toLowerCase()));
  const remoteScore = formationAnswerScore(richestFormation(remote));
  const localScore = formationAnswerScore(localAnswers);
  if (remoteCompany && remoteScore >= localScore) {
    return remote;
  }
  if (localScore === 0 && !localCompany) {
    return remote;
  }
  if (localScore <= remoteScore && !(localCompany && !remoteCompany)) {
    return remote;
  }
  const company = localCompany || {
    id: remoteCompany?.id || `company-${accountName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    name: local.businessCreation?.legalName || localCompany?.name || accountName,
    ein: remoteCompany?.ein || '',
  };
  const taxonomy = local.taxonomy || emptyDodTaxonomy();
  return {
    ...local,
    targetCompanyName: company.name,
    taxonomy: {
      ...taxonomy,
      companies: [company, ...taxonomy.companies.filter((row) => row.id !== company.id && !names.has(row.name.trim().toLowerCase()))],
      activeCompanyId: company.id,
    },
  };
};

const PROGRAM_NOTICE_INBOX_KEY = 'nextcompli.program-notices.v1';

type ProgramNoticeMark = 'read' | 'cleared';

const readProgramNoticeInbox = (): Record<string, ProgramNoticeMark> => {
  try {
    const raw = window.localStorage.getItem(PROGRAM_NOTICE_INBOX_KEY);
    const parsed = raw ? JSON.parse(raw) as Record<string, ProgramNoticeMark> : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeProgramNoticeInbox = (marks: Record<string, ProgramNoticeMark>) => {
  window.localStorage.setItem(PROGRAM_NOTICE_INBOX_KEY, JSON.stringify(marks));
};

const ComplianceConsolePanel: React.FC<ComplianceConsolePanelProps> = ({
  definition,
  language = 'en',
  reviewerLabel,
  gatewayBaseUrl,
  accessToken,
  platformAdmin = false,
  effectiveFeatures = [],
  workspaceReadOnly = false,
  taskFocus = null,
}) => {
  const [state, setState] = React.useState<ComplianceConsoleState>(() => emptyComplianceState());
  const [portfolioRows, setPortfolioRows] = React.useState<PortfolioCalendarEvent[]>([]);
  const [saveFeedback, setSaveFeedback] = React.useState<string | null>(null);
  const [saveBusy, setSaveBusy] = React.useState(false);
  const [syncError, setSyncError] = React.useState<string | null>(null);
  const [remoteReady, setRemoteReady] = React.useState(false);
  const [registryProposals, setRegistryProposals] = React.useState<RegistryProposal[]>([]);
  const [registryTick, setRegistryTick] = React.useState(0);
  const [activePillarId, setActivePillarId] = React.useState('');
  const [focusChecklistItemId, setFocusChecklistItemId] = React.useState('');
  const [dodWorkspaceView, setDodWorkspaceView] = React.useState<'today' | 'checklist'>('today');
  const [noticesOpen, setNoticesOpen] = React.useState(false);
  const [noticeMarks, setNoticeMarks] = React.useState<Record<string, ProgramNoticeMark>>(() => readProgramNoticeInbox());
  const [openNoticeId, setOpenNoticeId] = React.useState<string | null>(null);
  const [noticeMoreId, setNoticeMoreId] = React.useState<string | null>(null);
  const [showClearedNotices, setShowClearedNotices] = React.useState(false);
  const noticeAnchorRef = React.useRef<HTMLDivElement | null>(null);
  const noticeKey = (id: string) => `${definition.kind}:${id}`;
  const saveNoticeMarks = (next: Record<string, ProgramNoticeMark>) => {
    setNoticeMarks(next);
    writeProgramNoticeInbox(next);
  };
  React.useEffect(() => {
    if (!noticesOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setNoticesOpen(false);
    };
    const onPointer = (event: MouseEvent) => {
      if (!noticeAnchorRef.current?.contains(event.target as Node)) setNoticesOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [noticesOpen]);
  const [laneFilter, setLaneFilter] = React.useState<WorkflowLane[]>(
    taskFocus?.taskId ? ['ready', 'blocked', 'done'] : ['ready'],
  );
  const [glanceLens, setGlanceLens] = React.useState('');
  /** Controlled so browser restore cannot leave Status charts open on load. */
  const [statusChartsOpen, setStatusChartsOpen] = React.useState(false);
  const [openBoardSectionId, setOpenBoardSectionId] = React.useState<string | null>(null);
  React.useEffect(() => {
    setStatusChartsOpen(false);
  }, [state.taxonomy?.activeProjectId, state.taxonomy?.activeCompanyId]);
  const [focusDecisionId, setFocusDecisionId] = React.useState<string | null>(taskFocus?.taskId || null);
  const [soloTask, setSoloTask] = React.useState(Boolean(taskFocus?.taskId));
  const [bulkDoBusy, setBulkDoBusy] = React.useState(false);
  const [bulkDoNote, setBulkDoNote] = React.useState('');
  const [taskAssist, setTaskAssist] = React.useState<{ id: string; mode: 'explain' | 'do' } | null>(null);
  React.useEffect(() => {
    if (!soloTask) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setSoloTask(false);
      setFocusDecisionId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [soloTask]);
  const [credentialFocusPortal, setCredentialFocusPortal] = React.useState('');
  const [scanUrl, setScanUrl] = React.useState('https://');
  const [scanBusy, setScanBusy] = React.useState(false);
  const [scanMessage, setScanMessage] = React.useState<string | null>(null);
  const [agentInstallOpenSignal, setAgentInstallOpenSignal] = React.useState(0);
  const [sourcesOpenSignal, setSourcesOpenSignal] = React.useState(0);
  const [agentUpdateAvailable, setAgentUpdateAvailable] = React.useState(false);
  const [cloudProvider, setCloudProvider] = React.useState<'aws' | 'azure' | 'gcp'>('aws');
  const [cloudRoleName, setCloudRoleName] = React.useState('');
  const [cloudReviewMessage, setCloudReviewMessage] = React.useState<string | null>(null);
  const [crawlEnabled, setCrawlEnabled] = React.useState(false);
  const [scanCadence, setScanCadence] = React.useState<WebsiteScanCadence>('off');
  const scanDueFired = React.useRef(false);
  const [classifyBusy, setClassifyBusy] = React.useState(false);
  const [orgDraft, setOrgDraft] = React.useState<{ name: string; ein: string } | null>(null);
  const [projectDraft, setProjectDraft] = React.useState<{ id?: string; name: string; dataHandling: DataHandling } | null>(null);
  const [contractDraft, setContractDraft] = React.useState<{ name: string } | null>(null);
  const [companyIdsOpen, setCompanyIdsOpen] = React.useState(false);
  const [samMonitor, setSamMonitor] = React.useState<SamMonitorPayload | null>(null);
  const [revisingFormation, setRevisingFormation] = React.useState(false);
  const [revisingWills, setRevisingWills] = React.useState(false);
  const [subscriptionAccounts, setSubscriptionAccounts] = React.useState<Array<{ id: number; name: string; plan: string; status: string }>>([]);
  const [accountsLoaded, setAccountsLoaded] = React.useState(false);
  const [workingAccountIds, setWorkingAccountIds] = React.useState<number[]>([]);
  const [workingAccountsLoaded, setWorkingAccountsLoaded] = React.useState(false);
  const addedThisSession = React.useRef<Set<number>>(new Set());
  const [usesRemoteCatalog, setUsesRemoteCatalog] = React.useState(false);
  const [remoteRefreshNonce, setRemoteRefreshNonce] = React.useState(0);
  const [refreshBusy, setRefreshBusy] = React.useState(false);
  // Auto-bind workspace: one login = highest paid plan. Project/legal entity pickers scope the work.
  const [subscriptionAccountId, setSubscriptionAccountId] = React.useState<number | null>(null);
  const [calendarOpen, setCalendarOpen] = React.useState(false);
  const widgetSurface = `compliance:${definition.kind}`;
  const [featureLayout, setFeatureLayout] = React.useState<DashboardLayout>(() => readLayout(widgetSurface, COMPLIANCE_DASHBOARD_WIDGETS));
  const featureWidgetsHidden = React.useMemo(() => new Set(featureLayout.hidden), [featureLayout]);
  const commitFeatureLayout = React.useCallback((layout: DashboardLayout) => {
    const next = normalizeLayout(COMPLIANCE_DASHBOARD_WIDGETS, layout);
    writeLayout(widgetSurface, next);
    setFeatureLayout(next);
    const base = String(gatewayBaseUrl || '').replace(/\/$/, '');
    if (base && accessToken) {
      void writeAccountPreference(base, accessToken, `dashboard-layout:${widgetSurface}`, next);
    }
  }, [accessToken, gatewayBaseUrl, widgetSurface]);
  React.useEffect(() => {
    setFeatureLayout(readLayout(widgetSurface, COMPLIANCE_DASHBOARD_WIDGETS));
  }, [widgetSurface]);
  React.useEffect(() => {
    const base = String(gatewayBaseUrl || '').replace(/\/$/, '');
    if (!base || !accessToken) {
      return undefined;
    }
    let cancelled = false;
    const loadLayout = async () => {
      const result = await readAccountPreference(base, accessToken, `dashboard-layout:${widgetSurface}`);
      if (cancelled || !result.ok) {
        return;
      }
      if (result.value && typeof result.value === 'object') {
        const next = normalizeLayout(COMPLIANCE_DASHBOARD_WIDGETS, result.value);
        writeLayout(widgetSurface, next);
        setFeatureLayout(next);
        return;
      }
      void writeAccountPreference(base, accessToken, `dashboard-layout:${widgetSurface}`, readLayout(widgetSurface, COMPLIANCE_DASHBOARD_WIDGETS));
    };
    void loadLayout();
    return () => {
      cancelled = true;
    };
  }, [accessToken, gatewayBaseUrl, widgetSurface]);
  React.useEffect(() => {
    if (!calendarOpen) {
      return;
    }
    const node = document.getElementById('compliance-calendar-panel');
    node?.scrollIntoView({ block: 'start' });
  }, [calendarOpen]);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [taskSort, setTaskSort] = React.useState<'dependencies' | 'due' | 'unlocks' | 'blocking'>('dependencies');
  const projectPickedThisSession = React.useRef(false);
  const remoteReadyForAccount = React.useRef(false);

  const apiBase = String(gatewayBaseUrl || '').replace(/\/$/, '');
  const consoleStorageKey = subscriptionAccountId
    ? `${definition.storageKey}:${subscriptionAccountId}`
    : definition.storageKey;
  const canSyncRemote = Boolean(apiBase && accessToken);
  const runBulkDoTasks = React.useCallback(async (ids: string[]) => {
    const unique = Array.from(new Set(ids.map((id) => String(id || '').trim()).filter(Boolean)));
    if (!unique.length) return;
    if (!apiBase || !accessToken) {
      setBulkDoNote(language === 'es'
        ? 'Inicie sesión para que NextCompli pueda completar tareas.'
        : 'Sign in so NextCompli can complete tasks.');
      return;
    }
    setBulkDoBusy(true);
    setBulkDoNote(language === 'es'
      ? `NextCompli está trabajando ${unique.length} tarea(s)…`
      : `NextCompli is working ${unique.length} task(s)…`);
    let ok = 0;
    let failed = 0;
    let needsYou: string | null = null;
    try {
      for (const controlId of unique) {
        try {
          const response = await fetch(
            `${apiBase}/api/v1/compliance-registry/${encodeURIComponent(definition.kind)}/tasks/complete`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                control_id: controlId,
                ...(subscriptionAccountId ? { company_id: Number(subscriptionAccountId) } : {}),
              }),
            },
          );
          const body = await response.json().catch(() => ({})) as {
            needs_approval?: boolean;
            findings?: Array<{ status?: string }>;
            actions?: Array<{ status?: string }>;
          };
          if (!response.ok) {
            failed += 1;
            continue;
          }
          ok += 1;
          const blocked = (body.findings || []).some((row) => row.status === 'needs_credentials')
            || (body.actions || []).some((row) => row.status === 'failed' || row.status === 'staged');
          if ((body.needs_approval || blocked) && !needsYou) {
            needsYou = controlId;
          }
        } catch {
          failed += 1;
        }
      }
      setRegistryTick((tick) => tick + 1);
      setBulkDoNote(language === 'es'
        ? `Listo: ${ok} tarea(s)${failed ? `, ${failed} fallaron` : ''}.${needsYou ? ' Abriendo la que necesita su ayuda.' : ''}`
        : `Done: ${ok} task(s)${failed ? `, ${failed} failed` : ''}.${needsYou ? ' Opening one that needs you.' : ''}`);
      const openId = needsYou || unique[0];
      if (openId) {
        const pillar = definition.pillars.find((row) => row.items.some((item) => item.id === openId));
        if (pillar) {
          setOpenBoardSectionId(pillar.id);
        }
        setFocusDecisionId(openId);
        setSoloTask(true);
        setTaskAssist({ id: openId, mode: 'do' });
        setLaneFilter(['ready', 'blocked', 'done']);
      }
    } finally {
      setBulkDoBusy(false);
    }
  }, [accessToken, apiBase, definition.kind, language, subscriptionAccountId]);
  const writeLocked = Boolean(workspaceReadOnly);
  const isWebsite = definition.kind === 'website-compliance';
  const isCloud = definition.kind === 'cloud-compliance';
  const isOfac = definition.kind === 'ofac-compliance';
  const isDod = definition.kind === 'dod-contracts-compliance';
  const isBusiness = definition.kind === 'business-creation-compliance';
  const isWills = definition.kind === 'wills-trusts-compliance';
  const isEducation = definition.kind === 'education-compliance';
  const localizedCatalog = React.useMemo(
    () => localizeComplianceConsole(definition, language),
    [definition, language],
  );
  const catalog = React.useMemo(() => {
    if (isBusiness) {
      const answers = state.businessCreation || {};
      return {
        ...localizedCatalog,
        pillars: [
          ...businessCreationPillars(answers, language),
          ...localizedCatalog.pillars,
        ],
      };
    }
    if (isWills) {
      const answers = state.willsTrusts || {};
      return {
        ...localizedCatalog,
        pillars: [
          ...willsTrustsPillars(answers, language),
          ...localizedCatalog.pillars,
        ],
      };
    }
    return localizedCatalog;
  }, [isBusiness, isWills, language, localizedCatalog, state.businessCreation, state.willsTrusts]);
  // Solo mode paints a full-screen dim backdrop and elevates .compliance-dep-row.is-open.
  // If the board section for the focused step is closed, nothing sits above the backdrop
  // and the console looks frozen after Do it for me / bulk complete.
  React.useEffect(() => {
    if (!soloTask || !focusDecisionId) return;
    const pillar = catalog.pillars.find((row) => row.items.some((item) => item.id === focusDecisionId));
    if (pillar) {
      setOpenBoardSectionId(pillar.id);
    }
  }, [soloTask, focusDecisionId, catalog.pillars]);
  const unreadNoticeCount = (catalog.notices || []).filter((notice) => !noticeMarks[noticeKey(notice.id)]).length;
  const clearedNoticeCount = (catalog.notices || []).filter((notice) => noticeMarks[noticeKey(notice.id)] === 'cleared').length;
  const visibleNotices = (catalog.notices || []).filter((notice) => (
    showClearedNotices
      ? noticeMarks[noticeKey(notice.id)] === 'cleared'
      : noticeMarks[noticeKey(notice.id)] !== 'cleared'
  ));
  React.useEffect(() => {
    const currentRows: PortfolioCalendarEvent[] = (state.calendarEvents || [])
      .filter((event) => event.date && !event.skipped)
      .map((event) => ({ source: definition.title, event }));
    const localRows = Object.values(COMPLIANCE_CONSOLES).flatMap((consoleDef) => {
      if (consoleDef.kind === definition.kind) {
        return currentRows;
      }
      const key = subscriptionAccountId ? `${consoleDef.storageKey}:${subscriptionAccountId}` : consoleDef.storageKey;
      return (loadComplianceConsoleState(key).calendarEvents || [])
        .filter((event) => event.date && !event.skipped)
        .map((event) => ({ source: consoleDef.title, event }));
    });
    setPortfolioRows(localRows);
    if (!canSyncRemote || !subscriptionAccountId) {
      return undefined;
    }
    let cancel = false;
    void Promise.all(Object.values(COMPLIANCE_CONSOLES)
      .filter((consoleDef) => consoleDef.kind !== definition.kind)
      .map(async (consoleDef) => {
        const response = await fetch(
          `${apiBase}/api/v1/compliance-consoles/${encodeURIComponent(consoleDef.kind)}?company_id=${encodeURIComponent(subscriptionAccountId)}`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        if (!response.ok) {
          return [] as PortfolioCalendarEvent[];
        }
        const body = await response.json() as { state?: { calendarEvents?: unknown } };
        return parseCalendarEvents(body.state?.calendarEvents)
          .filter((event) => event.date && !event.skipped)
          .map((event) => ({ source: consoleDef.title, event }));
      }))
      .then((groups) => {
        if (!cancel) {
          setPortfolioRows([...currentRows, ...groups.flat()]);
        }
      })
      .catch(() => undefined);
    return () => {
      cancel = true;
    };
  }, [accessToken, apiBase, canSyncRemote, definition.kind, definition.title, state.calendarEvents, subscriptionAccountId]);
  const activePlan = React.useMemo(
    () => planFromFeatures(effectiveFeatures, { isPlatformAdmin: platformAdmin }),
    [effectiveFeatures, platformAdmin],
  );
  const planGated = isWebsite || isDod || isCloud || isOfac;
  const gatedPlan: PricingTierId = planGated ? activePlan : 'enterprise';

  React.useEffect(() => {
    setLaneFilter(['ready']);
  }, [definition.kind]);

  React.useEffect(() => {
    if (!canSyncRemote) {
      setAccountsLoaded(true);
      return undefined;
    }
    let cancelled = false;
    const loadAccounts = async () => {
      try {
        const response = await fetch(
          `${apiBase}/api/v1/compliance-consoles/${encodeURIComponent(definition.kind)}/accounts`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        if (!response.ok) {
          if (!cancelled) {
            setUsesRemoteCatalog(true);
            setSubscriptionAccounts([]);
          }
          return;
        }
        const body = await response.json();
        const catalog = Array.isArray(body?.accounts) ? body.accounts : null;
        const rows = catalog || [];
        const accounts = rows
          .map((row: { company_id?: number; company_name?: string; tier_name?: string; tier_id?: string; status?: string }) => ({
            id: Number(row.company_id),
            name: String(row.company_name || '').trim(),
            plan: String(row.tier_name || row.tier_id || '').trim(),
            status: String(row.status || '').trim(),
          }))
          .filter((row: { id: number; name: string }) => {
            const name = row.name.toLowerCase();
            return row.id && row.name && name !== 'nextcompli' && name !== 'keycloak';
          });
        if (cancelled) {
          return;
        }
        if (catalog) {
          setUsesRemoteCatalog(true);
          setSubscriptionAccounts(accounts);
          const ids = Array.isArray(body?.company_ids) ? body.company_ids.map((value: unknown) => Number(value)).filter((id: number) => id > 0) : [];
          setWorkingAccountIds(Array.from(new Set([...ids, ...addedThisSession.current])));
          setWorkingAccountsLoaded(true);
          setSubscriptionAccountId((current) => (
            current && accounts.some((row: { id: number }) => row.id === current) ? current : null
          ));
        }
      } catch {
        if (!cancelled) {
          setUsesRemoteCatalog(true);
          setSubscriptionAccounts([]);
        }
      } finally {
        if (!cancelled) {
          setAccountsLoaded(true);
        }
      }
    };
    void loadAccounts();
    return () => {
      cancelled = true;
    };
  }, [accessToken, apiBase, canSyncRemote, definition.kind, platformAdmin]);

  React.useEffect(() => {
    if (!canSyncRemote) {
      return undefined;
    }
    let cancelled = false;
    const loadWorkingAccounts = async () => {
      try {
        const response = await fetch(
          `${apiBase}/api/v1/compliance-consoles/${encodeURIComponent(definition.kind)}/accounts`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        if (!response.ok) {
          if (!cancelled) {
            setWorkingAccountsLoaded(true);
          }
          return;
        }
        const body = await response.json();
        const ids = Array.isArray(body?.company_ids) ? body.company_ids.map((value: unknown) => Number(value)).filter((id: number) => id > 0) : [];
        if (!cancelled) {
          setWorkingAccountIds(Array.from(new Set([...ids, ...addedThisSession.current])));
          setWorkingAccountsLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setWorkingAccountIds(Array.from(addedThisSession.current));
          setWorkingAccountsLoaded(true);
        }
      }
    };
    void loadWorkingAccounts();
    return () => {
      cancelled = true;
    };
  }, [accessToken, apiBase, canSyncRemote, definition.kind, subscriptionAccountId]);

  React.useEffect(() => {
    if (!workingAccountsLoaded || subscriptionAccountId == null) {
      return;
    }
    if (workingAccountIds.includes(subscriptionAccountId) || addedThisSession.current.has(subscriptionAccountId)) {
      return;
    }
    setSubscriptionAccountId(null);
  }, [definition.kind, subscriptionAccountId, workingAccountIds, workingAccountsLoaded]);

  React.useEffect(() => {
    remoteReadyForAccount.current = false;
    projectPickedThisSession.current = false;
    setRemoteReady(false);
    setActivePillarId('');
    setDodWorkspaceView('today');
    setFocusChecklistItemId('');
    setSaveFeedback(null);
    setSyncError(null);
    if (!canSyncRemote) {
      const loaded = bootstrapCompanyFromTargetName(loadComplianceConsoleState(consoleStorageKey));
      setState(loaded);
      if (loaded.lastScan?.final_url) {
        setScanUrl(loaded.lastScan.final_url);
      }
    }
  }, [canSyncRemote, consoleStorageKey]);

  React.useEffect(() => {
    const current = state.taxonomy || emptyDodTaxonomy();
    const active = current.companies.find((row) => row.id === current.activeCompanyId);
    if (active && !active.name.trim()) {
      setOrgDraft((draft) => draft || { name: '', ein: active.ein || '' });
    }
  }, [state.taxonomy]);

  React.useEffect(() => {
    if (!isDod || !canSyncRemote) {
      setSamMonitor(null);
      return undefined;
    }
    const company = (state.taxonomy || emptyDodTaxonomy()).companies.find(
      (row) => row.id === (state.taxonomy || emptyDodTaxonomy()).activeCompanyId,
    );
    const legalName = company?.name?.trim() || '';
    if (!legalName) {
      setSamMonitor(null);
      return undefined;
    }
    let cancelled = false;
    const loadSam = async () => {
      try {
        const response = await fetch(
          `${apiBase}/api/v1/sam/snapshot?legal_name=${encodeURIComponent(legalName)}`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        if (!response.ok) {
          return;
        }
        const payload = (await response.json()) as SamMonitorPayload;
        if (!cancelled) {
          setSamMonitor(payload);
        }
      } catch {
        if (!cancelled) {
          setSamMonitor(null);
        }
      }
    };
    void loadSam();
    return () => {
      cancelled = true;
    };
  }, [accessToken, apiBase, canSyncRemote, isDod, state.taxonomy]);

  const selectedAccountName = subscriptionAccounts.find((row) => row.id === subscriptionAccountId)?.name || '';

  React.useEffect(() => {
    if (!canSyncRemote || !accountsLoaded || (usesRemoteCatalog && !subscriptionAccountId) || (subscriptionAccounts.length > 0 && !subscriptionAccountId)) {
      return undefined;
    }
    let cancelled = false;
    const loadRemote = async () => {
      try {
        const accountQuery = subscriptionAccountId ? `?company_id=${subscriptionAccountId}` : '';
        const response = await fetch(
          `${apiBase}/api/v1/compliance-consoles/${encodeURIComponent(definition.kind)}${accountQuery}`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const body = await response.json();
        const remote = normalizeRemoteState(body);
        if (cancelled || !remote) {
          return;
        }
        const accountName = selectedAccountName || (body?.company_name ? String(body.company_name) : '');
        const adopted = (isBusiness || isCloud)
          ? adoptUnscopedBinder(remote, accountName, definition.storageKey)
          : remote;
        const scoped = isWebsite ? websitesWithAddresses(adopted) : adopted;
        const taxonomy = scoped.taxonomy || emptyDodTaxonomy();
        let nextTaxonomy = projectPickedThisSession.current
          ? taxonomy
          : {
            ...taxonomy,
            activeProjectId: '',
            activeContractId: '',
          };
        // Website / Formation: if exactly one named company exists and none is active, select it.
        if (!isDod) {
          const namedCompanies = (nextTaxonomy.companies || []).filter((row) => String(row.name || '').trim());
          if (namedCompanies.length === 1 && !String(nextTaxonomy.activeCompanyId || '').trim()) {
            nextTaxonomy = {
              ...nextTaxonomy,
              activeCompanyId: namedCompanies[0].id,
            };
          }
        }
        const withFreshProject = {
          ...scoped,
          taxonomy: nextTaxonomy,
        };
        saveComplianceConsoleState(consoleStorageKey, withFreshProject);
        const reviewerKey = String(reviewerLabel || '').trim().toLowerCase();
        if (reviewerKey.includes('@')) {
          saveComplianceConsoleState(`${definition.storageKey}:signed-in:${reviewerKey}`, withFreshProject);
        }
        remoteReadyForAccount.current = true;
        setState(withFreshProject);
        setRemoteReady(true);
        setRefreshBusy(false);
        if ((adopted !== remote || scoped !== adopted) && subscriptionAccountId) {
          void fetch(
            `${apiBase}/api/v1/compliance-consoles/${encodeURIComponent(definition.kind)}`,
            {
              method: 'PUT',
              headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ state: withFreshProject, company_id: subscriptionAccountId }),
            },
          );
        }
        setSyncError(null);
        if (withFreshProject.lastScan?.final_url) {
          setScanUrl(withFreshProject.lastScan.final_url);
        }
      } catch {
        if (!cancelled) {
          setRemoteReady(false);
          setRefreshBusy(false);
          setSyncError(
            language === 'es'
              ? 'No se pudo sincronizar con el servidor; se usa almacenamiento local.'
              : 'Could not sync with server; using local storage.',
          );
        }
      }
    };
    void loadRemote();
    return () => {
      cancelled = true;
    };
  }, [accessToken, accountsLoaded, apiBase, canSyncRemote, consoleStorageKey, definition.kind, definition.storageKey, isBusiness, isCloud, isDod, isWebsite, language, remoteRefreshNonce, selectedAccountName, subscriptionAccountId, subscriptionAccounts.length, usesRemoteCatalog]);

  const refreshRegistry = React.useCallback(async () => {
    if (!canSyncRemote || !subscriptionAccountId) {
      setRegistryProposals([]);
      return;
    }
    try {
      const controls = catalog.pillars.flatMap((pillar) => pillar.items.map((item) => ({
        id: item.id,
        title: item.title,
        evidenceHint: item.evidenceHint,
        verifySteps: (item.verifySteps || []).map((step) => ({
          id: step.id,
          text: step.text,
          evidenceHint: step.evidenceHint,
        })),
        priority: item.priority,
        gate: item.gate,
        completeRequires: item.completeRequires,
      })));
      await fetch(
        `${apiBase}/api/v1/compliance-registry/${encodeURIComponent(definition.kind)}/tasks/sync`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            company_id: subscriptionAccountId,
            controls,
            item_states: state.items,
          }),
        },
      );
      const response = await fetch(
        `${apiBase}/api/v1/compliance-registry/${encodeURIComponent(definition.kind)}?company_id=${encodeURIComponent(subscriptionAccountId)}`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      if (!response.ok) return;
      const body = await response.json() as { proposals?: RegistryProposal[] };
      setRegistryProposals(Array.isArray(body.proposals) ? body.proposals : []);
    } catch {
      // Registry is additive chrome; console still works offline.
    }
  }, [accessToken, apiBase, canSyncRemote, catalog.pillars, definition.kind, state.items, subscriptionAccountId]);

  React.useEffect(() => {
    if (!remoteReady || !canSyncRemote || !subscriptionAccountId) return undefined;
    void refreshRegistry();
    return undefined;
  }, [canSyncRemote, refreshRegistry, remoteReady, registryTick, subscriptionAccountId]);

  const runWebsiteScan = React.useCallback(async (
    urlOverride?: string,
    homepageOnly = false,
    options?: { purpose?: 'checklist' | 'hosting' },
  ) => {
    const purpose = options?.purpose || 'checklist';
    if (!isWebsite || !canSyncRemote) {
      setScanMessage(
        language === 'es'
          ? 'El escaneo requiere sesión y consola de sitio web.'
          : 'Scan requires sign-in and the website console.',
      );
      return;
    }
    const targetUrl = String(urlOverride || scanUrl || '').trim();
    if (!targetUrl || targetUrl === 'https://') {
      setScanMessage(
        language === 'es' ? 'Escriba una URL para escanear.' : 'Enter a URL to scan.',
      );
      return;
    }
    setScanBusy(true);
    setScanMessage(
      purpose === 'hosting'
        ? (language === 'es' ? `Buscando hosting de ${targetUrl}…` : `Looking up hosting for ${targetUrl}…`)
        : (language === 'es' ? `Escaneando ${targetUrl}…` : `Scanning ${targetUrl}…`),
    );
    try {
      const response = await fetch(
        `${apiBase}/api/v1/compliance-consoles/website-compliance/scan`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            url: targetUrl,
            ...(subscriptionAccountId ? { company_id: subscriptionAccountId } : {}),
            crawl_pages: homepageOnly || !crawlEnabled ? 1 : 8,
            cadence: scanCadence === 'daily' && activePlan === 'basic' ? 'weekly' : scanCadence,
          }),
        },
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body?.detail || `HTTP ${response.status}`);
      }
      const remote = normalizeRemoteState(body);
      if (remote) {
        saveComplianceConsoleState(consoleStorageKey, remote);
        const reviewerKey = String(reviewerLabel || '').trim().toLowerCase();
        if (reviewerKey.includes('@')) {
          saveComplianceConsoleState(`${definition.storageKey}:signed-in:${reviewerKey}`, remote);
        }
        setState(remote);
        setRemoteReady(true);
        if (remote.lastScan?.final_url) {
          setScanUrl(remote.lastScan.final_url);
        }
      }
      const hosting = remote?.lastScan && typeof remote.lastScan === 'object'
        ? (remote.lastScan as { hosting?: Record<string, unknown> }).hosting
        : null;
      if (purpose === 'hosting') {
        const providers = Array.isArray(hosting?.providers)
          ? (hosting?.providers as string[])
          : (Array.isArray(hosting?.cdns) ? (hosting?.cdns as string[]) : []);
        const klass = typeof hosting?.hosting_class === 'string' ? hosting.hosting_class : '';
        const bits = [
          providers.length ? providers.join(', ') : '',
          klass === 'managed_edge_or_saas' ? (language === 'es' ? 'CDN / SaaS' : 'CDN / SaaS')
            : klass === 'origin_visible' ? (language === 'es' ? 'origen visible' : 'origin visible')
              : '',
        ].filter(Boolean);
        setScanMessage(
          bits.length
            ? (language === 'es'
              ? `Hosting encontrado: ${bits.join(' · ')}.`
              : `Hosting found: ${bits.join(' · ')}.`)
            : (language === 'es'
              ? 'Búsqueda lista. No hubo una huella fuerte — use Este equipo o revise el plan abajo.'
              : 'Lookup finished. No strong fingerprint — use This computer or review the plan below.'),
        );
      } else {
        setActivePillarId('privacy-data-governance');
        setScanMessage(
          language === 'es'
            ? 'Escaneo de homepage completado. Se actualizaron políticas, CMP, GPC, declaración de accesibilidad, DMCA, HTTPS, HSTS y SPF/DKIM/DMARC; revisa los hallazgos y completa el resto de obligaciones legales con evidencia.'
            : 'Homepage scan complete. Policy, CMP, GPC, accessibility-statement, DMCA, HTTPS, HSTS, and SPF/DKIM/DMARC items were updated — review findings, then finish remaining legal items with evidence.',
        );
      }
      setRegistryTick((tick) => tick + 1);
    } catch (error) {
      setScanMessage(error instanceof Error ? error.message : 'Scan failed');
    } finally {
      setScanBusy(false);
    }
  }, [accessToken, activePlan, apiBase, canSyncRemote, consoleStorageKey, crawlEnabled, isWebsite, language, scanCadence, scanUrl, subscriptionAccountId]);

  React.useEffect(() => {
    const cadence = state.scanSchedule?.cadence;
    if (cadence === 'off' || cadence === 'weekly' || cadence === 'daily') {
      setScanCadence(cadence === 'daily' && activePlan === 'basic' ? 'weekly' : cadence);
    }
  }, [activePlan, state.scanSchedule?.cadence]);

  React.useEffect(() => {
    if (!isWebsite || !remoteReady || scanDueFired.current || scanBusy) {
      return;
    }
    const schedule = state.scanSchedule;
    if (!schedule || schedule.cadence === 'off' || !schedule.nextDueAt) {
      return;
    }
    if (schedule.cadence === 'daily' && activePlan === 'basic') {
      return;
    }
    const due = Date.parse(schedule.nextDueAt);
    if (!Number.isFinite(due) || due > Date.now()) {
      return;
    }
    const target = state.lastScan?.final_url || '';
    if (!target) {
      return;
    }
    scanDueFired.current = true;
    void runWebsiteScan(target, true);
  }, [activePlan, isWebsite, remoteReady, runWebsiteScan, scanBusy, state.lastScan?.final_url, state.scanSchedule]);

  const persist = React.useCallback(async (next: ComplianceConsoleState) => {
    const payload = syncActiveCompanyCheck(next);
    saveComplianceConsoleState(consoleStorageKey, payload);
    const reviewerKey = String(reviewerLabel || '').trim().toLowerCase();
    if (reviewerKey.includes('@')) {
      saveComplianceConsoleState(`${definition.storageKey}:signed-in:${reviewerKey}`, payload);
    }
    setState(payload);
    if (writeLocked) {
      setSaveFeedback(
        language === 'es'
          ? 'Solo lectura: un administrador o colaborador puede guardar cambios.'
          : 'Read-only: an Admin or Contributor can save changes.',
      );
      return;
    }
    if (!canSyncRemote || (usesRemoteCatalog && !subscriptionAccountId) || (subscriptionAccounts.length > 0 && (!subscriptionAccountId || !remoteReadyForAccount.current))) {
      setSaveFeedback(language === 'es' ? 'Progreso guardado localmente.' : 'Progress saved locally.');
      return;
    }
    setSaveBusy(true);
    setSaveFeedback(language === 'es' ? 'Guardando en el servidor…' : 'Saving to server…');
    try {
      const response = await fetch(
        `${apiBase}/api/v1/compliance-consoles/${encodeURIComponent(definition.kind)}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            state: payload,
            company_id: subscriptionAccountId,
          }),
        },
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      setRemoteReady(true);
      setSyncError(null);
      setSaveFeedback(language === 'es' ? 'Progreso guardado en el servidor.' : 'Progress saved to server.');
    } catch {
      setSaveFeedback(language === 'es' ? 'Progreso guardado localmente (servidor no disponible).' : 'Progress saved locally (server unavailable).');
      setSyncError(
        language === 'es'
          ? 'No se pudo sincronizar con el servidor; se usa almacenamiento local.'
          : 'Could not sync with server; using local storage.',
      );
    } finally {
      setSaveBusy(false);
    }
  }, [accessToken, apiBase, canSyncRemote, consoleStorageKey, definition.kind, language, subscriptionAccountId, subscriptionAccounts.length, usesRemoteCatalog, writeLocked]);

  const updateItem = React.useCallback((
    itemId: string,
    patch: Partial<{
      status: ComplianceItemStatus;
      notes: string;
      stepProgress: Record<string, ComplianceStepEvidence>;
      copySavedAt: string | null;
    }>,
    markReviewed = false,
  ) => {
    if (writeLocked) {
      setSaveFeedback(
        language === 'es'
          ? 'Solo lectura: un administrador o colaborador puede editar.'
          : 'Read-only: an Admin or Contributor can edit.',
      );
      return;
    }
    const previous = state.items[itemId] || { status: 'not_started' as ComplianceItemStatus, notes: '' };
    const nextStatus = markReviewed ? 'reviewed' : (patch.status ?? previous.status);
    const nextItem = {
      ...previous,
      status: nextStatus,
      notes: patch.notes ?? previous.notes,
      reviewedAt: nextStatus === 'reviewed' ? new Date().toISOString() : previous.reviewedAt,
      reviewedBy: nextStatus === 'reviewed' ? (reviewerLabel || previous.reviewedBy) : previous.reviewedBy,
      stepProgress: patch.stepProgress ?? previous.stepProgress,
      copySavedAt: patch.copySavedAt === null
        ? undefined
        : (patch.copySavedAt ?? (
          nextStatus === 'reviewed' && !previous.copySavedAt
            ? new Date().toISOString()
            : previous.copySavedAt
        )),
    };
    void persist({
      ...state,
      updatedAt: new Date().toISOString(),
      items: {
        ...state.items,
        [itemId]: nextItem,
      },
    });
  }, [language, persist, reviewerLabel, state, writeLocked]);

  React.useEffect(() => {
    if (!focusChecklistItemId || dodWorkspaceView !== 'checklist') {
      return undefined;
    }
    const frame = window.requestAnimationFrame(() => {
      const item = document.getElementById(`dod-check-item-${focusChecklistItemId}`);
      if (item instanceof HTMLElement) {
        scrollElementWithinConsole(item);
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusChecklistItemId, activePillarId, dodWorkspaceView]);

  const resetAll = () => {
    const cleared = emptyComplianceState();
    void persist(cleared);
    setScanMessage(null);
    setSaveFeedback(language === 'es' ? 'Lista reiniciada.' : 'Checklist reset.');
  };

  const selectDodCompanyId = (companyId: string) => {
    setDodWorkspaceView('today');
    setFocusChecklistItemId('');
    const next = selectDodCompany(state, companyId);
    void persist(isBusiness ? alignBusinessCreationToCompany(next) : next);
  };

  const selectDodProjectId = (projectId: string) => {
    projectPickedThisSession.current = Boolean(projectId);
    const taxonomy = state.taxonomy || emptyDodTaxonomy();
    const project = taxonomy.projects.find((row) => row.id === projectId);
    const withCompany = project?.companyId
      ? selectDodCompany(state, project.companyId)
      : state;
    const nextTaxonomy = withCompany.taxonomy || taxonomy;
    void persist({
      ...withCompany,
      updatedAt: new Date().toISOString(),
      taxonomy: {
        ...nextTaxonomy,
        activeProjectId: projectId,
        activeContractId: '',
      },
    });
  };

  const selectDodContractId = (contractId: string) => {
    const taxonomy = state.taxonomy || emptyDodTaxonomy();
    void persist({
      ...state,
      updatedAt: new Date().toISOString(),
      taxonomy: {
        ...taxonomy,
        activeContractId: contractId,
      },
    });
  };

  const addDodCompany = () => {
    setProjectDraft(null);
    setContractDraft(null);
    setOrgDraft({ name: '', ein: '' });
  };

  const saveDodCompany = async (event: React.FormEvent) => {
    event.preventDefault();
    const name = String(orgDraft?.name || '').trim();
    const ein = String(orgDraft?.ein || '').trim();
    if (!name || (isWebsite && !websiteScanUrlFromName(name))) {
      setSaveFeedback(
        isWebsite
          ? (language === 'es'
            ? 'Escriba una dirección pública, como ejemplo.com. El nombre de la empresa no es un sitio.'
            : 'Enter a public address, such as example.com. A company name is not a website.')
          : (isBusiness
            ? (language === 'es' ? 'Escriba el nombre de la empresa.' : 'Enter the company name.')
            : (language === 'es'
              ? 'Escriba la razón social exacta (carta EIN o archivo SOS).'
              : 'Enter the exact legal name from the EIN letter or SOS filing.')),
      );
      return;
    }
    const taxonomy = state.taxonomy || emptyDodTaxonomy();
    const unnamedId = taxonomy.companies.find((row) => row.id === taxonomy.activeCompanyId && !row.name.trim())?.id;
    const next = unnamedId
      ? selectDodCompany({
        ...state,
        taxonomy: {
          ...taxonomy,
          companies: taxonomy.companies.map((row) => (
            row.id === unnamedId ? { ...row, name, ein: ein || row.ein } : row
          )),
        },
      }, unnamedId)
      : (() => {
        const id = newDodId('company');
        return selectDodCompany({
          ...state,
          taxonomy: {
            ...taxonomy,
            companies: [...taxonomy.companies, { id, name, ein }],
          },
        }, id);
      })();
    const saved = isBusiness ? alignBusinessCreationToCompany(next) : next;
    const scanTarget = isWebsite ? websiteScanUrlFromName(name) : null;
    if (scanTarget) {
      setScanUrl(scanTarget);
      setDodWorkspaceView('today');
    }
    await persist(saved);
    setOrgDraft(null);
    if (scanTarget) {
      void runWebsiteScan(scanTarget, true);
    }
  };

  const cancelDodCompany = () => {
    const taxonomy = state.taxonomy || emptyDodTaxonomy();
    const unnamed = taxonomy.companies.find((row) => row.id === taxonomy.activeCompanyId && !row.name.trim());
    setOrgDraft(null);
    if (unnamed) {
      selectDodCompanyId('');
    }
  };

  const addDodProject = () => {
    setOrgDraft(null);
    setContractDraft(null);
    setProjectDraft({ name: '', dataHandling: 'fci' });
  };

  const saveDodProject = (event: React.FormEvent) => {
    event.preventDefault();
    const name = String(projectDraft?.name || '').trim();
    if (!name) {
      setSaveFeedback(
        language === 'es'
          ? 'Nombre el proyecto.'
          : 'Name the project.',
      );
      return;
    }
    const taxonomy = state.taxonomy || emptyDodTaxonomy();
    if (projectDraft?.id) {
      void persist({
        ...state,
        updatedAt: new Date().toISOString(),
        taxonomy: {
          ...taxonomy,
          projects: taxonomy.projects.map((project) => (
            project.id === projectDraft.id
              ? { ...project, name, dataHandling: projectDraft.dataHandling }
              : project
          )),
        },
      });
      setProjectDraft(null);
      return;
    }
    let nextTaxonomy = taxonomy;
    const namedCompany = taxonomy.companies.find((row) => row.id === taxonomy.activeCompanyId && row.name.trim());
    if (!namedCompany) {
      const accountName = subscriptionAccounts.find((row) => row.id === subscriptionAccountId)?.name || '';
      const fallback = taxonomy.companies.find((row) => row.name.trim());
      if (fallback) {
        nextTaxonomy = { ...taxonomy, activeCompanyId: fallback.id };
      } else {
        const companyId = newDodId('company');
        nextTaxonomy = {
          ...taxonomy,
          companies: [...taxonomy.companies, { id: companyId, name: accountName || name, ein: '' }],
          activeCompanyId: companyId,
        };
      }
    }
    const id = newDodId('project');
    void persist({
      ...state,
      updatedAt: new Date().toISOString(),
      taxonomy: {
        ...nextTaxonomy,
        projects: [...nextTaxonomy.projects, {
          id,
          companyId: nextTaxonomy.activeCompanyId,
          name,
          description: '',
          dataHandling: projectDraft?.dataHandling || 'fci',
        }],
        activeProjectId: id,
        activeContractId: '',
      },
    });
    setProjectDraft(null);
  };

  const addDodContract = () => {
    if (!(state.taxonomy || emptyDodTaxonomy()).activeProjectId) {
      return;
    }
    setContractDraft({ name: '' });
  };

  const saveDodContract = (event: React.FormEvent) => {
    event.preventDefault();
    const taxonomy = state.taxonomy || emptyDodTaxonomy();
    if (!taxonomy.activeProjectId) {
      return;
    }
    const name = String(contractDraft?.name || '').trim();
    if (!name) {
      setSaveFeedback(
        language === 'es'
          ? 'Identifique el aviso o contrato (número de solicitation o nombre del vehículo).'
          : 'Identify the solicitation or contract (notice number or vehicle name).',
      );
      return;
    }
    const id = newDodId('contract');
    void persist({
      ...state,
      updatedAt: new Date().toISOString(),
      taxonomy: {
        ...taxonomy,
        contracts: [...taxonomy.contracts, {
          id,
          projectId: taxonomy.activeProjectId,
          name,
          clauseText: '',
          requiredLevel: 'none',
          assessmentPath: 'none',
        }],
        activeContractId: id,
      },
    });
    setContractDraft(null);
  };

  const runDodClassify = async () => {
    const taxonomy = state.taxonomy || emptyDodTaxonomy();
    const company = taxonomy.companies.find((row) => row.id === taxonomy.activeCompanyId) || taxonomy.companies[0];
    const project = taxonomy.projects.find((row) => row.id === taxonomy.activeProjectId) || taxonomy.projects[0];
    const contract = taxonomy.contracts.find((row) => row.id === taxonomy.activeContractId);
    const input = {
      companyName: company?.name || state.targetCompanyName,
      projectName: project?.name,
      projectDescription: project?.description,
      contractName: contract?.name,
      clauseText: contract?.clauseText,
      dataHandling: project?.dataHandling,
      companyHasSam: (state.items['sam-uei-cage']?.status === 'reviewed'),
      companyHasSprsL1: (state.items['sprs-attestation']?.status === 'reviewed'),
      companyHasNist800171: (state.items['ssp-poam-cui']?.status === 'reviewed'),
    };
    let assessment = classifyDodLevel(input);
    setClassifyBusy(true);
    try {
      if (canSyncRemote) {
        const response = await fetch(
          `${apiBase}/api/v1/compliance-consoles/dod-contracts-compliance/classify`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              company_name: input.companyName || '',
              project_name: input.projectName || '',
              project_description: input.projectDescription || '',
              contract_name: input.contractName || '',
              clause_text: input.clauseText || '',
              data_handling: input.dataHandling || '',
              company_has_sam: Boolean(input.companyHasSam),
              company_has_sprs_l1: Boolean(input.companyHasSprsL1),
              company_has_nist_800_171: Boolean(input.companyHasNist800171),
            }),
          },
        );
        if (response.ok) {
          const fromApi = assessmentFromGateway(await response.json());
          if (fromApi) {
            assessment = fromApi;
          }
        }
      }
    } catch {
      // Keep the local rule result when the gateway overlay is unavailable.
    } finally {
      setClassifyBusy(false);
    }
    const nextContracts = taxonomy.contracts.map((row) => (
      row.id === taxonomy.activeContractId
        ? { ...row, requiredLevel: assessment.contractLevel, assessmentPath: assessment.assessmentPath }
        : row
    ));
    void persist({
      ...state,
      updatedAt: new Date().toISOString(),
      targetCompanyName: company?.name || state.targetCompanyName,
      taxonomy: { ...taxonomy, contracts: nextContracts },
      levelAssessment: assessment,
    });
    setSaveFeedback(
      language === 'es'
        ? `Nivel calculado: ${assessment.contractLevel.toUpperCase()} (${assessment.source === 'ai' ? 'IA' : 'reglas'}).`
        : `Level calculated: ${assessment.contractLevel.toUpperCase()} (${assessment.source === 'ai' ? 'AI' : 'rules'}).`,
    );
  };

  const totals = catalog.pillars.reduce(
    (acc, pillar) => {
      const progress = pillarProgress(pillar, state, gatedPlan);
      return {
        reviewed: acc.reviewed + progress.reviewed,
        gaps: acc.gaps + progress.gaps,
        total: acc.total + progress.total,
      };
    },
    { reviewed: 0, gaps: 0, total: 0 },
  );

  const progressCounts = catalog.pillars.reduce(
    (acc, pillar) => {
      pillar.items.filter((item) => itemIncludedInPlan(item, gatedPlan)).forEach((item) => {
        const status = (state.items[item.id]?.status || 'not_started') as ComplianceItemStatus;
        if (status === 'reviewed') {
          acc.done += 1;
        } else if (checklistItemWaiting(catalog, state, item)) {
          acc.blocked += 1;
        } else {
          acc.open += 1;
        }
      });
      return acc;
    },
    { done: 0, blocked: 0, open: 0 },
  );
  const activePillar = catalog.pillars.find((pillar) => pillar.id === activePillarId) || catalog.pillars[0];
  const lastScan = state.lastScan || null;
  const dodGates: Array<'entity' | 'fci' | 'cui' | 'classified' | 'pursuit'> = [
    'entity',
    'fci',
    'cui',
    'classified',
    'pursuit',
  ];
  const taxonomy = state.taxonomy || emptyDodTaxonomy();
  const selectedCompany = taxonomy.companies.find((row) => row.id === taxonomy.activeCompanyId);
  const companyProjects = taxonomy.projects.filter((row) => row.companyId === taxonomy.activeCompanyId);
  const accountProjects = taxonomy.projects;
  const selectedProject = (isDod ? accountProjects : companyProjects).find((row) => row.id === taxonomy.activeProjectId);
  const renameDodProject = () => {
    if (!selectedProject) {
      return;
    }
    setOrgDraft(null);
    setContractDraft(null);
    setProjectDraft({
      id: selectedProject.id,
      name: selectedProject.name,
      dataHandling: selectedProject.dataHandling || 'fci',
    });
  };
  const projectContracts = taxonomy.contracts.filter((row) => row.projectId === taxonomy.activeProjectId);
  const dodDecisions = selectedCompany ? nextDodDecisions(catalog, state, 3, gatedPlan) : [];
  const boardRows = selectedCompany
    ? catalog.pillars.flatMap((pillar) => (
      pillar.items
        .filter((item) => itemIncludedInPlan(item, gatedPlan))
        .map((item, index) => ({
          item,
          index,
          pillarId: pillar.id,
          pillarLabel: pillar.label,
          depth: checklistDependencyDepth(catalog, item.id),
        }))
    ))
    : [];
  const orderedBoard = boardRows.slice().sort((left, right) => (
    catalog.pillars.findIndex((pillar) => pillar.id === left.pillarId)
    - catalog.pillars.findIndex((pillar) => pillar.id === right.pillarId)
    || left.depth - right.depth
    || left.index - right.index
  ));
  const taskSections = orderedBoard.reduce<Array<{
    id: string;
    label: string;
    done: number;
    total: number;
    marks: Array<'done' | 'wait' | 'open'>;
    rows: typeof orderedBoard;
  }>>((groups, row) => {
    const status = (state.items[row.item.id]?.status || 'not_started') as ComplianceItemStatus;
    const waiting = checklistItemWaiting(catalog, state, row.item);
    let group = groups[groups.length - 1];
    if (!group || group.id !== row.pillarId) {
      const pillarRows = orderedBoard.filter((entry) => entry.pillarId === row.pillarId);
      const marks = pillarRows.map((entry) => {
        const entryStatus = (state.items[entry.item.id]?.status || 'not_started') as ComplianceItemStatus;
        if (entryStatus === 'reviewed') {
          return 'done' as const;
        }
        if (checklistItemWaiting(catalog, state, entry.item)) {
          return 'wait' as const;
        }
        return 'open' as const;
      });
      const done = marks.filter((mark) => mark === 'done').length;
      group = { id: row.pillarId, label: row.pillarLabel, done, total: pillarRows.length, marks, rows: [] };
      groups.push(group);
    }
    const glanceOk = !glanceLens
      || (glanceLens.startsWith('status:') && status === glanceLens.slice(7))
      || (glanceLens === 'priority:blocking' && row.item.priority === 'blocking' && status !== 'reviewed')
      || (glanceLens === 'priority:recommended' && row.item.priority === 'recommended' && status !== 'reviewed')
      || (glanceLens === 'priority:other' && row.item.priority !== 'blocking' && row.item.priority !== 'recommended' && status !== 'reviewed')
      || (glanceLens.startsWith('group:') && row.pillarId === glanceLens.slice(6));
    if (laneFilter.includes(taskLane(status, waiting)) && glanceOk) {
      group.rows.push(row);
    }
    return groups;
  }, []).filter((group) => group.rows.length > 0);
  const dueDateFor = (itemId: string): string => {
    const published = publishedDeadlinesFor(definition.kind).find((row) => row.itemId === itemId);
    if (published) {
      return published.date;
    }
    const saved = (state.calendarEvents || []).find((event) => event.id === itemId && event.date && !event.skipped);
    return saved?.date || '';
  };
  const unlockCount = (itemId: string): number => catalog.pillars.reduce((count, pillar) => (
    count + pillar.items.filter((item) => (item.dependsOn || []).includes(itemId)).length
  ), 0);
  const sortKey = (row: (typeof orderedBoard)[number]): string => {
    if (taskSort === 'due') {
      return dueDateFor(row.item.id) || '9999-99-99';
    }
    if (taskSort === 'unlocks') {
      return String(1000 - unlockCount(row.item.id)).padStart(4, '0');
    }
    if (taskSort === 'blocking') {
      return `${row.item.priority === 'blocking' ? '0' : '1'}${String(row.depth).padStart(2, '0')}`;
    }
    return String(row.depth).padStart(2, '0');
  };
  const sortedSections = taskSections
    .map((group) => ({
      ...group,
      rows: group.rows.slice().sort((left, right) => sortKey(left).localeCompare(sortKey(right)) || left.index - right.index),
    }))
    .sort((left, right) => {
      const leftKey = left.rows.reduce((best, row) => (sortKey(row) < best ? sortKey(row) : best), '9999');
      const rightKey = right.rows.reduce((best, row) => (sortKey(row) < best ? sortKey(row) : best), '9999');
      return leftKey.localeCompare(rightKey);
    });
  const nextStep = orderedBoard
    .filter((row) => {
      const status = (state.items[row.item.id]?.status || 'not_started') as ComplianceItemStatus;
      return status !== 'reviewed' && !checklistItemWaiting(catalog, state, row.item);
    })
    .slice()
    .sort((left, right) => left.depth - right.depth || left.index - right.index)[0];
  const openFormationHomeTask = (taskId: string) => {
    openKeyTask(taskId);
    setDodWorkspaceView('checklist');
  };
  const keepCopyPending = catalog.pillars.reduce((count, pillar) => (
    count + pillar.items.filter((item) => {
      if (!item.keepCopy || !itemIncludedInPlan(item, gatedPlan)) {
        return false;
      }
      const itemState = state.items[item.id];
      return !itemState?.copySavedAt && itemState?.status !== 'reviewed';
    }).length
  ), 0);
  const einFromNotes = (() => {
    const notes = String(state.items['ein-irs']?.notes || '');
    const match = notes.match(/\b(\d{2}-\d{7}|\d{9})\b/);
    return match?.[1] || '';
  })();
  const einValue = selectedCompany?.ein?.trim() || einFromNotes;
  const ueiValue = selectedCompany?.uei?.trim() || '';
  const cageValue = selectedCompany?.cage?.trim() || '';
  const einReady = Boolean(einValue) || (state.items['ein-irs']?.status === 'reviewed');
  const samSnap = samMonitor?.snapshot;
  const samOpportunities = (samMonitor?.opportunities || []).slice(0, 5);
  const samNotices = (samMonitor?.notices || []).slice(0, 3);
  const samMonitorLabel = (() => {
    if (!samMonitor) {
      return '';
    }
    if (!samMonitor.catalog?.configured) {
      return language === 'es'
        ? 'SAM.gov: el monitor espera una clave de API pública (Account Details). No rastrea el sitio.'
        : 'SAM.gov: monitor waiting for a public API key (Account Details). It does not scrape the site.';
    }
    if (samSnap?.error) {
      return `SAM.gov: ${samSnap.error}`;
    }
    const extras: string[] = [];
    if (samOpportunities.length) {
      extras.push(
        language === 'es'
          ? `${samOpportunities.length} oportunidad(es) NAICS 541512`
          : `${samOpportunities.length} NAICS 541512 opportunit${samOpportunities.length === 1 ? 'y' : 'ies'}`,
      );
    }
    if (samNotices.length) {
      extras.push(
        language === 'es'
          ? `${samNotices.length} aviso(s) por correo`
          : `${samNotices.length} official email notice${samNotices.length === 1 ? '' : 's'}`,
      );
    }
    if (samSnap?.found && (samSnap.uei_sam || samSnap.registration_status)) {
      const bits = [
        samSnap.registration_status || (language === 'es' ? 'Encontrado' : 'Found'),
        samSnap.exclusion_status
          ? `exclusion ${samSnap.exclusion_status}`
          : (language === 'es' ? 'sin exclusión' : 'no exclusion flag'),
      ];
      if (samSnap.uei_sam) {
        bits.push(`UEI ${samSnap.uei_sam}`);
      }
      if (samSnap.cage_code) {
        bits.push(`CAGE ${samSnap.cage_code}`);
      }
      return `SAM.gov: ${[...bits, ...extras].join(' · ')}`;
    }
    const pending = language === 'es'
      ? 'SAM.gov: todavía no hay un registro público Activo.'
      : 'SAM.gov: no public Active record yet.';
    return extras.length ? `${pending} ${extras.join(' · ')}` : pending;
  })();
  const llcReady = Boolean(selectedCompany?.name.trim()) && (state.items['tx-llc-good-standing']?.status === 'reviewed');
  const capabilityItem = state.items['capability-statement'];
  const capabilityNotes = String(capabilityItem?.notes || '').trim();
  const capabilityStatus = (capabilityItem?.status || 'not_started') as ComplianceItemStatus;
  const capabilityReady = capabilityStatus === 'reviewed';
  const capabilityHasBacking = Boolean(
    capabilityNotes
    || capabilityStatus === 'reviewed'
    || capabilityStatus === 'in_progress'
    || capabilityStatus === 'gap',
  );
  const llcHasBacking = Boolean(selectedCompany?.name.trim());
  const einHasBacking = Boolean(einValue || String(state.items['ein-irs']?.notes || '').trim() || state.items['ein-irs']?.status === 'reviewed');
  const samItem = state.items['sam-uei-cage'];
  const samHasBacking = Boolean(
    ueiValue
    || cageValue
    || String(samItem?.notes || '').trim()
    || samItem?.status === 'reviewed'
    || samItem?.status === 'in_progress'
    || samItem?.status === 'gap',
  );
  const ueiHasBacking = Boolean(ueiValue || samHasBacking);
  const cageHasBacking = Boolean(cageValue || samHasBacking);
  const openKeyCompanyIds = () => {
    setSoloTask(false);
    setFocusDecisionId(null);
    setCompanyIdsOpen(true);
  };
  const openKeyTask = (taskId: string) => {
    setCompanyIdsOpen(false);
    const match = orderedBoard.find((row) => row.item.id === taskId);
    if (match) {
      setOpenBoardSectionId(match.pillarId);
    }
    setFocusDecisionId(taskId);
    setSoloTask(true);
    setLaneFilter(['ready', 'blocked', 'done']);
  };
  const contractLevel = state.levelAssessment?.contractLevel || 'none';
  const cmmcPracticeRows = contractLevel === 'l2' || contractLevel === 'l3'
    ? [...CMMC_L1_PRACTICES, ...CMMC_L2_PRACTICES]
    : CMMC_L1_PRACTICES;
  const cmmcPracticeDone = cmmcPracticeRows.filter((practice) => (
    (state.practiceDispositions?.[practice.id] || 'not_started') === 'met'
  )).length;
  const applicableDodGates = dodGates.filter((gate) => {
    if (dodItemsForGate(definition, gate).length === 0) {
      return false;
    }
    if (gate === 'entity') {
      return true;
    }
    if (gate === 'fci') {
      return Boolean(selectedProject) || contractLevel === 'l1' || contractLevel === 'l2' || contractLevel === 'l3';
    }
    if (gate === 'cui') {
      return contractLevel === 'l2' || contractLevel === 'l3' || state.levelAssessment?.dataHandling === 'cui';
    }
    if (gate === 'classified') {
      return contractLevel === 'l3' || state.levelAssessment?.dataHandling === 'classified';
    }
    return Boolean(selectedProject);
  });

  const es = language === 'es';
  const formationAnswers = state.businessCreation || {};
  const willsAnswers = state.willsTrusts || {};
  const hasNamedProject = Boolean(selectedProject && selectedProject.name.trim() && !projectDraft);
  const visibleAccountIds = new Set<number>(workingAccountIds);
  const workingAccounts = subscriptionAccounts.filter((account) => visibleAccountIds.has(account.id));
  const mustPickAccount = canSyncRemote && !subscriptionAccountId && (
    !accountsLoaded || usesRemoteCatalog || subscriptionAccounts.length > 0
  );
  const hasNamedCompany = !mustPickAccount && (isDod
    ? (hasNamedProject || Boolean(taskFocus && selectedCompany?.name.trim()))
    : Boolean(
      selectedCompany
      && selectedCompany.name.trim()
      && !orgDraft
      && (!isWebsite || websiteScanUrlFromName(selectedCompany.name)),
    ));
  const glanceRows = catalog.pillars.flatMap((pillar) => (
    pillar.items
      .filter((item) => itemIncludedInPlan(item, gatedPlan))
      .map((item) => {
        const status = (state.items[item.id]?.status || 'not_started') as ComplianceItemStatus;
        const waiting = checklistItemWaiting(catalog, state, item);
        return {
          id: item.id,
          title: item.title,
          action: item.action || '',
          pillar: pillar.label,
          status,
          waiting,
          priority: item.priority || '',
          done: status === 'reviewed',
          locked: isItemCompleteLocked(item, state.items[item.id], { uei: ueiValue, cage: cageValue }),
        };
      })
  ));
  // Live tallies only after a named project/company is chosen — never show catalog defaults as “saved” work.
  const liveGlanceRows = hasNamedCompany ? glanceRows : [];
  const glanceTally = {
    reviewed: liveGlanceRows.filter((row) => row.status === 'reviewed').length,
    in_progress: liveGlanceRows.filter((row) => row.status === 'in_progress').length,
    not_started: liveGlanceRows.filter((row) => row.status === 'not_started').length,
    gap: liveGlanceRows.filter((row) => row.status === 'gap').length,
  };
  const glanceTotal = liveGlanceRows.length;
  const glanceShare = glanceTotal > 0 ? Math.round((glanceTally.reviewed / glanceTotal) * 100) : 0;
  const glanceBlocking = liveGlanceRows.filter((row) => row.priority === 'blocking' && row.status !== 'reviewed').length;
  const glanceRecommended = liveGlanceRows.filter((row) => row.priority === 'recommended' && row.status !== 'reviewed').length;
  const pickGlance = (next: string) => {
    const clearing = glanceLens === next;
    setGlanceLens(clearing ? '' : next);
    setLaneFilter(clearing ? ['ready'] : ['ready', 'blocked', 'done']);
    if (clearing) {
      return;
    }
    if (next.startsWith('group:')) {
      setOpenBoardSectionId(next.slice(6));
      return;
    }
    const match = orderedBoard.find((row) => {
      const status = (state.items[row.item.id]?.status || 'not_started') as ComplianceItemStatus;
      if (next.startsWith('status:')) {
        return status === next.slice(7);
      }
      if (next === 'priority:blocking') {
        return row.item.priority === 'blocking' && status !== 'reviewed';
      }
      if (next === 'priority:recommended') {
        return row.item.priority === 'recommended' && status !== 'reviewed';
      }
      if (next === 'priority:other') {
        return row.item.priority !== 'blocking' && row.item.priority !== 'recommended' && status !== 'reviewed';
      }
      return false;
    });
    if (match) {
      setOpenBoardSectionId(match.pillarId);
    }
  };
  const glancePillars = Array.from(liveGlanceRows.reduce((groups, row) => {
    const list = groups.get(row.pillar) || [];
    list.push(row);
    groups.set(row.pillar, list);
    return groups;
  }, new Map<string, typeof liveGlanceRows>()));
  const linkedCompany = React.useRef(false);
  const linkedTask = React.useRef(false);
  const chooseSubscriptionAccount = (accountId: number) => {
    addedThisSession.current.add(accountId);
    projectPickedThisSession.current = false;
    setSubscriptionAccountId(accountId);
    setWorkingAccountIds((current) => (current.includes(accountId) ? current : [...current, accountId]));
    const base = String(gatewayBaseUrl || '').replace(/\/$/, '');
    if (base && accessToken) {
      void writeAccountPreference(base, accessToken, `console-account:${definition.kind}`, { companyId: accountId });
    }
  };
  React.useEffect(() => {
    if (!accountsLoaded || taskFocus || subscriptionAccountId || subscriptionAccounts.length === 0) {
      return undefined;
    }
    let cancelled = false;
    const restoreAccount = async () => {
      const base = String(gatewayBaseUrl || '').replace(/\/$/, '');
      let savedId = 0;
      if (base && accessToken) {
        const result = await readAccountPreference(base, accessToken, `console-account:${definition.kind}`);
        if (cancelled) {
          return;
        }
        if (result.ok && result.value && typeof result.value === 'object') {
          savedId = Number((result.value as { companyId?: number }).companyId) || 0;
        }
      }
      const planRank = (plan: string): number => {
        const normalized = String(plan || '').toLowerCase();
        if (normalized.includes('complete') || normalized.includes('enterprise')) return 3;
        if (normalized.includes('advantage') || normalized.includes('advanced')) return 2;
        if (normalized.includes('core') || normalized.includes('basic')) return 1;
        return 0;
      };
      const saved = subscriptionAccounts.find((row) => row.id === savedId);
      const highest = [...subscriptionAccounts].sort((a, b) => planRank(b.plan) - planRank(a.plan))[0];
      const next = saved || highest;
      if (next) {
        chooseSubscriptionAccount(next.id);
      }
    };
    void restoreAccount();
    return () => {
      cancelled = true;
    };
  }, [accountsLoaded, accessToken, definition.kind, gatewayBaseUrl, subscriptionAccountId, subscriptionAccounts, taskFocus]);
  React.useEffect(() => {
    if (linkedCompany.current || !accountsLoaded) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const company = taskFocus?.companyId || Number(params.get('company') || '');
    const project = taskFocus?.projectId || params.get('project') || '';
    if (!company) {
      return;
    }
    if (taskFocus && !subscriptionAccounts.some((row) => row.id === company)) {
      setSubscriptionAccounts((current) => [
        ...current,
        { id: company, name: taskFocus.companyName || '', plan: '', status: 'active' },
      ]);
    } else if (!subscriptionAccounts.some((row) => row.id === company)) {
      return;
    }
    linkedCompany.current = true;
    chooseSubscriptionAccount(company);
    if (project) {
      projectPickedThisSession.current = true;
    }
  }, [accountsLoaded, subscriptionAccounts, taskFocus?.companyId, taskFocus?.companyName]);
  React.useEffect(() => {
    const project = taskFocus?.projectId || new URLSearchParams(window.location.search).get('project') || '';
    if (!project) {
      return;
    }
    const taxonomy = state.taxonomy || emptyDodTaxonomy();
    if (!taxonomy.projects.some((row) => row.id === project)) {
      return;
    }
    if (taxonomy.activeProjectId === project) {
      return;
    }
    selectDodProjectId(project);
  }, [state.taxonomy, taskFocus?.projectId]);
  React.useEffect(() => {
    if (taskFocus || mustPickAccount || !isDod) {
      return;
    }
    const taxonomy = state.taxonomy || emptyDodTaxonomy();
    if (taxonomy.activeProjectId || projectDraft) {
      return;
    }
    if (taxonomy.projects.length !== 1) {
      return;
    }
    selectDodProjectId(taxonomy.projects[0].id);
  }, [isDod, mustPickAccount, projectDraft, state.taxonomy, taskFocus]);
  React.useEffect(() => {
    if (!taskFocus) {
      return;
    }
    const taxonomyNow = state.taxonomy || emptyDodTaxonomy();
    const companies = taxonomyNow.companies.filter((row) => row.name.trim());
    const entityName = String(taskFocus.entityName || '').split(' — ')[0].trim().toLowerCase();
    const match = companies.find((row) => row.id === taskFocus.projectId)
      || companies.find((row) => entityName && row.name.trim().toLowerCase() === entityName)
      || (companies.length === 1 ? companies[0] : undefined);
    if (!match || taxonomyNow.activeCompanyId === match.id) {
      return;
    }
    if (!taxonomyNow.activeCompanyId) {
      setState((current) => {
        const tax = current.taxonomy || emptyDodTaxonomy();
        if (tax.activeCompanyId === match.id) {
          return current;
        }
        if (tax.companies.some((row) => row.id === tax.activeCompanyId && row.name.trim())) {
          return current;
        }
        return {
          ...current,
          taxonomy: {
            ...tax,
            activeCompanyId: match.id,
          },
        };
      });
      return;
    }
    selectDodCompanyId(match.id);
  }, [state.taxonomy, taskFocus]);
  React.useEffect(() => {
    if (linkedTask.current) {
      return;
    }
    const taskId = taskFocus?.taskId || new URLSearchParams(window.location.search).get('task') || '';
    if (!taskId || !hasNamedCompany) {
      return;
    }
    const project = taskFocus?.projectId || new URLSearchParams(window.location.search).get('project') || '';
    const taxonomy = state.taxonomy || emptyDodTaxonomy();
    const projectIsCompany = taxonomy.companies.some((row) => row.id === project);
    const projectKnown = Boolean(project) && !projectIsCompany && taxonomy.projects.some((row) => row.id === project);
    if (projectKnown && taxonomy.activeProjectId !== project) {
      return;
    }
    const pillar = catalog.pillars.find((row) => row.items.some((item) => item.id === taskId));
    if (!pillar) {
      return;
    }
    linkedTask.current = true;
    setLaneFilter(['ready', 'blocked', 'done']);
    setOpenBoardSectionId(pillar.id);
    setFocusDecisionId(taskId);
    setSoloTask(true);
    setDodWorkspaceView('today');
  }, [catalog, hasNamedCompany, state.taxonomy, taskFocus?.projectId, taskFocus?.taskId]);
  const showProjectOrCompanyStep = !mustPickAccount;
  const formationComplete = isBusiness && isBusinessCreationComplete(formationAnswers);
  // Existing companies open the progress dashboard. Formation questions stay in a
  // collapsed intake panel (Visa/NN-g progressive disclosure) so the page is not a wall of choices.
  const showFormationIntake = isBusiness && hasNamedCompany && (!formationComplete || revisingFormation);
  const commitFormation = (nextAnswers: ComplianceConsoleState['businessCreation']) => {
    const answers = nextAnswers || {};
    let next: ComplianceConsoleState = {
      ...state,
      updatedAt: new Date().toISOString(),
      businessCreation: answers,
    };
    if (isBusinessCreationComplete(answers)) {
      next = ensureBusinessCompany(next, answers);
      setRevisingFormation(false);
    }
    void persist(next);
  };
  React.useEffect(() => {
    if (!isBusiness || orgDraft) {
      return;
    }
    const aligned = alignBusinessCreationToCompany(state);
    if (aligned === state) {
      return;
    }
    void persist(aligned);
  }, [isBusiness, orgDraft, persist, state]);
  const answerFormation = (id: BusinessQuestionId, value: string) => {
    commitFormation(formationDraftFrom(formationAnswers, id, value));
  };
  const willsComplete = isWills && isWillsTrustsComplete(willsAnswers);
  const showWillsIntake = isWills && hasNamedCompany && (!willsComplete || revisingWills);
  const commitWills = (nextAnswers: ComplianceConsoleState['willsTrusts']) => {
    const answers = nextAnswers || {};
    if (isWillsTrustsComplete(answers)) {
      setRevisingWills(false);
    }
    void persist({
      ...state,
      updatedAt: new Date().toISOString(),
      willsTrusts: answers,
    });
  };
  const answerWills = (id: WillsQuestionId, value: string) => {
    commitWills(answerWillsQuestion(willsAnswers, id, value));
  };
  const scopeCopy = isCloud
    ? {
      which: es ? 'Cuenta de nube' : 'Cloud account',
      howPick: es ? 'Cómo elegir la cuenta de nube' : 'How to pick the cloud account',
      pickHelp: es
        ? 'Elija la cuenta de nube del cliente. No es la cuenta donde corre NextCompli. El sitio público se revisa en Cumplimiento del sitio web, sin este acceso.'
        : 'Pick the client’s cloud account. It is not the account that runs NextCompli. The public site is reviewed in Website Compliance, without this access.',
      choose: es ? 'Seleccionar cuenta de nube' : 'Select cloud account',
      add: es ? 'Añadir cuenta de nube' : 'Add cloud account',
      empty: es ? 'Todavía no hay cuentas de nube.' : 'No cloud accounts are saved yet.',
      new: es ? 'Cuenta de nube nueva' : 'New cloud account',
      whenAdd: es ? 'Cuándo añadir una cuenta de nube' : 'When to add a cloud account',
      addHelp: es
        ? 'Esto nombra la cuenta del cliente. No guarda contraseñas ni claves.'
        : 'This names the client account. It does not store passwords or secret keys.',
      nameLabel: es ? 'Nombre de la cuenta de nube' : 'Cloud account name',
      namePlaceholder: es ? 'Nombre que usa el cliente para esta cuenta' : 'Name the client uses for this account',
      save: es ? 'Guardar cuenta de nube' : 'Save cloud account',
      views: es ? 'Vistas de la cuenta' : 'Account views',
      nameInputId: 'cloud-account-name',
    }
    : isWebsite
    ? {
      which: es ? 'Sitio' : 'Website',
      howPick: es ? 'Cómo elegir un sitio' : 'How to pick a website',
      pickHelp: es
        ? 'Elija un sitio guardado en el menú. Para uno nuevo, pulse Añadir sitio y escriba la dirección pública, como ejemplo.com. Eso inicia el escaneo de la homepage.'
        : 'Pick a saved website in the menu. For a new one, press Add website and type the public address, such as example.com. That starts the homepage scan.',
      choose: es ? 'Seleccionar sitio' : 'Select website',
      add: es ? 'Añadir sitio' : 'Add website',
      empty: es ? 'Todavía no hay sitios.' : 'No websites are saved yet.',
      new: es ? 'Sitio nuevo' : 'New website',
      whenAdd: es ? 'Cuándo añadir un sitio' : 'When to add a website',
      addHelp: es
        ? 'Esto crea un sitio nuevo. Si escribe una URL o dominio (ejemplo.com), se escanea la homepage sola. Si quería uno que ya existe, pulse Cancelar y elíjalo en Sitio.'
        : 'This creates a new website. A URL or domain (example.com) starts a homepage scan automatically. If you wanted one that already exists, press Cancel and pick it from Website.',
      nameLabel: es ? 'Nombre del sitio' : 'Website name',
      namePlaceholder: es ? 'ejemplo.com' : 'example.com',
      save: es ? 'Guardar sitio' : 'Save website',
      views: es ? 'Vistas del sitio' : 'Website views',
      nameInputId: 'website-scope-name',
    }
    : isBusiness
    ? {
      which: es ? 'Entidad legal' : 'Legal entity',
      howPick: es ? 'Cómo elegir una entidad legal' : 'How to pick a legal entity',
      pickHelp: es
        ? 'Elija una entidad legal ya guardada. No se escribe en el menú. Para una nueva, pulse Añadir entidad legal.'
        : 'Pick a legal entity that is already saved. You cannot type in the menu. For a new one, press Add legal entity.',
      choose: es ? 'Seleccionar entidad legal' : 'Select legal entity',
      add: es ? 'Añadir entidad legal' : 'Add legal entity',
      empty: es ? 'Todavía no hay entidades legales.' : 'No legal entities are saved yet.',
      new: es ? 'Entidad legal nueva' : 'New legal entity',
      whenAdd: es ? 'Cuándo añadir una entidad legal' : 'When to add a legal entity',
      addHelp: es
        ? 'Esto crea una entidad legal nueva. Si quería una que ya existe, pulse Cancelar y elíjala en Entidad legal.'
        : 'This creates a new legal entity. If you wanted one that already exists, press Cancel and pick it from Legal entity.',
      nameLabel: es ? 'Nombre legal' : 'Legal name',
      namePlaceholder: es ? 'Nombre que presentará al estado' : 'Name you will file with the state',
      save: es ? 'Guardar entidad legal' : 'Save legal entity',
      views: es ? 'Vistas de la entidad legal' : 'Legal entity views',
      nameInputId: 'formation-company-name',
    }
    : isWills
    ? {
      which: es ? 'Persona' : 'Person',
      howPick: es ? 'Cómo elegir una persona' : 'How to pick a person',
      pickHelp: es
        ? 'Elija una persona ya guardada. No se escribe en el menú. Para una nueva, pulse Añadir persona.'
        : 'Pick a person that is already saved. You cannot type in the menu. For a new one, press Add person.',
      choose: es ? 'Seleccionar persona' : 'Select person',
      add: es ? 'Añadir persona' : 'Add person',
      empty: es ? 'Todavía no hay personas.' : 'No people are saved yet.',
      new: es ? 'Persona nueva' : 'New person',
      whenAdd: es ? 'Cuándo añadir una persona' : 'When to add a person',
      addHelp: es
        ? 'Esto nombra a la persona del plan. No redacta un testamento ni un fideicomiso.'
        : 'This names the person for the plan. It does not write a will or a trust.',
      nameLabel: es ? 'Nombre legal' : 'Legal name',
      namePlaceholder: es ? 'Nombre en la licencia o el pasaporte' : 'Name on the license or passport',
      save: es ? 'Guardar persona' : 'Save person',
      views: es ? 'Vistas de la persona' : 'Person views',
      nameInputId: 'wills-person-name',
    }
    : isEducation
    ? {
      which: es ? 'Campus' : 'Campus',
      howPick: es ? 'Cómo elegir un campus' : 'How to pick a campus',
      pickHelp: es
        ? 'Elija un campus ya guardado. No se escribe en el menú. Para uno nuevo, pulse Añadir campus.'
        : 'Pick a campus that is already saved. You cannot type in the menu. For a new one, press Add campus.',
      choose: es ? 'Seleccionar campus' : 'Select campus',
      add: es ? 'Añadir campus' : 'Add campus',
      empty: es ? 'Todavía no hay campus.' : 'No campuses are saved yet.',
      new: es ? 'Campus nuevo' : 'New campus',
      whenAdd: es ? 'Cuándo añadir un campus' : 'When to add a campus',
      addHelp: es
        ? 'Esto crea un campus nuevo. Si quería uno que ya existe, pulse Cancelar y elíjalo en Campus.'
        : 'This creates a new campus. If you wanted one that already exists, press Cancel and pick it from Campus.',
      nameLabel: es ? 'Nombre del campus' : 'Campus name',
      namePlaceholder: es ? 'Nombre legal de la institución' : 'Institution’s legal name',
      save: es ? 'Guardar campus' : 'Save campus',
      views: es ? 'Vistas del campus' : 'Campus views',
      nameInputId: 'education-campus-name',
    }
    : {
      which: es ? 'Proyecto' : 'Project',
      howPick: es ? 'Cómo elegir un proyecto' : 'How to pick a project',
      pickHelp: es
        ? 'Elija un proyecto ya guardado. No se escribe en el menú. Para uno nuevo, pulse Añadir proyecto.'
        : 'Pick a project that is already saved. You cannot type in the menu. For a new one, press Add project.',
      choose: es ? 'Seleccionar proyecto' : 'Select project',
      add: es ? 'Añadir proyecto' : 'Add project',
      empty: es ? 'Todavía no hay proyectos.' : 'No projects are saved yet.',
      new: es ? 'Proyecto nuevo' : 'New project',
      whenAdd: es ? 'Cuándo añadir un proyecto' : 'When to add a project',
      addHelp: es
        ? 'Esto crea un proyecto nuevo. Si quería uno que ya existe, pulse Cancelar y elíjalo en Proyecto.'
        : 'This creates a new project. If you wanted one that already exists, press Cancel and pick it from Project.',
      nameLabel: es ? 'Nombre del proyecto' : 'Project name',
      namePlaceholder: isDod
        ? (es ? 'Tal como aparece en la carta del IRS' : 'Exactly as it appears on the IRS letter')
        : (es ? 'Campus u oficina de investigación' : 'Campus or research office'),
      save: es ? 'Guardar proyecto' : 'Save project',
      views: es ? 'Vistas del proyecto' : 'Project views',
      nameInputId: 'dod-org-legal-name',
    };

  const workspaceLoading = !accountsLoaded || (canSyncRemote && Boolean(subscriptionAccountId) && !remoteReady);

  return (
    <section
      className={`dashboard-settings-sections dashboard-settings-sections-centered dashboard-settings-sections-research compliance-console compliance-console-wide${taskFocus ? ' is-task-dialog is-task-ready' : ''}${soloTask && focusDecisionId ? ' is-solo-task' : ''}${workspaceLoading ? ' is-workspace-loading' : ''}`}
      aria-label={catalog.title}
      aria-busy={workspaceLoading || undefined}
    >
      {workspaceLoading ? (
        <div className="compliance-workspace-loading" role="status">
          <span className="compliance-inline-spinner" aria-hidden="true" />
          <span>
            {language === 'es' ? 'Cargando el espacio de cumplimiento…' : 'Loading compliance workspace…'}
          </span>
          <div className="compliance-workspace-loading-skeleton" aria-hidden="true">
            <div className="compliance-workspace-loading-bar" />
            <div className="compliance-workspace-loading-bar is-short" />
            <div className="compliance-workspace-loading-card" />
            <div className="compliance-workspace-loading-card" />
          </div>
        </div>
      ) : null}
      {soloTask && focusDecisionId && !taskFocus ? (
        <button
          type="button"
          className="task-solo-backdrop"
          aria-label={language === 'es' ? 'Cerrar este paso' : 'Close this step'}
          onClick={() => {
            setSoloTask(false);
            setFocusDecisionId(null);
          }}
        />
      ) : null}
      <article className="card settings-card">
        <div className="settings-card-header compliance-console-header">
          <h2 className="help-badge-label-row">
            <FeatureMark featureKey={definition.kind} />
            {catalog.title}
            <HelpBadge label={language === 'es' ? 'Acerca de esta consola' : 'About this console'}>
              <p>
                {isCloud
                  ? (language === 'es'
                    ? 'Primero elija la cuenta de suscripción y luego la cuenta de nube del cliente. Esta consola necesita el acceso de solo lectura que el cliente concede. No usa la cuenta donde corre NextCompli. El sitio público se revisa en Cumplimiento del sitio web, sin ese acceso.'
                    : 'First pick the subscription account, then the client’s cloud account. This console needs the read-only access that client grants. It does not use the account that runs NextCompli. The public site is reviewed in Website Compliance, without that access.')
                  : isBusiness
                  ? (language === 'es'
                    ? 'Primero ponga el nombre de la entidad legal. Luego responda una pregunta a la vez. Al terminar, las respuestas se vuelven los pasos de presentación, impuestos, licencias y reportes. Esto no presenta nada ante el estado.'
                    : 'First name the legal entity. Then answer one question at a time. When you finish, the answers become the filing, tax, license, and report steps. This does not file anything with the state.')
                  : isDod
                  ? (language === 'es'
                    ? 'Primero elija la cuenta de suscripción y luego el proyecto. Los papeles cotidianos quedan en la lista simple. Los papeles extra secretos abren herramientas extra. Esto no es una evaluación C3PAO. Con sesión, el progreso se guarda en el servidor.'
                    : 'First pick the subscription account, then the project. Everyday contract papers stay on the simple list. Extra-secret papers unlock extra tools. This is not a C3PAO assessment. When you are signed in, progress syncs to the server.')
                  : isWebsite
                    ? (language === 'es'
                      ? 'Primero elija el sitio. Luego haga la primera tarjeta o lance el escaneo de la homepage. El correo, Zero Trust y el gobierno extra se desbloquean en planes superiores. Con sesión, el progreso se guarda en el servidor.'
                      : 'First pick the website. Then do the first card or run the homepage scan. Extra email, Zero Trust, and governance controls unlock on higher plans. When you are signed in, progress syncs to the server.')
                    : (language === 'es'
                      ? 'Primero elija el campus o la oficina de investigación. Luego haga la primera tarjeta — casi siempre cribar a un académico o socio. HECVAT permanece en EDUCAUSE. Con sesión, el progreso se guarda en el servidor.'
                      : 'First pick the campus or research office. Then do the first card — usually screening a scholar or partner. HECVAT stays on EDUCAUSE. When you are signed in, progress syncs to the server.')}
              </p>
            </HelpBadge>
          </h2>
          <div className="compliance-console-header-actions">
            <button
              type="button"
              className={`compliance-calendar-launch${calendarOpen ? ' is-on' : ''}`}
              aria-pressed={calendarOpen}
              aria-label={language === 'es' ? 'Fechas y plazos' : 'Dates and deadlines'}
              onClick={() => setCalendarOpen((open) => !open)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect x="3" y="4" width="18" height="17" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
                <path d="M3 9h18M8 2.5v3M16 2.5v3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                <text x="12" y="18" textAnchor="middle" fontSize="8" fontWeight="700" fill="currentColor">
                  {(() => {
                    const now = new Date();
                    const key = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                    const next = [
                      ...publishedDeadlinesFor(definition.kind).flatMap((row) => [row.prepareBy, row.date]),
                      ...(state.calendarEvents || []).filter((event) => event.date && !event.skipped).map((event) => event.date),
                    ].filter((date) => date >= key).sort()[0];
                    return next ? String(Number(next.slice(-2))) : String(now.getDate());
                  })()}
                </text>
              </svg>
              <span>{language === 'es' ? 'Fechas' : 'Dates'}</span>
            </button>
            <DashboardWidgetEditor
              surface={widgetSurface}
              language={language}
              widgets={COMPLIANCE_DASHBOARD_WIDGETS}
              hidden={featureWidgetsHidden}
              order={featureLayout.order}
              onChange={commitFeatureLayout}
            />
            {hasNamedCompany ? (
              <button
                type="button"
                className={`compliance-calendar-launch${settingsOpen || companyIdsOpen ? ' is-on' : ''}`}
                aria-pressed={isWebsite ? settingsOpen : (isDod ? companyIdsOpen : settingsOpen)}
                aria-label={isWebsite
                  ? (language === 'es' ? 'Ajustes del sitio' : 'Website settings')
                  : isBusiness
                    ? (language === 'es' ? 'Ajustes de la empresa' : 'Company settings')
                    : (language === 'es' ? 'Ajustes del proyecto' : 'Project settings')}
                onClick={() => {
                  if (isDod) {
                    setCompanyIdsOpen((open) => !open);
                    return;
                  }
                  if (isBusiness) {
                    setRevisingFormation((open) => !open);
                    setSettingsOpen((open) => !open);
                    return;
                  }
                  setSettingsOpen((open) => !open);
                }}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                  <circle cx="12" cy="12" r="2.6" fill="none" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.03.03a2 2 0 1 1-2.83 2.83l-.03-.03a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.04 1.56V21a2 2 0 0 1-4 0v-.05a1.7 1.7 0 0 0-1.04-1.56 1.7 1.7 0 0 0-1.87.34l-.03.03a2 2 0 1 1-2.83-2.83l.03-.03A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.56-1.04H3a2 2 0 0 1 0-4h.04A1.7 1.7 0 0 0 4.6 8.92a1.7 1.7 0 0 0-.34-1.87l-.03-.03a2 2 0 1 1 2.83-2.83l.03.03a1.7 1.7 0 0 0 1.87.34H9a1.7 1.7 0 0 0 1-1.56V3a2 2 0 0 1 4 0v.04a1.7 1.7 0 0 0 1.04 1.56h.01a1.7 1.7 0 0 0 1.87-.34l.03-.03a2 2 0 1 1 2.83 2.83l-.03.03a1.7 1.7 0 0 0-.34 1.87v.01A1.7 1.7 0 0 0 20.96 10H21a2 2 0 0 1 0 4h-.04A1.7 1.7 0 0 0 19.4 15Z" fill="none" stroke="currentColor" strokeWidth="1.8" />
                </svg>
              </button>
            ) : null}
            {hasNamedCompany && showProjectOrCompanyStep ? (
              <div className="dod-workspace-tabs compliance-header-views" role="tablist" aria-label={scopeCopy.views}>
                <button
                  type="button"
                  role="tab"
                  id="dod-tab-today"
                  aria-controls="dod-view-today"
                  aria-selected={dodWorkspaceView === 'today'}
                  className={`dod-workspace-tab${dodWorkspaceView === 'today' ? ' is-active' : ''}`}
                  onClick={() => setDodWorkspaceView('today')}
                >
                  {isBusiness
                    ? (language === 'es' ? 'Inicio' : 'Home')
                    : (language === 'es' ? 'Próximos pasos' : 'Next steps')}
                </button>
                <button
                  type="button"
                  role="tab"
                  id="dod-tab-checklist"
                  aria-controls="dod-view-checklist"
                  aria-selected={dodWorkspaceView === 'checklist'}
                  aria-label={language === 'es'
                    ? `Lista ${totals.reviewed}/${totals.total}`
                    : `Checklist ${totals.reviewed}/${totals.total}`}
                  className={`dod-workspace-tab compliance-icon-btn${dodWorkspaceView === 'checklist' ? ' is-active' : ''}`}
                  onClick={() => setDodWorkspaceView('checklist')}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="4" y="3.5" width="16" height="17" rx="2" fill="none" stroke="currentColor" strokeWidth="1.7" />
                    <path d="M8 8.5h8M8 12h8M8 15.5h5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                  </svg>
                  <span>{totals.reviewed}/{totals.total}</span>
                </button>
              </div>
            ) : null}
            {taskFocus || (soloTask && focusDecisionId) ? null : (
            <DodConsoleGuide
              kind={definition.kind}
              language={language}
              hasCompany={Boolean(selectedCompany && selectedCompany.name.trim() && !orgDraft)}
              addingCompany={Boolean(orgDraft)}
              companyCount={taxonomy.companies.length}
              showDataTypeNavigator={false}
            />
            )}
            {writeLocked ? (
              <p className="compliance-console-readonly-banner" role="status">
                {language === 'es'
                  ? 'Solo lectura. Un administrador o colaborador puede editar proyectos y marcar pasos.'
                  : 'Read-only. An Admin or Contributor can edit projects and mark steps.'}
              </p>
            ) : null}
            {hasNamedCompany && catalog.notices?.length ? (
              <div className="compliance-notice-anchor" ref={noticeAnchorRef}>
                <button
                  type="button"
                  className="dashboard-btn compliance-icon-btn"
                  aria-expanded={noticesOpen}
                  aria-controls="compliance-program-notices"
                  aria-label={language === 'es'
                    ? `Avisos del programa${unreadNoticeCount ? `, ${unreadNoticeCount} sin leer` : ''}`
                    : `Program notices${unreadNoticeCount ? `, ${unreadNoticeCount} unread` : ''}`}
                  onClick={() => setNoticesOpen((open) => !open)}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M6 16V10a6 6 0 1 1 12 0v6l1.5 2h-15L6 16Z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                    <path d="M10 19a2 2 0 0 0 4 0" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                  </svg>
                  {unreadNoticeCount > 0 ? <span>{unreadNoticeCount}</span> : null}
                  <span className="compliance-calendar-tip">{language === 'es' ? 'Avisos' : 'Notices'}</span>
                </button>
                {noticesOpen ? (
                  <div id="compliance-program-notices" className="compliance-notices-panel dod-program-notices" role="dialog" aria-label={language === 'es' ? 'Avisos del programa' : 'Program notices'}>
                    <div className="compliance-notice-toolbar">
                      <strong>{language === 'es' ? 'Avisos' : 'Notices'}</strong>
                      <button type="button" onClick={() => {
                        const next = { ...noticeMarks };
                        catalog.notices.forEach((notice) => {
                          const key = noticeKey(notice.id);
                          if (next[key] !== 'cleared') next[key] = 'read';
                        });
                        saveNoticeMarks(next);
                      }}
                      >
                        {language === 'es' ? 'Marcar leídos' : 'Mark all read'}
                      </button>
                      <button type="button" onClick={() => {
                        const next = { ...noticeMarks };
                        catalog.notices.forEach((notice) => {
                          next[noticeKey(notice.id)] = 'cleared';
                        });
                        setOpenNoticeId(null);
                        saveNoticeMarks(next);
                      }}
                      >
                        {language === 'es' ? 'Quitar todos' : 'Clear all'}
                      </button>
                    </div>
                    {visibleNotices.length === 0 ? (
                      <p className="dod-notice-empty">
                        {language === 'es' ? 'No hay avisos pendientes.' : 'Nothing is waiting.'}
                      </p>
                    ) : (
                      <div className="dod-notice-rows">
                        {(() => {
                          const seenTerms = new Set<DodGlossaryTermId>();
                          return visibleNotices.map((notice) => {
                          const mark = noticeMarks[noticeKey(notice.id)];
                          const open = openNoticeId === notice.id;
                          return (
                            <div key={notice.id} className={`dod-notice-row${mark === 'read' || mark === 'cleared' ? ' is-read' : ' is-unread'}`}>
                              <div className="dod-notice-head">
                                {mark === 'read' || mark === 'cleared' ? null : <span className="dod-notice-dot" aria-hidden="true" />}
                                <strong className="dod-notice-title">
                                  <DodTermText text={notice.title} language={language} seen={seenTerms} />
                                </strong>
                                <span className="dod-notice-actions">
                                  <button
                                    type="button"
                                    aria-expanded={open}
                                    onClick={() => {
                                      setOpenNoticeId(open ? null : notice.id);
                                      setNoticeMoreId(null);
                                      if (!open && mark !== 'cleared') {
                                        saveNoticeMarks({ ...noticeMarks, [noticeKey(notice.id)]: 'read' });
                                      }
                                    }}
                                  >
                                    {open
                                      ? (language === 'es' ? 'Ocultar' : 'Hide')
                                      : (language === 'es' ? 'Leer' : 'Read')}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (mark === 'cleared') {
                                        saveNoticeMarks({ ...noticeMarks, [noticeKey(notice.id)]: 'read' });
                                        return;
                                      }
                                      if (openNoticeId === notice.id) setOpenNoticeId(null);
                                      saveNoticeMarks({ ...noticeMarks, [noticeKey(notice.id)]: 'cleared' });
                                    }}
                                  >
                                    {mark === 'cleared'
                                      ? (language === 'es' ? 'Restaurar' : 'Restore')
                                      : (language === 'es' ? 'Quitar' : 'Clear')}
                                  </button>
                                </span>
                              </div>
                              {open ? (
                                <>
                                  <p className={`dod-notice-body${noticeMoreId === notice.id ? '' : ' is-clamped'}`}>
                                    <DodTermText text={notice.body} language={language} seen={seenTerms} />
                                  </p>
                                  {notice.body.length > 180 ? (
                                    <button type="button" className="dod-notice-more" onClick={() => setNoticeMoreId(noticeMoreId === notice.id ? null : notice.id)}>
                                      {noticeMoreId === notice.id
                                        ? (language === 'es' ? 'Menos' : 'Less')
                                        : (language === 'es' ? 'Más' : 'More')}
                                    </button>
                                  ) : null}
                                  {notice.sourceUrl ? (
                                    <HelpBadge label={language === 'es' ? 'Fuente' : 'Source'}>
                                      <p>
                                        <a href={notice.sourceUrl} target="_blank" rel="noopener noreferrer">
                                          {notice.sourceLabel || notice.sourceUrl}
                                        </a>
                                      </p>
                                    </HelpBadge>
                                  ) : null}
                                </>
                              ) : null}
                            </div>
                          );
                          });
                        })()}
                      </div>
                    )}
                    {clearedNoticeCount > 0 ? (
                      <button type="button" className="dod-notice-more" onClick={() => setShowClearedNotices((current) => !current)}>
                        {showClearedNotices
                          ? (language === 'es' ? 'Ocultar los quitados' : 'Hide cleared')
                          : (language === 'es' ? `Ver los quitados (${clearedNoticeCount})` : `Show cleared (${clearedNoticeCount})`)}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
            {canSyncRemote && subscriptionAccountId ? (
              <button
                type="button"
                className={`dashboard-btn compliance-icon-btn${refreshBusy ? ' is-busy' : ''}`}
                disabled={refreshBusy}
                aria-busy={refreshBusy}
                aria-label={refreshBusy
                  ? (language === 'es' ? 'Actualizando' : 'Refreshing')
                  : (language === 'es' ? 'Actualizar' : 'Refresh')}
                onClick={() => {
                  setRefreshBusy(true);
                  setRemoteRefreshNonce((value) => value + 1);
                }}
              >
                {refreshBusy ? (
                  <span className="compliance-inline-spinner" aria-hidden="true" />
                ) : (
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M20 12a8 8 0 1 1-2.2-5.5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                    <path d="M20 4.5V9h-4.5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            ) : null}
          </div>
        </div>

        <>
            {calendarOpen ? (
              <div id="compliance-calendar-panel">
                <ComplianceCalendar
                  language={language}
                  events={state.calendarEvents || []}
                  suggestions={calendarSuggestionsFor(definition.kind)}
                  portfolio={portfolioRows}
                  listGroup={definition.title}
                  heading={language === 'es' ? 'Fechas de este trabajo' : 'Dates for this work'}
                  published={publishedDeadlinesFor(definition.kind).flatMap((row) => ([
                    {
                      id: `${row.id}-prepare`,
                      title: language === 'es' ? `Preparar: ${row.title.es}` : `Prepare: ${row.title.en}`,
                      date: row.prepareBy,
                      source: row.source[language],
                      kind: 'prepare' as const,
                    },
                    {
                      id: `${row.id}-file`,
                      title: row.title[language],
                      date: row.date,
                      source: row.source[language],
                      kind: 'file' as const,
                    },
                  ]))}
                  onSave={(event: ComplianceCalendarEvent) => {
                    const taskId = calendarTaskId(definition.kind);
                    const currentItem = state.items[taskId];
                    const nextEvents = [
                      ...(state.calendarEvents || []).filter((row) => row.id !== event.id),
                      event,
                    ];
                    void persist({
                      ...state,
                      updatedAt: new Date().toISOString(),
                      calendarEvents: nextEvents,
                      items: {
                        ...state.items,
                        [taskId]: {
                          status: currentItem?.status === 'reviewed' ? 'reviewed' : 'in_progress',
                          notes: currentItem?.notes || '',
                          stepProgress: {
                            ...(currentItem?.stepProgress || {}),
                            [event.id]: {
                              done: true,
                              evidenceNote: event.skipped ? 'Not this year' : event.date,
                            },
                          },
                        },
                      },
                    });
                  }}
                />
              </div>
            ) : null}
            <div className={`dod-scope-bar${!hasNamedCompany ? ' is-first-step' : ''}`}>
              <p className="compliance-scope-identity" aria-label={es ? 'Qué está abierto' : 'What is open'}>
                {(subscriptionAccounts.length > 0 || usesRemoteCatalog) ? (
                  <span>
                    <strong>{es ? 'Espacio' : 'Workspace'}:</strong>
                    {' '}
                    {selectedAccountName || (es ? 'ninguno elegido' : 'none chosen')}
                    {subscriptionAccounts.find((row) => row.id === subscriptionAccountId)?.plan
                      ? ` · ${subscriptionAccounts.find((row) => row.id === subscriptionAccountId)?.plan}`
                      : ''}
                  </span>
                ) : null}
                <span>
                  <strong>
                    {isDod ? (es ? 'Proyecto' : 'Project') : scopeCopy.which}
                    :
                  </strong>
                  {' '}
                  {mustPickAccount
                    ? (es ? 'cargando…' : 'loading…')
                    : ((isDod ? selectedProject?.name : selectedCompany?.name)?.trim()
                      || (es ? 'ninguno elegido' : 'none chosen'))}
                </span>
              </p>
              <div className="dod-scope-filters">
              {subscriptionAccounts.length > 0 || usesRemoteCatalog ? (
              <div className="sub-account-switcher dod-scope-field">
                <div className="dod-scope-picker-label">
                  <label htmlFor="subscription-account-select">
                    {language === 'es' ? 'Espacio de trabajo' : 'Workspace'}
                  </label>
                  <HelpBadge label={language === 'es' ? 'Espacio vs plan' : 'Workspace vs plan'}>
                    <p>
                      {language === 'es'
                        ? 'Su correo tiene un solo nivel de suscripción (el más alto que paga). El espacio de trabajo es el binder (GossLed, Canyon Foundry). Los administradores del proyecto controlan quién entra. NextCompli no es un espacio de cliente.'
                        : 'Your email has one subscription level (the highest you pay for). Workspace is the binder (GossLed, Canyon Foundry). Project admins control who gets in. NextCompli is not a customer workspace.'}
                    </p>
                  </HelpBadge>
                </div>
                <select
                  id="subscription-account-select"
                  className="dod-scope-select"
                  value={subscriptionAccountId ?? ''}
                  disabled={!accountsLoaded || subscriptionAccounts.length === 0}
                  onChange={(event) => {
                    const next = Number(event.target.value);
                    if (next) {
                      chooseSubscriptionAccount(next);
                    }
                  }}
                >
                  <option value="">
                    {!accountsLoaded
                      ? (language === 'es' ? 'Cargando cuentas…' : 'Loading accounts…')
                      : subscriptionAccounts.length === 0
                        ? (language === 'es' ? 'Ninguna cuenta en esta sesión' : 'No accounts on this login')
                        : (language === 'es' ? 'Seleccionar cuenta' : 'Select account')}
                  </option>
                  {subscriptionAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name}{account.plan ? ` · ${account.plan}` : ''}
                    </option>
                  ))}
                </select>
                {accountsLoaded && subscriptionAccounts.length === 0 ? (
                  <p className="compliance-console-company-hint" role="status">
                    {language === 'es'
                      ? 'Si ya aceptó la invitación, el espacio aparece cuando tiene una suscripción activa o pasos guardados. Su plan es el nivel más alto de este correo.'
                      : 'If you already accepted the invite, the workspace appears when it has an active subscription or saved steps. Your plan is the highest level on this email.'}
                  </p>
                ) : null}
              </div>
              ) : null}
              <div className="compliance-console-company dod-scope-picker dod-scope-field">
                <div className="dod-scope-picker-label">
                  <label htmlFor={isDod ? 'dod-project-select' : 'dod-company-select'}>
                    {isDod
                      ? (language === 'es' ? 'Proyecto' : 'Project')
                      : scopeCopy.which}
                  </label>
                  <HelpBadge label={isDod ? (language === 'es' ? 'Cómo elegir un proyecto' : 'How to pick a project') : scopeCopy.howPick}>
                    <p>
                      {isDod ? (
                        <>
                          {language === 'es'
                            ? 'Un proyecto es el trabajo: un contrato, un sistema o un programa. Muchos trabajos no son una empresa nueva. Los papeles cotidianos ('
                            : 'A project is the work: a contract, a system, or a program. Many jobs are not a new company. Everyday papers ('}
                          <DodTermText text="FCI" language={language} />
                          {language === 'es' ? ') y los extra secretos (' : ') and extra-secret papers ('}
                          <DodTermText text="CUI" language={language} />
                          ).
                        </>
                      ) : scopeCopy.pickHelp}
                    </p>
                  </HelpBadge>
                </div>
                {isDod ? (
                  <>
                    <div className="dod-scope-control">
                      <select
                        id="dod-project-select"
                        className="dod-scope-select"
                        value={mustPickAccount ? '' : (taxonomy.activeProjectId || '')}
                        disabled={mustPickAccount}
                        onChange={(event) => {
                          setProjectDraft(null);
                          selectDodProjectId(event.target.value);
                        }}
                      >
                        <option value="">
                          {mustPickAccount
                            ? (language === 'es' ? 'Primero elija una cuenta' : 'Choose an account first')
                            : (language === 'es' ? 'Seleccionar proyecto' : 'Select project')}
                        </option>
                        {accountProjects.map((project) => (
                          <option key={project.id} value={project.id}>
                            {project.name.trim() || (language === 'es' ? 'Nombre requerido' : 'Name required')}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="dashboard-btn compliance-add-account"
                        onClick={addDodProject}
                        disabled={writeLocked || mustPickAccount}
                      >
                        {language === 'es' ? 'Añadir proyecto' : 'Add project'}
                      </button>
                      <button
                        type="button"
                        className="dashboard-btn"
                        onClick={renameDodProject}
                        disabled={writeLocked || !selectedProject}
                      >
                        {language === 'es' ? 'Cambiar nombre' : 'Rename'}
                      </button>
                    </div>
                    {!mustPickAccount && !selectedProject && !projectDraft && accountProjects.length === 0 ? (
                      <p className="compliance-console-company-hint" role="status">
                        {language === 'es' ? 'Todavía no hay proyectos.' : 'No projects are saved yet.'}
                      </p>
                    ) : null}
                    {projectDraft ? (
                      <form className="dod-register-form" onSubmit={saveDodProject}>
                        <label htmlFor="dod-scope-name">
                          {language === 'es' ? 'Nombre del proyecto' : 'Project name'}
                          <input
                            id="dod-scope-name"
                            value={projectDraft.name}
                            autoFocus
                            required
                            placeholder={language === 'es' ? 'p. ej. GossLed LLC' : 'e.g. GossLed LLC'}
                            onChange={(event) => setProjectDraft({ ...projectDraft, name: event.target.value })}
                          />
                        </label>
                        <fieldset className="dod-project-papers">
                          <legend>
                            {language === 'es' ? 'Qué papeles usa este contrato?' : 'What papers does this contract use?'}
                          </legend>
                          <p className="dod-project-papers-help">
                            <DodTermText
                              language={language}
                              seen={new Set()}
                              text={
                                language === 'es'
                                  ? 'Elija una vez. FCI es el papelerío cotidiano. CUI es extra secreto no clasificado — NextCompli no aloja CUI.'
                                  : 'Pick once. FCI is everyday contract paperwork. CUI is extra-secret unclassified — NextCompli does not host CUI.'
                              }
                            />
                          </p>
                          <div className="dod-navigator" role="group" aria-label={language === 'es' ? 'Tipo de datos' : 'Data type'}>
                            {([
                              { id: 'fci' as const, en: 'Everyday papers (FCI)', es: 'Papeles cotidianos (FCI)' },
                              { id: 'cui' as const, en: 'Extra-secret unclassified (CUI)', es: 'Extra secreto no clasificado (CUI)' },
                              { id: 'none' as const, en: 'Not sure yet', es: 'Aún no sé' },
                            ]).map((choice) => (
                              <button
                                key={choice.id}
                                type="button"
                                className={`dod-navigator-btn ${projectDraft.dataHandling === choice.id ? 'is-selected' : ''}`}
                                onClick={() => setProjectDraft({ ...projectDraft, dataHandling: choice.id })}
                              >
                                {language === 'es' ? choice.es : choice.en}
                              </button>
                            ))}
                          </div>
                          {projectDraft.dataHandling === 'cui' ? (
                            <p className="dod-navigator-note">
                              <DodTermText
                                language={language}
                                seen={new Set()}
                                text={
                                  language === 'es'
                                    ? 'Las herramientas CUI aparecen solo cuando un aviso lo exige. No suba CUI a NextCompli.'
                                    : 'CUI tools appear only when a notice requires them. Do not upload CUI into NextCompli.'
                                }
                              />
                            </p>
                          ) : null}
                        </fieldset>
                        <div className="compliance-console-company-row">
                          <button type="submit" className="dashboard-btn dashboard-btn-primary">
                            {language === 'es' ? 'Guardar proyecto' : 'Save project'}
                          </button>
                          <button type="button" className="dashboard-security-inline-btn" onClick={() => setProjectDraft(null)}>
                            {language === 'es' ? 'Cancelar' : 'Cancel'}
                          </button>
                        </div>
                      </form>
                    ) : null}
                  </>
                ) : (
                <>
                <div className="dod-scope-control">
                  <select
                    id="dod-company-select"
                    className="dod-scope-select"
                    autoComplete="off"
                    disabled={mustPickAccount}
                    value={mustPickAccount ? '' : (isWebsite && selectedCompany && !websiteScanUrlFromName(selectedCompany.name) ? '' : (taxonomy.activeCompanyId || ''))}
                    onChange={(event) => {
                      setOrgDraft(null);
                      selectDodCompanyId(event.target.value);
                    }}
                  >
                    <option value="">
                      {mustPickAccount
                        ? (language === 'es' ? 'Primero elija una cuenta' : 'Choose an account first')
                        : scopeCopy.choose}
                    </option>
                    {taxonomy.companies.filter((company) => !isWebsite || websiteScanUrlFromName(company.name)).map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name.trim() || (language === 'es' ? 'Nombre requerido' : 'Name required')}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="dashboard-btn compliance-add-account"
                    onClick={addDodCompany}
                    disabled={writeLocked || mustPickAccount}
                  >
                    {scopeCopy.add}
                  </button>
                </div>
                {!mustPickAccount && !orgDraft && taxonomy.companies.filter((company) => !isWebsite || websiteScanUrlFromName(company.name)).length === 0 ? (
                  <p className="compliance-console-company-hint" role="status">
                    {scopeCopy.empty}
                  </p>
                ) : null}
                {orgDraft ? (
                  <form className="dod-register-form" onSubmit={saveDodCompany}>
                    <div className="dod-scope-picker-label">
                      <strong>{scopeCopy.new}</strong>
                      <HelpBadge label={scopeCopy.whenAdd}>
                        <p>
                          {scopeCopy.addHelp}
                        </p>
                      </HelpBadge>
                    </div>
                    <label htmlFor={scopeCopy.nameInputId}>
                      {scopeCopy.nameLabel}
                      <input
                        id={scopeCopy.nameInputId}
                        value={orgDraft.name}
                        autoComplete={isWebsite ? 'url' : 'organization'}
                        autoFocus
                        required
                        placeholder={scopeCopy.namePlaceholder}
                        onChange={(event) => setOrgDraft({ ...orgDraft, name: event.target.value })}
                      />
                    </label>
                    {isDod ? (
                    <label htmlFor="dod-org-ein">
                      {language === 'es' ? 'Número de impuestos (EIN)' : 'Tax ID (EIN)'}
                      <input
                        id="dod-org-ein"
                        value={orgDraft.ein}
                        inputMode="numeric"
                        autoComplete="off"
                        placeholder="XX-XXXXXXX"
                        onChange={(event) => setOrgDraft({ ...orgDraft, ein: event.target.value })}
                      />
                    </label>
                    ) : null}
                    <div className="compliance-console-company-row">
                      <button type="submit" className="dashboard-btn dashboard-btn-primary">
                        {scopeCopy.save}
                      </button>
                      <button type="button" className="dashboard-security-inline-btn" onClick={cancelDodCompany}>
                        {language === 'es' ? 'Cancelar' : 'Cancel'}
                      </button>
                    </div>
                  </form>
                ) : null}
                </>
                )}
              </div>
              </div>
              {mustPickAccount ? (
                <p className="compliance-console-company-hint" role="status">
                  {language === 'es'
                    ? 'Elija una cuenta. Luego elija el proyecto. La lista permanece oculta hasta entonces.'
                    : 'Pick an account. Then pick the project. The checklist stays hidden until then.'}
                </p>
              ) : null}
              {(isDod || isCloud || isOfac || isWebsite || isBusiness) && gatewayBaseUrl && accessToken && hasNamedCompany && !mustPickAccount ? (
                <div className={`agent-console-panel${isWebsite ? ' is-website' : ''}`}>
                  <AgentRunBadges
                    feature={
                      isDod
                        ? 'dod-contracts-compliance'
                        : isCloud
                          ? 'cloud-compliance'
                          : isOfac
                            ? 'ofac-compliance'
                            : isBusiness
                              ? 'business-creation-compliance'
                              : 'website-compliance'
                    }
                    language={language}
                    gatewayBaseUrl={String(gatewayBaseUrl)}
                    accessToken={String(accessToken)}
                    companyId={subscriptionAccountId}
                    readOnly={writeLocked}
                    scopeLabel={
                      isDod
                        ? (selectedProject?.name || (language === 'es' ? 'Sin proyecto' : 'No project'))
                        : isWebsite
                          ? (selectedCompany?.name || (language === 'es' ? 'Sin sitio' : 'No website'))
                          : (selectedCompany?.name || (language === 'es' ? 'Sin cuenta' : 'No account'))
                    }
                    sourcesOpenSignal={sourcesOpenSignal}
                    onOpenTask={(id) => {
                      if (isBusiness && formationComplete) {
                        openFormationHomeTask(id);
                        return;
                      }
                      const match = orderedBoard.find((row) => row.item.id === id);
                      if (match) {
                        setOpenBoardSectionId(match.pillarId);
                      }
                      setFocusDecisionId(id);
                      setSoloTask(true);
                      setLaneFilter(['ready', 'blocked', 'done']);
                    }}
                    onRequestInstall={isWebsite
                      ? () => setAgentInstallOpenSignal((n) => n + 1)
                      : undefined}
                    onUpdateAvailableChange={isWebsite ? setAgentUpdateAvailable : undefined}
                  />
                  {isWebsite ? (
                    <WebsiteAgentInstallCard
                      embedded
                      language={language}
                      gatewayBaseUrl={String(gatewayBaseUrl)}
                      accessToken={String(accessToken)}
                      siteLabel={selectedCompany?.name || 'site-1'}
                      planAllowsInstall={gatedPlan !== 'basic'}
                      readOnly={writeLocked}
                      installOpenSignal={agentInstallOpenSignal}
                      updateAvailable={agentUpdateAvailable}
                      lookupBusy={scanBusy}
                      lookupMessage={
                        (() => {
                          const site = websiteScanUrlFromName(String(selectedCompany?.name || '').trim());
                          if (site && scanMessage && /enter the site url|escriba la url del sitio/i.test(scanMessage)) {
                            return null;
                          }
                          return scanMessage;
                        })()
                      }
                      onLookupHosting={() => {
                        // Prefer the Website picker (jfgoss.com), not the blank scanUrl default.
                        const fromSite = websiteScanUrlFromName(String(selectedCompany?.name || '').trim());
                        const fromLast = lastScan && typeof lastScan === 'object'
                          ? websiteScanUrlFromName(String(lastScan.final_url || '').trim())
                          : null;
                        const scanRaw = String(scanUrl || '').trim();
                        const fromField = (scanRaw && scanRaw !== 'https://' && scanRaw !== 'http://')
                          ? websiteScanUrlFromName(scanRaw)
                          : null;
                        const target = fromSite || fromLast || fromField;
                        if (!target) {
                          setScanMessage(language === 'es'
                            ? 'Elija un sitio arriba (ej. jfgoss.com), luego busque hosting.'
                            : 'Pick a website above (e.g. jfgoss.com), then look up hosting.');
                          return;
                        }
                        setScanMessage(null);
                        if (fromSite && target !== scanUrl) {
                          setScanUrl(target);
                        }
                        void runWebsiteScan(target, true, { purpose: 'hosting' });
                      }}
                      lookupTargetLabel={
                        websiteScanUrlFromName(String(selectedCompany?.name || '').trim())
                        || String(selectedCompany?.name || '').trim()
                        || ''
                      }
                      hostingHint={
                        lastScan && typeof lastScan === 'object' && lastScan.hosting
                          && typeof lastScan.hosting === 'object'
                          ? lastScan.hosting as {
                            providers?: string[];
                            cms?: string[];
                            languages?: string[];
                            cdns?: string[];
                            hosting_class?: string;
                            evidence?: string[];
                            managed_edge?: boolean;
                            confidence?: number;
                            note?: string;
                            install_advice?: {
                              primary_venue?: string;
                              also_consider?: string[];
                              avoid?: string[];
                              reason_en?: string;
                              reason_es?: string;
                            };
                          }
                          : null
                      }
                    />
                  ) : null}
                </div>
              ) : null}
            </div>

            {taskFocus || soloTask ? null : (
            <>
            {!hasNamedCompany ? (
            <p className="compliance-dashboard-empty" role="status">
              {es
                ? `Elija ${scopeCopy.which.toLowerCase()} arriba. La lista, el agente y los borradores permanecen ocultos hasta entonces.`
                : `Pick a ${scopeCopy.which.toLowerCase()} above. The checklist, agent, and drafts stay hidden until then.`}
            </p>
            ) : (
            <section className="dashboard-glance compliance-dashboard-glance" aria-label={es ? 'De un vistazo' : 'At a glance'}>
              <details
                className="compliance-glance-charts dashboard-glance-wide"
                style={{ order: 10 }}
                open={statusChartsOpen}
                onToggle={(event) => setStatusChartsOpen(event.currentTarget.open)}
              >
                <summary>
                  <span>{es ? 'Gráficos de estado' : 'Status charts'}</span>
                  <span className="compliance-fold-hint">
                    {es
                      ? `${glanceShare}% terminado · ${glanceTotal} pasos. Cerrado al inicio.`
                      : `${glanceShare}% finished · ${glanceTotal} steps. Collapsed by default.`}
                  </span>
                </summary>
                <div className="compliance-glance-charts-grid">
                  <div className="compliance-glance-charts-primary">
              {featureWidgetsHidden.has('finished') ? null : (
                <section
                  className="dashboard-glance-card"
                  style={{ order: featureLayout.order.indexOf('finished') }}
                  aria-label={es ? 'Parte terminada' : 'Finished share'}
                >
                  <h3>{es ? 'Parte terminada' : 'Finished share'}</h3>
                  <GlanceDonut
                    percent={glanceShare}
                    ariaLabel={es ? `${glanceShare} por ciento terminado` : `${glanceShare} percent finished`}
                    caption={es
                      ? (hasNamedCompany
                        ? `${glanceTally.reviewed} de ${Math.max(glanceTotal, 1)} pasos guardados están terminados. No es un puntaje de auditoría.`
                        : '0 de 0 pasos guardados. Elija un proyecto para ver el progreso.')
                      : (hasNamedCompany
                        ? `${glanceTally.reviewed} of ${Math.max(glanceTotal, 1)} saved steps are finished. This is not an audit score.`
                        : '0 of 0 saved steps. Pick a project to see progress.')}
                    slices={[
                      { key: 'done', value: glanceTally.reviewed, label: es ? 'Terminado' : 'Finished', color: '#3dd68c' },
                      { key: 'progress', value: glanceTally.in_progress, label: es ? 'En progreso' : 'In progress', color: '#4c8dff' },
                      { key: 'not-started', value: glanceTally.not_started, label: es ? 'Sin iniciar' : 'Not started', color: '#8b93a7' },
                      { key: 'gap', value: glanceTally.gap, label: es ? 'Brecha' : 'Gap', color: '#e24b4b' },
                    ]}
                  />
                </section>
              )}
              {featureWidgetsHidden.has('status') ? null : (
                <section
                  className="dashboard-glance-card"
                  style={{ order: featureLayout.order.indexOf('status') }}
                  aria-label={es ? 'Pasos guardados' : 'Saved steps'}
                >
                  <h3>{es ? `Pasos guardados · ${glanceTotal}` : `Saved steps · ${glanceTotal}`}</h3>
                  <GlanceBars
                    selectedLens={glanceLens}
                    onPick={hasNamedCompany ? pickGlance : undefined}
                    slices={[
                      { key: 'done', lensKey: 'status:reviewed', value: glanceTally.reviewed, label: es ? 'Terminado' : 'Finished', color: '#3dd68c' },
                      { key: 'progress', lensKey: 'status:in_progress', value: glanceTally.in_progress, label: es ? 'En progreso' : 'In progress', color: '#4c8dff' },
                      { key: 'not-started', lensKey: 'status:not_started', value: glanceTally.not_started, label: es ? 'Sin iniciar' : 'Not started', color: '#8b93a7' },
                      { key: 'gap', lensKey: 'status:gap', value: glanceTally.gap, label: es ? 'Brecha' : 'Gap', color: '#e24b4b' },
                      { key: 'blocking', lensKey: 'priority:blocking', value: glanceBlocking, label: es ? 'Bloqueante' : 'Blocking', color: '#f97066' },
                      { key: 'recommended', lensKey: 'priority:recommended', value: glanceRecommended, label: es ? 'Recomendado' : 'Recommended', color: '#c9a227' },
                    ]}
                  />
                </section>
              )}
              {featureWidgetsHidden.has('where') ? null : (
                <section
                  className="dashboard-glance-card"
                  style={{ order: featureLayout.order.indexOf('where') }}
                  aria-label={es ? 'Dónde está el trabajo' : 'Where the work sits'}
                >
                  <h3>{es ? 'Dónde está el trabajo' : 'Where the work sits'}</h3>
                  <GlanceFlow
                    steps={[
                      { key: 'not-started', value: glanceTally.not_started, label: es ? 'Sin iniciar' : 'Not started', color: '#8b93a7' },
                      { key: 'progress', value: glanceTally.in_progress, label: es ? 'En progreso' : 'In progress', color: '#4c8dff' },
                      { key: 'gap', value: glanceTally.gap, label: es ? 'Brecha' : 'Gap', color: '#e24b4b' },
                      { key: 'done', value: glanceTally.reviewed, label: es ? 'Terminado' : 'Finished', color: '#3dd68c' },
                    ]}
                  />
                </section>
              )}
                  </div>
              {(featureWidgetsHidden.has('priority') && featureWidgetsHidden.has('dates') && featureWidgetsHidden.has('groups')) ? null : (
                  <div className="compliance-glance-more-grid">
              {featureWidgetsHidden.has('priority') ? null : (
                <section
                  className="dashboard-glance-card dashboard-glance-pair"
                  style={{ order: featureLayout.order.indexOf('priority') }}
                  aria-label={es ? 'Mezcla de prioridad' : 'Priority mix'}
                >
                  <h3>{es ? 'Mezcla de prioridad' : 'Priority mix'}</h3>
                  <GlanceColumns
                    selectedLens={glanceLens}
                    onPick={hasNamedCompany ? pickGlance : undefined}
                    slices={[
                      { key: 'blocking', lensKey: 'priority:blocking', value: glanceBlocking, label: es ? 'Bloqueante' : 'Blocking', color: '#f97066' },
                      { key: 'recommended', lensKey: 'priority:recommended', value: glanceRecommended, label: es ? 'Recomendado' : 'Recommended', color: '#c9a227' },
                      {
                        key: 'other',
                        lensKey: 'priority:other',
                        value: Math.max(0, glanceTotal - glanceTally.reviewed - glanceBlocking - glanceRecommended),
                        label: es ? 'Otro trabajo abierto' : 'Other open work',
                        color: '#8b93a7',
                      },
                    ]}
                  />
                </section>
              )}
              {featureWidgetsHidden.has('dates') ? null : (
                <section
                  className="dashboard-glance-card dashboard-glance-pair"
                  style={{ order: featureLayout.order.indexOf('dates') }}
                  aria-label={es ? 'Fechas próximas' : 'Upcoming dates'}
                >
                  <h3>{es ? 'Fechas próximas' : 'Upcoming dates'}</h3>
                  <GlanceTimeline
                    emptyLabel={es ? 'Ningún paso abierto tiene fecha.' : 'No open step has a date.'}
                    onOpen={(id) => {
                      if (!hasNamedCompany) return;
                      const match = orderedBoard.find((row) => row.item.id === id);
                      if (!match) return;
                      setOpenBoardSectionId(match.pillarId);
                      setFocusDecisionId(id);
                      setSoloTask(true);
                      setLaneFilter(['ready', 'blocked', 'done']);
                    }}
                    rows={liveGlanceRows
                      .map((row) => ({ row, date: dueDateFor(row.id) }))
                      .filter((entry) => entry.date && entry.row.status !== 'reviewed')
                      .sort((left, right) => left.date.localeCompare(right.date) || left.row.title.localeCompare(right.row.title))
                      .slice(0, 6)
                      .map((entry) => ({
                        key: entry.row.id,
                        date: entry.date,
                        title: entry.row.title,
                        place: entry.row.pillar,
                      }))}
                  />
                </section>
              )}
              {featureWidgetsHidden.has('groups') ? null : (
                <section
                  className="dashboard-glance-card dashboard-glance-wide"
                  style={{ order: featureLayout.order.indexOf('groups') }}
                  aria-label={es ? 'Por grupo' : 'By group'}
                >
                  <h3>{es ? 'Por grupo' : 'By group'}</h3>
                  {glancePillars.length ? (
                    <ul className="dashboard-glance-stacks dashboard-glance-stacks--readable">
                      {glancePillars.map(([label, rows]) => {
                        const parts = [
                          { key: 'gap', value: rows.filter((row) => row.status === 'gap').length, color: '#e24b4b' },
                          { key: 'wait', value: rows.filter((row) => row.status === 'not_started').length, color: '#c9a227' },
                          { key: 'work', value: rows.filter((row) => row.status === 'in_progress').length, color: '#4c8dff' },
                          { key: 'done', value: rows.filter((row) => row.status === 'reviewed').length, color: '#3dd68c' },
                        ];
                        return (
                          <li key={label}>
                            <span className="dashboard-glance-stack-copy"><strong>{label}</strong></span>
                            <span className="dashboard-glance-stack">
                              {parts.filter((part) => part.value > 0).map((part) => (
                                <i key={part.key} style={{ width: `${Math.round((part.value / Math.max(1, rows.length)) * 100)}%`, background: part.color }} />
                              ))}
                            </span>
                            <strong className="dashboard-glance-stack-total">{rows.length}</strong>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="compliance-kpi-empty">
                      {es
                        ? 'Los grupos aparecen cuando el proyecto tiene pasos.'
                        : 'Groups appear when the project has steps.'}
                    </p>
                  )}
                </section>
              )}
                  </div>
              )}
                </div>
              </details>
            </section>
            )}
            {hasNamedCompany && glanceLens ? (
              <p className="dashboard-home-lens">
                <span>{es ? 'Mostrando solo lo que eligió arriba.' : 'Showing only what you picked above.'}</span>
                <button type="button" className="dashboard-btn dashboard-btn-secondary" onClick={() => pickGlance(glanceLens)}>
                  {es ? 'Ver todo' : 'Show everything'}
                </button>
              </p>
            ) : null}
            </>
            )}

            {isBusiness && hasNamedCompany && !formationComplete && catalog.pillars.every((pillar) => pillar.id === 'formation-calendar' || pillar.items.length === 0) ? (
              <div className="compliance-scope-empty" role="status">
                <strong>
                  {language === 'es'
                    ? 'Todavía no hay pasos de formación'
                    : 'No formation steps yet'}
                </strong>
                <p>
                  {language === 'es'
                    ? 'Expanda Preguntas de formación arriba y responda una a la vez. Cuando termine, NextCompli arma la lista de presentación, impuestos y licencias a partir de esas respuestas. El 0% no significa que se perdió trabajo — aún no se generó la lista.'
                    : 'Expand Formation questions above and answer one at a time. When you finish, NextCompli builds the filing, tax, and license checklist from those answers. 0% does not mean work was lost — the checklist has not been generated yet.'}
                </p>
              </div>
            ) : null}
            {isWills && hasNamedCompany && !willsComplete && catalog.pillars.every((pillar) => pillar.id === 'wills-calendar' || pillar.items.length === 0) ? (
              <div className="compliance-scope-empty" role="status">
                <strong>
                  {language === 'es'
                    ? 'Todavía no hay pasos del plan'
                    : 'No plan steps yet'}
                </strong>
                <p>
                  {language === 'es'
                    ? 'Expanda Preguntas del plan arriba y responda una a la vez. Cuando termine, NextCompli arma la lista a partir de esas respuestas. Un abogado con licencia en ese estado redacta los papeles. NextCompli no.'
                    : 'Expand Plan questions above and answer one at a time. When you finish, NextCompli builds the checklist from those answers. A lawyer licensed in that state writes the papers. NextCompli does not.'}
                </p>
              </div>
            ) : null}
            {isWills && hasNamedCompany ? (
              <details className={`business-creation-intake${willsComplete ? ' is-complete' : ''}`} open={revisingWills || undefined}>
                <summary>
                  {language === 'es' ? 'Preguntas del plan' : 'Plan questions'}
                  <span>
                    {willsComplete
                      ? (language === 'es' ? 'Completas — pulse para revisar' : 'Complete — expand to review')
                      : catalog.pillars.some((pillar) => pillar.id !== 'wills-calendar' && pillar.items.length > 0)
                        ? (language === 'es' ? 'Falta una pregunta' : 'One question is still open')
                        : (language === 'es' ? 'Necesarias para armar la lista' : 'Needed to build the checklist')}
                  </span>
                </summary>
                {showWillsIntake ? (
                  <WillsTrustsInterview
                    answers={willsAnswers}
                    language={language}
                    onAnswer={answerWills}
                    onBack={() => commitWills(willsBackFrom(willsAnswers))}
                    onStartOver={() => {
                      setRevisingWills(false);
                      void persist({
                        ...state,
                        updatedAt: new Date().toISOString(),
                        willsTrusts: {},
                      });
                    }}
                  />
                ) : (
                  <WillsTrustsSummary
                    answers={willsAnswers}
                    language={language}
                    onRevise={() => {
                      setRevisingWills(true);
                      commitWills(willsBackFrom(willsAnswers));
                    }}
                  />
                )}
              </details>
            ) : null}
            {isBusiness && hasNamedCompany && showFormationIntake ? (
              <section
                className="business-creation-stage"
                aria-label={language === 'es' ? 'Formar mi empresa' : 'Form my company'}
              >
                {!formationComplete ? (
                  <p className="business-creation-stage-lede">
                    {catalog.pillars.some((pillar) => pillar.id !== 'formation-calendar' && pillar.items.length > 0)
                      ? (language === 'es' ? 'Falta una pregunta' : 'One question is still open')
                      : (language === 'es'
                        ? 'Una pregunta a la vez. Al terminar, NextCompli arma la lista de presentación, impuestos y licencias.'
                        : 'One question at a time. When you finish, NextCompli builds the filing, tax, and license checklist.')}
                  </p>
                ) : null}
                <BusinessCreationInterview
                  answers={formationAnswers}
                  language={language}
                  onAnswer={answerFormation}
                  onBack={() => commitFormation(formationBackFrom(formationAnswers))}
                  onStartOver={() => {
                    setRevisingFormation(false);
                    const name = selectedCompany?.name.trim() || '';
                    void persist({
                      ...state,
                      updatedAt: new Date().toISOString(),
                      businessCreation: name ? { legalName: name } : {},
                    });
                  }}
                />
              </section>
            ) : null}
            {isBusiness && hasNamedCompany && formationComplete && !revisingFormation ? (
              <details className="business-creation-intake is-complete">
                <summary>
                  {language === 'es' ? 'Preguntas de formación' : 'Formation questions'}
                  <span>{language === 'es' ? 'Completas — pulse para revisar' : 'Complete — expand to review'}</span>
                </summary>
                <BusinessCreationSummary
                  answers={formationAnswers}
                  language={language}
                  onRevise={() => {
                    setRevisingFormation(true);
                    commitFormation(formationBackFrom(formationAnswers));
                  }}
                />
              </details>
            ) : null}
            <>
            {hasNamedCompany ? (
              <>
              <div className="dod-workspace">
                <div
                  role="tabpanel"
                  id="dod-view-today"
                  aria-labelledby="dod-tab-today"
                  hidden={dodWorkspaceView !== 'today'}
                  className="dod-workspace-panel"
                >
                  <ComplianceKpiDashboard
                    done={progressCounts.done}
                    blocked={progressCounts.blocked}
                    open={progressCounts.open}
                    gaps={totals.gaps}
                    levelLabel={isDod && state.levelAssessment ? CMMC_LEVEL_LABELS[contractLevel][language] : undefined}
                    language={language}
                    hiddenWidgets={featureWidgetsHidden}
                    selectedLane={laneFilter.length === 1 ? laneFilter[0] : undefined}
                    onBulkDoTasks={writeLocked ? undefined : runBulkDoTasks}
                    bulkDoBusy={bulkDoBusy}
                    tasks={orderedBoard.map((row) => {
                      const itemState = state.items[row.item.id];
                      const status = (itemState?.status || 'not_started') as ComplianceItemStatus;
                      const waiting = checklistItemWaiting(catalog, state, row.item);
                      return {
                        id: row.item.id,
                        title: row.item.title,
                        action: row.item.action || '',
                        section: row.pillarLabel,
                        priority: row.item.priority,
                        lane: taskLane(status, waiting),
                        status,
                        reviewedAt: itemState?.reviewedAt,
                        dueDate: publishedDeadlinesFor(definition.kind).find((deadline) => deadline.itemId === row.item.id)?.date || '',
                        openDeps: checklistDependencyStates(catalog, state, row.item).filter((dep) => !dep.done).length,
                        unlocks: catalog.pillars.reduce((count, pillar) => (
                          count + pillar.items.filter((item) => (item.dependsOn || []).includes(row.item.id)).length
                        ), 0),
                        waitsOutside: row.item.completeRequires === 'uei-cage' || isDodWaitingOnExternal(row.item, state),
                      };
                    })}
                    events={[
                      ...(state.calendarEvents || [])
                        .filter((event) => event.date && !event.skipped)
                        .map((event) => ({ id: event.id, title: event.title, date: event.date })),
                      ...publishedDeadlinesFor(definition.kind).flatMap((row) => ([
                        {
                          id: `${row.id}-prepare`,
                          title: language === 'es' ? `Preparar: ${row.title.es}` : `Prepare: ${row.title.en}`,
                          date: row.prepareBy,
                        },
                        {
                          id: `${row.id}-file`,
                          title: row.title[language],
                          date: row.date,
                        },
                      ])),
                    ]}
                    sections={taskSections.map((section) => ({
                      id: section.id,
                      label: section.label,
                      total: section.total,
                    }))}
                    onSelectLane={(lane) => {
                      setLaneFilter([lane]);
                      const match = orderedBoard.find((row) => {
                        const status = (state.items[row.item.id]?.status || 'not_started') as ComplianceItemStatus;
                        const waiting = checklistItemWaiting(catalog, state, row.item);
                        return taskLane(status, waiting) === lane;
                      });
                      if (match) {
                        setOpenBoardSectionId(match.pillarId);
                      }
                    }}
                    onOpenTask={(id) => {
                      if (isBusiness && formationComplete) {
                        openFormationHomeTask(id);
                        return;
                      }
                      const match = orderedBoard.find((row) => row.item.id === id);
                      if (match) {
                        setOpenBoardSectionId(match.pillarId);
                      }
                      setFocusDecisionId(id);
                      setSoloTask(true);
                      setLaneFilter(['ready', 'blocked', 'done']);
                    }}
                  />
                  {bulkDoNote ? (
                    <p className="compliance-bulk-do-note" role="status">{bulkDoNote}</p>
                  ) : null}
                  <div className="dod-company-head">
                    {isDod && !taskFocus ? (
                    <section className="dod-key-data" aria-label={language === 'es' ? 'Datos clave' : 'Key data'}>
                      <div className="dod-key-data-head">
                      <h3>
                        {language === 'es' ? 'Datos clave' : 'Key data'}
                        <HelpBadge label={language === 'es' ? 'Qué va aquí' : 'What belongs here'}>
                          <p>
                            {language === 'es'
                              ? 'Estos son los datos clave de la empresa, no una puntuación. EIN es el número de impuestos. UEI y CAGE salen de SAM.gov. La declaración de capacidades es la página de una hoja que describe el trabajo. Pulse un dato cuando haya detalle que abrir. No invente los identificadores.'
                              : 'These are the company key facts, not a score. EIN is the tax number. UEI and CAGE come from SAM.gov. The capability statement is the one-page leave-behind that describes the work. Click a fact when there is detail to open. Do not invent the identifiers.'}
                          </p>
                        </HelpBadge>
                      </h3>
                      <button
                        type="button"
                        className={`compliance-ids-settings${companyIdsOpen ? ' is-on' : ''}`}
                        aria-expanded={companyIdsOpen}
                        aria-label={language === 'es' ? 'Editar identificadores' : 'Edit company IDs'}
                        onClick={() => setCompanyIdsOpen((open) => !open)}
                      >
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <circle cx="12" cy="12" r="2.6" />
                          <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.03.03a2 2 0 0 1-2.83 2.83l-.03-.03a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.04 1.56V21a2 2 0 0 1-4 0v-.05a1.7 1.7 0 0 0-1.04-1.56 1.7 1.7 0 0 0-1.87.34l-.03.03a2 2 0 1 1-2.83-2.83l.03-.03A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.56-1.04H3a2 2 0 0 1 0-4h.04A1.7 1.7 0 0 0 4.6 8.92a1.7 1.7 0 0 0-.34-1.87l-.03-.03a2 2 0 1 1 2.83-2.83l.03.03a1.7 1.7 0 0 0 1.87.34H9a1.7 1.7 0 0 0 1-1.56V3a2 2 0 0 1 4 0v.04a1.7 1.7 0 0 0 1.04 1.56h.01a1.7 1.7 0 0 0 1.87-.34l.03-.03a2 2 0 1 1 2.83 2.83l-.03.03a1.7 1.7 0 0 0-.34 1.87v.01A1.7 1.7 0 0 0 20.96 10H21a2 2 0 0 1 0 4h-.04A1.7 1.7 0 0 0 19.4 15Z" />
                        </svg>
                      </button>
                      </div>
                      <div className="dod-company-identity" aria-label={language === 'es' ? 'Identificadores de la empresa' : 'Company IDs'}>
                        {llcHasBacking ? (
                          <button
                            type="button"
                            className={`dod-id-chip dod-id-chip-action${llcReady ? ' dod-id-ready' : ''}`}
                            onClick={openKeyCompanyIds}
                            aria-label={language === 'es' ? `Abrir ${selectedCompany.name}` : `Open ${selectedCompany.name}`}
                          >
                            {selectedCompany.name}
                          </button>
                        ) : (
                          <strong className={llcReady ? 'dod-id-chip dod-id-ready' : undefined}>{selectedCompany.name}</strong>
                        )}
                        <span className={einReady ? 'dod-id-chip dod-id-ready' : 'dod-id-chip dod-id-pending'}>
                          {einHasBacking ? (
                            <button
                              type="button"
                              className="dod-id-chip-action"
                              onClick={openKeyCompanyIds}
                              aria-label={language === 'es' ? 'Abrir el EIN' : 'Open EIN'}
                            >
                              EIN {einValue || '—'}
                            </button>
                          ) : (
                            <span>EIN {einValue || '—'}</span>
                          )}
                          <HelpBadge label={language === 'es' ? 'Qué es el EIN' : 'What EIN means'} wide>
                            <p>
                              {language === 'es'
                                ? 'EIN es el número de impuestos que el IRS le dio a la empresa. No lo invente. Está en la carta CP575.'
                                : 'EIN is the tax number the IRS gave the company. Do not invent it. It is on the CP575 letter.'}
                            </p>
                          </HelpBadge>
                        </span>
                        <span className={ueiValue ? 'dod-id-chip dod-id-ready' : 'dod-id-chip dod-id-pending'}>
                          {ueiHasBacking ? (
                            <button
                              type="button"
                              className="dod-id-chip-action"
                              onClick={() => (ueiValue ? openKeyCompanyIds() : openKeyTask('sam-uei-cage'))}
                              aria-label={language === 'es' ? 'Abrir el UEI' : 'Open UEI'}
                            >
                              UEI {ueiValue || (language === 'es' ? 'SAM pendiente' : 'Pending SAM')}
                            </button>
                          ) : (
                            <span>UEI {ueiValue || (language === 'es' ? 'SAM pendiente' : 'Pending SAM')}</span>
                          )}
                          <HelpBadge label={language === 'es' ? 'Qué es el UEI' : 'What UEI means'} wide>
                            <p>
                              {language === 'es'
                                ? 'UEI es el identificador de 12 caracteres que SAM.gov asigna al registrar la empresa. No compre un DUNS. No invente este número.'
                                : 'UEI is the 12-character ID SAM.gov assigns when you register the company. Do not buy a DUNS. Do not invent this number.'}
                            </p>
                            {samMonitorLabel ? <p>{samMonitorLabel}</p> : null}
                          </HelpBadge>
                        </span>
                        <span className={cageValue ? 'dod-id-chip dod-id-ready' : 'dod-id-chip dod-id-pending'}>
                          {cageHasBacking ? (
                            <button
                              type="button"
                              className="dod-id-chip-action"
                              onClick={() => (cageValue ? openKeyCompanyIds() : openKeyTask('sam-uei-cage'))}
                              aria-label={language === 'es' ? 'Abrir el CAGE' : 'Open CAGE'}
                            >
                              CAGE {cageValue || '—'}
                            </button>
                          ) : (
                            <span>CAGE {cageValue || '—'}</span>
                          )}
                          <HelpBadge label={language === 'es' ? 'Qué es el CAGE' : 'What CAGE means'} wide>
                            <p>
                              {language === 'es'
                                ? 'CAGE es un identificador extra de Defensa. SAM lo emite después del registro. Déjelo vacío hasta que SAM se lo dé.'
                                : 'CAGE is an extra Defense ID. SAM issues it after registration. Leave it blank until SAM gives it to you.'}
                            </p>
                          </HelpBadge>
                        </span>
                        <span className={capabilityReady ? 'dod-id-chip dod-id-ready' : 'dod-id-chip dod-id-pending'}>
                          {capabilityHasBacking ? (
                            <button
                              type="button"
                              className="dod-id-chip-action"
                              onClick={() => openKeyTask('capability-statement')}
                              aria-label={language === 'es' ? 'Abrir la declaración de capacidades' : 'Open capability statement'}
                            >
                              {language === 'es' ? 'Declaración de capacidades' : 'Capability statement'}
                              {capabilityReady
                                ? ''
                                : (language === 'es' ? ' · Borrador' : ' · Draft')}
                            </button>
                          ) : (
                            <span>
                              {language === 'es' ? 'Declaración de capacidades' : 'Capability statement'}
                              {' · —'}
                            </span>
                          )}
                          <HelpBadge label={language === 'es' ? 'Qué es la declaración de capacidades' : 'What a capability statement is'} wide>
                            <p>
                              {language === 'es'
                                ? 'Es la página de una hoja (y el PDF) que describe el trabajo de la empresa: nombre legal, UEI, CAGE, códigos, teléfono y el trabajo que puede hacer. No copie el UEI, CAGE ni el número de contrato de otra empresa.'
                                : 'It is the one-page page (and PDF) that describes the company’s work: legal name, UEI, CAGE, codes, phone, and the work it can perform. Do not copy another company’s UEI, CAGE, or contract number.'}
                            </p>
                          </HelpBadge>
                        </span>
                      </div>
                      {companyIdsOpen ? (
                        <DodLevelWorkbench
                          part="ids"
                          language={language}
                          taxonomy={taxonomy}
                          dispositions={state.practiceDispositions || {}}
                          assessment={state.levelAssessment || null}
                          connectors={state.evidenceConnectors || emptyDodEvidenceConnectors()}
                          classifyBusy={classifyBusy}
                          canUseAi={canSyncRemote}
                          hideScopeEditors
                          plan={gatedPlan}
                          onTaxonomyChange={(nextTaxonomy) => {
                            const company = nextTaxonomy.companies.find((row) => row.id === nextTaxonomy.activeCompanyId);
                            void persist({
                              ...state,
                              updatedAt: new Date().toISOString(),
                              taxonomy: nextTaxonomy,
                              targetCompanyName: company?.name || state.targetCompanyName,
                            });
                          }}
                          onDispositionChange={(practiceId, disposition: PracticeDisposition) => {
                            void persist({
                              ...state,
                              updatedAt: new Date().toISOString(),
                              practiceDispositions: {
                                ...(state.practiceDispositions || {}),
                                [practiceId]: disposition,
                              },
                            });
                          }}
                          onConnectorsChange={(evidenceConnectors) => {
                            void persist({
                              ...state,
                              updatedAt: new Date().toISOString(),
                              evidenceConnectors,
                            });
                          }}
                          onClassify={() => { void runDodClassify(); }}
                        />
                      ) : null}
                    </section>
                    ) : null}
                    <div className="dod-dashboard-board">
                    <div className="dod-decisions" role="region" aria-label={language === 'es' ? 'Tus tareas' : 'Your tasks'}>
                      <div className="dod-work-toolbar">
                        <h3>
                          {language === 'es' ? 'Tus tareas' : 'Your tasks'}
                          {keepCopyPending > 0 ? (
                            <span
                              className="dod-keep-copy-badge"
                              title={language === 'es'
                                ? 'Documentos que debe descargar y guardar en el archivo de la empresa'
                                : 'Documents that must be downloaded and kept in the company file'}
                            >
                              {language === 'es'
                                ? `${keepCopyPending} por guardar`
                                : `${keepCopyPending} to save`}
                            </span>
                          ) : null}
                          <HelpBadge label={language === 'es' ? 'Acerca de los próximos pasos' : 'About immediate next steps'}>
                            <p>
                              {language === 'es'
                                ? 'Siguiente muestra los pasos que puede hacer ahora. Bloqueado espera un paso anterior. Hecho significa que ese paso ya terminó. Cada columna es un grupo. El anillo muestra cuánto de ese grupo ya terminó.'
                                : 'Next shows steps you can do now. Blocked waits on an earlier step. Done means that step is finished. Each column is one group. The ring is how much of that group is finished.'}
                            </p>
                          </HelpBadge>
                        </h3>
                      </div>
                      <p className="dod-focus-status" role="status">
                        {language === 'es'
                          ? (dodDecisions.length === 0
                            ? (isDod
                              ? `${selectedCompany.name.trim()} puede ofertar.`
                              : `${selectedCompany.name.trim()} no tiene pasos abiertos.`)
                            : (isDod
                              ? `${selectedCompany.name.trim()} todavía tiene pasos por terminar. Los pasos terminados siguen en la lista para que vea lo que ya hizo.`
                              : `${selectedCompany.name.trim()} todavía tiene pasos abiertos. Pulse un paso para ver qué hacer.`))
                          : (dodDecisions.length === 0
                            ? (isDod
                              ? `${selectedCompany.name.trim()} is ready to bid.`
                              : `${selectedCompany.name.trim()} has no open steps.`)
                            : (isDod
                              ? `${selectedCompany.name.trim()} still has steps to finish before it can bid. Finished steps stay on the list so you can see what is already done.`
                              : `${selectedCompany.name.trim()} still has open steps. Click a step to see what to do.`))}
                      </p>
                      {nextStep ? (
                        <div className="compliance-next-step">
                          <p>
                            <strong>{language === 'es' ? 'Haga esto ahora' : 'Do this next'}</strong>
                            {' '}
                            {nextStep.item.title}
                          </p>
                          <p>
                            {nextStep.depth === 0
                              ? (language === 'es'
                                ? 'Nada tiene que terminar antes. Es el paso con menos dependencias.'
                                : 'Nothing has to finish first. It has the fewest earlier steps.')
                              : (language === 'es'
                                ? `Espera ${nextStep.depth} paso${nextStep.depth === 1 ? '' : 's'} anterior${nextStep.depth === 1 ? '' : 'es'}.`
                                : `It waits on ${nextStep.depth} earlier step${nextStep.depth === 1 ? '' : 's'}.`)}
                          </p>
                          <button
                            type="button"
                            className="dashboard-btn dashboard-btn-primary"
                            onClick={() => {
                              setOpenBoardSectionId(nextStep.pillarId);
                              setFocusDecisionId(nextStep.item.id);
                              setSoloTask(true);
                              setLaneFilter(['ready', 'blocked', 'done']);
                            }}
                          >
                            {language === 'es' ? 'Abrir ese paso' : 'Open that step'}
                          </button>
                        </div>
                      ) : null}
                      <div className="compliance-task-sort" role="group" aria-label={language === 'es' ? 'Orden de las tareas' : 'Task order'}>
                        {([
                          { id: 'dependencies' as const, label: language === 'es' ? 'Menos dependencias' : 'Fewest earlier steps', icon: 'chain' },
                          { id: 'due' as const, label: language === 'es' ? 'Fecha de presentación' : 'Filing date', icon: 'date' },
                          { id: 'unlocks' as const, label: language === 'es' ? 'Desbloquea más pasos' : 'Unlocks the most steps', icon: 'unlock' },
                          { id: 'blocking' as const, label: language === 'es' ? 'Bloqueantes primero' : 'Blocking first', icon: 'block' },
                        ]).map((option) => (
                          <button
                            key={option.id}
                            type="button"
                            aria-pressed={taskSort === option.id}
                            aria-label={option.label}
                            className={taskSort === option.id ? 'is-on' : ''}
                            onClick={() => setTaskSort(option.id)}
                          >
                            {option.icon === 'chain' ? (
                              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 12h6M8 8H6a4 4 0 0 0 0 8h2M16 8h2a4 4 0 0 1 0 8h-2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                            ) : null}
                            {option.icon === 'date' ? (
                              <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="5" width="16" height="15" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M4 9.5h16M8 3.5v3M16 3.5v3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                            ) : null}
                            {option.icon === 'unlock' ? (
                              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 11V8a5 5 0 0 1 9.5-2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><rect x="5" y="11" width="14" height="9" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" /></svg>
                            ) : null}
                            {option.icon === 'block' ? (
                              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M7 17 17 7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                            ) : null}
                            <span className="icon-tip">{option.label}</span>
                          </button>
                        ))}
                      </div>
                      <div
                        id="dod-task-panel-open"
                      >
                      <ComplianceWorkflowLegend
                        language={language}
                        kind={definition.kind}
                        selected={laneFilter}
                        onToggle={(lane) => setLaneFilter((current) => (
                          current.includes(lane) ? current.filter((item) => item !== lane) : [...current, lane]
                        ))}
                      />
                      {taskSections.length === 0 ? (
                        <p className="dod-focus-status" role="status">
                          {language === 'es'
                            ? 'Ningún paso en este grupo.'
                            : 'No steps in this group.'}
                        </p>
                      ) : null}
                      {soloTask && focusDecisionId && !taskFocus ? (
                        <div className="compliance-task-focus">
                          <button
                            type="button"
                            className="dashboard-security-inline-btn"
                            onClick={() => {
                              setSoloTask(false);
                              setFocusDecisionId(null);
                            }}
                          >
                            {language === 'es' ? 'Cerrar' : 'Close'}
                          </button>
                        </div>
                      ) : null}
                      {taskSections.length ? (
                        <div className="compliance-project-board">
                          <div className="compliance-section-tabs" role="tablist" aria-label={language === 'es' ? 'Grupos de pasos' : 'Step groups'}>
                            {(soloTask && focusDecisionId
                              ? sortedSections.filter((section) => section.rows.some((row) => row.item.id === focusDecisionId))
                              : sortedSections).map((section, index) => {
                              const open = openBoardSectionId === section.id
                                || Boolean(soloTask && focusDecisionId && section.rows.some((row) => row.item.id === focusDecisionId))
                                || Boolean(taskFocus && section.rows.some((row) => row.item.id === (focusDecisionId || taskFocus.taskId)));
                              const tone = ['#c23b73', '#1f8a70', '#7a3e9d', '#1f3f73', '#d06a1f'][index % 5];
                              return (
                                <div key={section.id} className="compliance-section">
                                <button
                                  type="button"
                                  role="tab"
                                  id={`compliance-section-tab-${section.id}`}
                                  aria-controls={`compliance-section-panel-${section.id}`}
                                  aria-selected={open}
                                  className={`compliance-section-tab${open ? ' is-open' : ''}`}
                                  style={{ ['--project-tone' as string]: tone }}
                                  onClick={() => setOpenBoardSectionId(open ? null : section.id)}
                                >
                                  <SectionProgressRing
                                    done={section.done}
                                    total={section.total}
                                    blocked={section.marks.filter((mark) => mark === 'wait').length}
                                    language={language}
                                  />
                                  <span className="compliance-step-section-name">{section.label}</span>
                                  <span className="compliance-section-count">{section.done}/{section.total}</span>
                                </button>
                                {open ? (
                            <div
                              id={`compliance-section-panel-${section.id}`}
                              role="tabpanel"
                              aria-labelledby={`compliance-section-tab-${section.id}`}
                              className="compliance-section-panel compliance-project-col"
                              style={{ ['--project-tone' as string]: tone }}
                            >
                              <ul>
                          {(soloTask && focusDecisionId
                            ? section.rows.filter((entry) => entry.item.id === focusDecisionId)
                            : section.rows).map((entry) => {
                            const row = entry;
                            const open = focusDecisionId === row.item.id;
                            const notes = String(state.items[row.item.id]?.notes || '').trim();
                            const itemStatus = (state.items[row.item.id]?.status || 'not_started') as ComplianceItemStatus;
                            const deps = checklistDependencyStates(catalog, state, row.item);
                            const isWaiting = checklistItemWaiting(catalog, state, row.item);
                            const waitLabel = isWaiting ? complianceWaitLabel(row.item, state, language) : '';
                            const completeLocked = isItemCompleteLocked(row.item, state.items[row.item.id], { uei: ueiValue, cage: cageValue });
                            const lockedReason = itemCompleteLockedReason(row.item, language);
                            const stepCounts = verifiedStepCounts(row.item, state.items[row.item.id]);
                            const seenTerms = new Set<DodGlossaryTermId>();
                            const prereqFacts = { companyName: selectedCompany.name, ein: einValue };
                            const statusChip = itemStatus === 'reviewed'
                              ? statusLabel(itemStatus, language, definition.kind)
                              : isWaiting
                              ? waitLabel
                              : stepCounts.total
                                ? (language === 'es'
                                  ? `${stepCounts.done} de ${stepCounts.total} verificados`
                                  : `${stepCounts.done} of ${stepCounts.total} verified`)
                                : statusLabel(itemStatus, language, definition.kind);
                            const markStatus = itemStatus === 'reviewed' ? 'reviewed' : isWaiting ? 'blocked' : itemStatus;
                            const panelId = `dod-task-detail-${row.item.id}`;
                            const filedFact = itemStatus === 'reviewed'
                              ? (row.item.id === 'ein-irs' && einValue
                                ? `EIN ${einValue}`
                                : row.item.id === 'tx-llc-good-standing' && selectedCompany.name.trim()
                                  ? selectedCompany.name.trim()
                                  : '')
                              : '';
                            return (
                              <li
                                key={row.item.id}
                                id={`compliance-task-${row.item.id}`}
                                className={`compliance-dep-row${open ? ' is-open' : ''}${itemStatus === 'reviewed' ? ' is-done' : ''}${isWaiting ? ' is-wait' : ''}`}
                                style={{ ['--dep-depth' as string]: row.depth }}
                                {...(soloTask && open && !taskFocus ? { role: 'dialog', 'aria-modal': true as const, 'aria-label': row.item.title } : {})}
                              >
                                <div className={`compliance-dep-head${soloTask && open ? ' is-solo-open' : ''}`}>
                                  <button
                                    type="button"
                                    className="compliance-dep-trigger"
                                    aria-expanded={open}
                                    aria-controls={panelId}
                                    tabIndex={soloTask && open ? -1 : undefined}
                                    aria-hidden={soloTask && open ? true : undefined}
                                    aria-label={language === 'es'
                                      ? `${open ? 'Cerrar' : 'Abrir'} ${row.item.title}`
                                      : `${open ? 'Close' : 'Open'} ${row.item.title}`}
                                    onClick={() => {
                                      // Solo open: only Close exits. Native <select> option clicks
                                      // can fall through onto this full-bleed layer and dump the task.
                                      if (open && soloTask) {
                                        return;
                                      }
                                      setFocusDecisionId(open ? null : row.item.id);
                                    }}
                                  />
                                  {/* Solo dialog: skip the decorative mark — it looks like a second checkbox next to verify steps. */}
                                  {soloTask && open ? null : (
                                    <span className={`compliance-dep-mark status-${markStatus}`} aria-hidden="true">
                                      {itemStatus === 'reviewed' ? '✓' : ''}
                                    </span>
                                  )}
                                  <div className="compliance-dep-title">
                                    <DodTermText text={row.item.title} language={language} seen={seenTerms} />
                                  </div>
                                  {open && soloTask ? (
                                    <div className="compliance-task-top-actions">
                                      <TaskAssistButtons
                                        labeled
                                        language={language}
                                        mode={taskAssist?.id === row.item.id ? taskAssist.mode : null}
                                        onExplain={() => {
                                          setFocusDecisionId(row.item.id);
                                          setTaskAssist((current) => (
                                            current?.id === row.item.id && current.mode === 'explain'
                                              ? null
                                              : { id: row.item.id, mode: 'explain' }
                                          ));
                                        }}
                                        onDo={() => {
                                          setFocusDecisionId(row.item.id);
                                          const closing = taskAssist?.id === row.item.id && taskAssist.mode === 'do';
                                          setTaskAssist(closing ? null : { id: row.item.id, mode: 'do' });
                                        }}
                                      />
                                      {itemStatus === 'reviewed' ? null : (
                                        <button
                                          type="button"
                                          className="dashboard-btn dashboard-btn-primary compliance-task-complete"
                                          onClick={() => updateItem(row.item.id, {}, true)}
                                          disabled={writeLocked || completeLocked}
                                        >
                                          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                                            <path d="M6 12.5 10 16.5 18 8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                          </svg>
                                          {itemCompleteLabel(row.item, language, definition.kind)}
                                        </button>
                                      )}
                                      {writeLocked || completeLocked ? (
                                        <p className="dashboard-panel-subtitle compliance-task-complete-hint">
                                          {writeLocked
                                            ? (language === 'es' ? 'Solo lectura' : 'Read-only')
                                            : lockedReason}
                                        </p>
                                      ) : null}
                                    </div>
                                  ) : open ? (
                                    <TaskAssistButtons
                                      language={language}
                                      mode={taskAssist?.id === row.item.id ? taskAssist.mode : null}
                                      onExplain={() => {
                                        setFocusDecisionId(row.item.id);
                                        setTaskAssist((current) => (
                                          current?.id === row.item.id && current.mode === 'explain'
                                            ? null
                                            : { id: row.item.id, mode: 'explain' }
                                        ));
                                      }}
                                      onDo={() => {
                                        setFocusDecisionId(row.item.id);
                                        const closing = taskAssist?.id === row.item.id && taskAssist.mode === 'do';
                                        setTaskAssist(closing ? null : { id: row.item.id, mode: 'do' });
                                      }}
                                    />
                                  ) : null}
                                  <label
                                    className={`compliance-console-item-status-select-wrap status-${itemStatus}`}
                                    onMouseDown={(event) => event.stopPropagation()}
                                    onClick={(event) => event.stopPropagation()}
                                    onKeyDown={(event) => event.stopPropagation()}
                                  >
                                    <span className="visually-hidden">
                                      {language === 'es' ? 'Estado de la tarea' : 'Task status'}
                                    </span>
                                    <select
                                      className={`compliance-console-item-status-select status-${itemStatus}`}
                                      value={itemStatus}
                                      aria-label={`${row.item.title} status`}
                                      title={isWaiting ? statusChip : undefined}
                                      disabled={Boolean(writeLocked)}
                                      onMouseDown={(event) => event.stopPropagation()}
                                      onClick={(event) => event.stopPropagation()}
                                      onChange={(event) => {
                                        event.stopPropagation();
                                        updateItem(row.item.id, {
                                          status: event.target.value as ComplianceItemStatus,
                                        });
                                      }}
                                    >
                                      {statusOptionsFor(definition.kind).map((option) => (
                                        <option key={option.value} value={option.value}>
                                          {language === 'es' ? option.labelEs : option.labelEn}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                  {soloTask && open ? null : (
                                    <span className="compliance-dep-chevron" aria-hidden="true">{open ? '▾' : '▸'}</span>
                                  )}
                                  {soloTask && open && !taskFocus ? (
                                    <button
                                      type="button"
                                      className="task-solo-close"
                                      onClick={() => {
                                        setSoloTask(false);
                                        setFocusDecisionId(null);
                                      }}
                                    >
                                      {language === 'es' ? 'Cerrar' : 'Close'}
                                    </button>
                                  ) : null}
                                </div>
                                {filedFact ? <p className="compliance-step-fact">{filedFact}</p> : null}
                                {open && deps.length && !(soloTask && deps.every((dep) => dep.done)) ? (
                                  <p className={`compliance-dep-after${(soloTask && open) || !deps.every((dep) => dep.done) ? ' is-blocked' : ' is-clear'}`}>
                                    <DodTermText
                                      language={language}
                                      seen={seenTerms}
                                      text={
                                        `${soloTask && open
                                          ? (language === 'es' ? 'Haga esto primero: ' : 'Do this first: ')
                                          : deps.every((dep) => dep.done)
                                            ? (language === 'es' ? 'Ya hecho: ' : 'Already done: ')
                                            : (language === 'es' ? 'Espera a: ' : 'Waits on: ')}${(soloTask && open ? deps.filter((dep) => !dep.done) : deps)
                                          .map((dep) => shortDependencyLabel(dep.item, language, prereqFacts))
                                          .join(' · ')}`
                                      }
                                    />
                                  </p>
                                ) : null}
                                {open ? (
                                  <div id={panelId} className="compliance-dep-panel" role="region">
                                    {soloTask && row.item.action && !row.item.verifySteps?.length
                                      && taskAssist?.id !== row.item.id ? (
                                      <p className="compliance-dep-action">
                                        <DodTermText text={row.item.action} language={language} seen={seenTerms} />
                                      </p>
                                    ) : null}
                                    {(() => {
                                      const outside = outsideWaitFor(row.item, state);
                                      if (!outside) return null;
                                      const party = language === 'es' ? outside.party.es : outside.party.en;
                                      return (
                                        <p className="compliance-outside-wait">
                                          <DodTermText
                                            language={language}
                                            seen={seenTerms}
                                            text={language === 'es'
                                              ? `En espera de ${party}. Revise la página oficial cada ${outside.checkDays} días. Este paso permanece en Inicio hasta que respondan.`
                                              : `Waiting on ${party}. Check the official page every ${outside.checkDays} days. This step stays on Home until they answer.`}
                                          />
                                        </p>
                                      );
                                    })()}
                                    {deps.length && !(soloTask && open) ? (
                                      <section className="task-fold" aria-label={language === 'es' ? 'Depende de' : 'Depends on'}>
                                        <h4>{language === 'es' ? 'Depende de' : 'Depends on'}</h4>
                                        <DodPrerequisites
                                          deps={deps}
                                          language={language}
                                          facts={prereqFacts}
                                          waitingNote={row.item.dependencyNote}
                                        />
                                      </section>
                                    ) : null}
                                    <DodHowTo
                                      item={row.item}
                                      language={language}
                                      waiting={isWaiting}
                                      itemState={state.items[row.item.id]}
                                      seenTerms={seenTerms}
                                      assist={taskAssist?.id === row.item.id ? taskAssist.mode : null}
                                      brief={soloTask && focusDecisionId === row.item.id}
                                      calendarDate={(state.calendarEvents || []).find((event) => event.id === row.item.id && event.date && !event.skipped)?.date || ''}
                                      onCalendarDate={(date) => {
                                        void persist({
                                          ...state,
                                          updatedAt: new Date().toISOString(),
                                          calendarEvents: upsertCalendarDate(state.calendarEvents || [], {
                                            id: row.item.id,
                                            title: row.item.title,
                                            date,
                                          }),
                                        });
                                      }}
                                      apiBase={apiBase}
                                      accessToken={accessToken}
                                      agentSkill={isDod ? 'dod_gap' : isCloud ? 'cloud_review' : isOfac ? 'ofac_watch' : isWebsite ? 'website_scan' : undefined}
                                      consoleKind={definition.kind}
                                      companyId={subscriptionAccountId}
                                      legalName={selectedCompany?.name || ''}
                                      ein={einValue}
                                      onRegistryChanged={() => setRegistryTick((tick) => tick + 1)}
                                      onApplyCalendarDates={(events) => {
                                        let nextEvents = [...(state.calendarEvents || [])];
                                        for (const event of events) {
                                          nextEvents = upsertCalendarDate(nextEvents, event);
                                        }
                                        const taskId = calendarTaskId(definition.kind);
                                        void persist({
                                          ...state,
                                          updatedAt: new Date().toISOString(),
                                          calendarEvents: nextEvents,
                                          items: {
                                            ...state.items,
                                            [taskId]: {
                                              ...(state.items[taskId] || { status: 'open', notes: '' }),
                                              status: 'done',
                                              notes: language === 'es'
                                                ? 'Fechas publicadas y cadencias claras aplicadas con Hazlo por mí.'
                                                : 'Published dates and clear cadences applied with Do it for me.',
                                            },
                                          },
                                        });
                                      }}
                                      existingCalendarEvents={state.calendarEvents || []}
                                      onMarkComplete={(patch) => {
                                        if (writeLocked) {
                                          updateItem(row.item.id, {
                                            status: itemStatus === 'not_started' ? 'in_progress' : itemStatus,
                                            stepProgress: {
                                              ...(state.items[row.item.id]?.stepProgress || {}),
                                              ...patch,
                                            },
                                          });
                                          return;
                                        }
                                        updateItem(row.item.id, {
                                          stepProgress: {
                                            ...(state.items[row.item.id]?.stepProgress || {}),
                                            ...patch,
                                          },
                                        }, true);
                                      }}
                                      onUseAnswer={(note) => updateItem(row.item.id, {
                                        notes: note,
                                        status: itemStatus === 'not_started' ? 'in_progress' : itemStatus,
                                      })}
                                      onStepProgressChange={(stepProgress) => updateItem(row.item.id, {
                                        status: itemStatus === 'not_started' ? 'in_progress' : itemStatus,
                                        stepProgress,
                                      })}
                                    />
                                    <ControlProofProposals
                                      controlId={row.item.id}
                                      proposals={registryProposals.filter((rowProposal) => rowProposal.control_id === row.item.id)}
                                      language={language}
                                      gatewayBaseUrl={apiBase}
                                      accessToken={accessToken}
                                      readOnly={writeLocked}
                                      onChanged={() => setRegistryTick((tick) => tick + 1)}
                                      onAddCredentials={(portalId) => setCredentialFocusPortal(portalId || 'tx_comptroller')}
                                    />
                                    {notes ? (
                                      <details className="dod-decision-notes">
                                        <summary>{language === 'es' ? 'Notas del caso' : 'Case notes'}</summary>
                                        <p>{notes}</p>
                                      </details>
                                    ) : null}
                                    {row.item.verifySteps?.length || (soloTask && open) ? null : (
                                      <p className="dod-decision-proof">
                                        {language === 'es' ? 'Cuando termine, conserve: ' : 'When done, keep: '}
                                        <DodTermText text={row.item.evidenceHint} language={language} seen={seenTerms} />
                                      </p>
                                    )}
                                    {soloTask && open && !row.item.keepCopy ? null : (
                                    <div className="dod-decision-item-actions">
                                      {row.item.keepCopy ? (
                                        <button
                                          type="button"
                                          className={`dod-keep-copy-chip${state.items[row.item.id]?.copySavedAt || itemStatus === 'reviewed' ? ' is-saved' : ''}`}
                                          onClick={() => {
                                            const saved = Boolean(state.items[row.item.id]?.copySavedAt) || itemStatus === 'reviewed';
                                            updateItem(row.item.id, {
                                              copySavedAt: saved ? null : new Date().toISOString(),
                                            });
                                          }}
                                        >
                                          {state.items[row.item.id]?.copySavedAt || itemStatus === 'reviewed'
                                            ? (language === 'es' ? 'Guardado' : 'Saved')
                                            : (language === 'es' ? 'Guardar una copia' : 'Save a copy')}
                                        </button>
                                      ) : null}
                                      {/* Solo open: Mark complete lives in the header next to Explain / Do it for me. */}
                                      {soloTask && open ? null : itemStatus === 'reviewed' ? null : (
                                        <>
                                        <button
                                          type="button"
                                          className="dashboard-btn dashboard-btn-primary"
                                          onClick={() => updateItem(row.item.id, {}, true)}
                                          disabled={writeLocked || completeLocked}
                                        >
                                          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                                            <path d="M6 12.5 10 16.5 18 8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                          </svg>
                                          {itemCompleteLabel(row.item, language, definition.kind)}
                                        </button>
                                        {writeLocked || completeLocked ? (
                                          <p className="dashboard-panel-subtitle">
                                            {writeLocked
                                              ? (language === 'es' ? 'Solo lectura' : 'Read-only')
                                              : lockedReason}
                                          </p>
                                        ) : null}
                                        </>
                                      )}
                                    </div>
                                    )}
                                  </div>
                                ) : null}
                              </li>
                            );
                          })}
                              </ul>
                            </div>
                                ) : null}
                                </div>
                              );
                            })}
                            {isDod ? (
                              <div className="compliance-section">
                                <button
                                  type="button"
                                  role="tab"
                                  id="compliance-section-tab-cmmc-worksheet"
                                  aria-controls="compliance-section-panel-cmmc-worksheet"
                                  aria-selected={openBoardSectionId === 'cmmc-worksheet'}
                                  className={`compliance-section-tab${openBoardSectionId === 'cmmc-worksheet' ? ' is-open' : ''}`}
                                  style={{ ['--project-tone' as string]: '#1f6feb' }}
                                  onClick={() => setOpenBoardSectionId(openBoardSectionId === 'cmmc-worksheet' ? null : 'cmmc-worksheet')}
                                >
                                  <SectionProgressRing
                                    done={cmmcPracticeDone}
                                    total={cmmcPracticeRows.length}
                                    blocked={0}
                                    language={language}
                                  />
                                  <span className="compliance-step-section-name">CMMC</span>
                                  <span className="compliance-section-count">{cmmcPracticeDone}/{cmmcPracticeRows.length}</span>
                                </button>
                                {openBoardSectionId === 'cmmc-worksheet' ? (
                                  <div
                                    id="compliance-section-panel-cmmc-worksheet"
                                    role="tabpanel"
                                    aria-labelledby="compliance-section-tab-cmmc-worksheet"
                                    className="compliance-section-panel compliance-project-col"
                                    style={{ ['--project-tone' as string]: '#1f6feb' }}
                                  >
                                    <h3 className="dod-cmmc-section-title">
                                      CMMC
                                      <HelpBadge label={language === 'es' ? 'Dónde va esto' : 'Where this belongs'}>
                                        <p>
                                          {language === 'es'
                                            ? 'Estas son las prácticas de ciberseguridad del trabajo. El anillo es cuántas están marcadas como cumplidas. NextCompli no certifica CMMC.'
                                            : 'These are the cybersecurity practices for the job. The ring is how many are marked met. NextCompli does not certify CMMC.'}
                                        </p>
                                      </HelpBadge>
                                    </h3>
                                    <DodLevelWorkbench
                                      part="level"
                                      language={language}
                                      taxonomy={taxonomy}
                                      dispositions={state.practiceDispositions || {}}
                                      assessment={state.levelAssessment || null}
                                      connectors={state.evidenceConnectors || emptyDodEvidenceConnectors()}
                                      classifyBusy={classifyBusy}
                                      canUseAi={canSyncRemote}
                                      hideScopeEditors
                                      plan={gatedPlan}
                                      onTaxonomyChange={(nextTaxonomy) => {
                                        const company = nextTaxonomy.companies.find((row) => row.id === nextTaxonomy.activeCompanyId);
                                        void persist({
                                          ...state,
                                          updatedAt: new Date().toISOString(),
                                          taxonomy: nextTaxonomy,
                                          targetCompanyName: company?.name || state.targetCompanyName,
                                        });
                                      }}
                                      onDispositionChange={(practiceId, disposition: PracticeDisposition) => {
                                        void persist({
                                          ...state,
                                          updatedAt: new Date().toISOString(),
                                          practiceDispositions: {
                                            ...(state.practiceDispositions || {}),
                                            [practiceId]: disposition,
                                          },
                                        });
                                      }}
                                      onConnectorsChange={(evidenceConnectors) => {
                                        void persist({
                                          ...state,
                                          updatedAt: new Date().toISOString(),
                                          evidenceConnectors,
                                        });
                                      }}
                                      onClassify={() => { void runDodClassify(); }}
                                    />
                                  </div>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ) : (
                        <p className="compliance-console-company-hint" role="status">
                          {language === 'es' ? 'No hay pasos inmediatos abiertos.' : 'No immediate next steps are open.'}
                        </p>
                      )}
                      </div>
                    </div>
                  </div>
                  </div>

                  {isWebsite && (settingsOpen || lastScan) ? (
                    <div className="compliance-console-scan dod-console-tool">
                      <div className="compliance-console-scan-header">
                        <div className="dod-scope-picker-label">
                          <h3>{language === 'es' ? 'Escaneo de homepage' : 'Homepage scan'}</h3>
                          <HelpBadge label={language === 'es' ? 'Qué escanea' : 'What we scan'} wide>
                          <p>
                            <DodTermText
                              text={language === 'es'
                                ? 'Lee el HTML de la homepage y el DNS: enlaces de privacidad, cookies, términos, DMCA y accesibilidad; banner y CMP; rastreadores; controles CCPA y un archivo de declaración GPC; HTTPS; encabezados HSTS, nosniff, CSP, marco y referrer; SPF, DKIM y DMARC; y listas públicas de bloqueo (DNSBL) de los servidores de correo del dominio guardado. Las páginas extra se quedan en este sitio. No es un motor WCAG, ni un puntaje de reputación, ni un pentest.'
                                : 'Reads homepage HTML and DNS: privacy, cookie, terms, DMCA, and accessibility links; consent banner and CMP; trackers; CCPA checks and a GPC declaration file; HTTPS; HSTS, nosniff, CSP, frame, and referrer headers; SPF, DKIM, and DMARC; and public block lists (DNSBL) for the mail servers of the domain you saved. Optional extra pages stay on this site only. Not a WCAG engine, a reputation score, or a pentest.'}
                              language={language}
                            />
                          </p>
                          </HelpBadge>
                        </div>
                        {settingsOpen ? (
                          <p className="dashboard-panel-subtitle">
                            {selectedCompany?.name?.trim() || (language === 'es' ? 'Sitio guardado' : 'Saved website')}
                          </p>
                        ) : null}
                      </div>
                      {settingsOpen ? (
                      <div className="compliance-console-scan-row">
                        <label className="compliance-console-scan-option">
                          <input
                            type="checkbox"
                            checked={crawlEnabled}
                            onChange={(event) => setCrawlEnabled(event.target.checked)}
                          />
                          {language === 'es' ? 'Incluir hasta 8 páginas del mismo sitio' : 'Include up to 8 pages on this site'}
                        </label>
                        <label className="compliance-console-scan-option">
                          <span className="visually-hidden">{language === 'es' ? 'Cadencia de reescaneo' : 'Rescan cadence'}</span>
                          <select
                            value={scanCadence}
                            aria-label={language === 'es' ? 'Cadencia de reescaneo' : 'Rescan cadence'}
                            onChange={(event) => setScanCadence(event.target.value as WebsiteScanCadence)}
                          >
                            <option value="off">{language === 'es' ? 'Sin reescaneo' : 'No schedule'}</option>
                            <option value="weekly">{language === 'es' ? 'Semanal (CompliCore)' : 'Weekly (CompliCore)'}</option>
                            <option value="daily" disabled={activePlan === 'basic'}>
                              {language === 'es' ? 'Diario (CompliAdvantage)' : 'Daily (CompliAdvantage)'}
                            </option>
                          </select>
                        </label>
                        <button
                          type="button"
                          className="dashboard-btn dashboard-btn-primary"
                          disabled={writeLocked || scanBusy || !canSyncRemote}
                          onClick={() => void runWebsiteScan(websiteScanUrlFromName(selectedCompany?.name || '') || undefined)}
                        >
                          {scanBusy
                            ? (language === 'es' ? 'Escaneando…' : 'Scanning…')
                            : (language === 'es' ? 'Ejecutar escaneo' : 'Run scan')}
                        </button>
                      </div>
                      ) : null}
                      {scanMessage ? (
                        <p className="compliance-console-scan-message" role="status">
                          <DodTermText text={scanMessage} language={language} />
                        </p>
                      ) : null}
                      {lastScan ? (
                        <ScanFindingsPanel
                          scan={lastScan}
                          history={state.lastScanHistory || []}
                          language={language}
                        />
                      ) : null}
                    </div>
                  ) : null}

                  {isCloud && hasNamedCompany ? (
                    <div className="compliance-console-scan dod-console-tool">
                      <div className="compliance-console-scan-header">
                        <div className="dod-scope-picker-label">
                          <h3>{language === 'es' ? 'Revisión diaria de solo lectura' : 'Daily read-only review'}</h3>
                          <HelpBadge label={language === 'es' ? 'Qué hace la revisión' : 'What the review does'} wide>
                            <p>
                              <DodTermText
                                text={language === 'es'
                                  ? 'CompliAdvantage. Ponga en cola una revisión al día para el rol de solo lectura que concedió el cliente. Este botón no entra a la nube y no marca un paso como hecho. Cuando ese rol esté conectado, la revisión leerá identidad, registros, red, almacenamiento, cifrado, copias y hallazgos. No usa la cuenta de operaciones de NextCompli. CompliCore archiva la prueba en la lista escrita.'
                                  : 'CompliAdvantage. Queue one review a day for the read-only role the client granted. This button does not sign into the cloud and does not mark a step done. When that role is connected, the review reads identity, logging, network, storage, encryption, backups, and findings. It does not use the NextCompli operations account. CompliCore files proof on the written checklist.'}
                                language={language}
                              />
                            </p>
                          </HelpBadge>
                        </div>
                      </div>
                      {activePlan === 'basic' ? (
                        <p className="compliance-console-scan-message" role="status">
                          {language === 'es'
                            ? 'La lista escrita está en CompliCore. La revisión diaria de solo lectura es CompliAdvantage.'
                            : 'The written checklist is on CompliCore. The once-a-day read-only review is CompliAdvantage.'}
                        </p>
                      ) : (
                      <div className="compliance-console-scan-row">
                        <label>
                          <span className="visually-hidden">{language === 'es' ? 'Nube' : 'Cloud'}</span>
                          <select
                            value={cloudProvider}
                            aria-label={language === 'es' ? 'Nube' : 'Cloud'}
                            disabled={writeLocked}
                            onChange={(event) => setCloudProvider(event.target.value as 'aws' | 'azure' | 'gcp')}
                          >
                            <option value="aws">Amazon Web Services</option>
                            <option value="azure">Microsoft Azure</option>
                            <option value="gcp">Google Cloud</option>
                          </select>
                        </label>
                        <input
                          type="text"
                          value={cloudRoleName}
                          disabled={writeLocked}
                          onChange={(event) => setCloudRoleName(event.target.value)}
                          placeholder={language === 'es' ? 'Nombre del rol de solo lectura' : 'Read-only role name'}
                          aria-label={language === 'es' ? 'Nombre del rol de solo lectura' : 'Read-only role name'}
                          autoComplete="off"
                        />
                        <button
                          type="button"
                          className="dashboard-btn dashboard-btn-primary"
                          disabled={writeLocked}
                          onClick={() => {
                            try {
                              const accountName = selectedCompany?.name || state.targetCompanyName || '';
                              const next = queueCloudReadOnlyReview(state, {
                                provider: cloudProvider,
                                roleName: cloudRoleName,
                                accountName,
                              });
                              setCloudReviewMessage(next.lastCloudReview?.summary || '');
                              void persist(next);
                              if (apiBase && accessToken && cloudRoleName.trim()) {
                                void fetch(`${apiBase}/api/v1/agent/monitor/runs`, {
                                  method: 'POST',
                                  headers: {
                                    Authorization: `Bearer ${accessToken}`,
                                    'Content-Type': 'application/json',
                                  },
                                  body: JSON.stringify({
                                    skill: 'cloud_review',
                                    provider: cloudProvider,
                                    role_name: cloudRoleName,
                                    account_name: accountName,
                                  }),
                                }).then(async (response) => {
                                  if (!response.ok) {
                                    return;
                                  }
                                  setCloudReviewMessage((current) => (
                                    language === 'es'
                                      ? `${current || 'Revisión en cola.'} El Agente Monitor dejó un borrador en Consola → Agente.`
                                      : `${current || 'Review queued.'} The Monitor Agent left a draft under Console → Agent.`
                                  ));
                                }).catch(() => undefined);
                              }
                            } catch (error) {
                              setCloudReviewMessage(error instanceof Error ? error.message : 'Review was not queued.');
                            }
                          }}
                        >
                          {language === 'es' ? 'Poner en cola la revisión de hoy' : "Queue today's review"}
                        </button>
                      </div>
                      )}
                      {cloudReviewMessage ? (
                        <p className="compliance-console-scan-message" role="status">
                          <DodTermText text={cloudReviewMessage} language={language} />
                        </p>
                      ) : null}
                    </div>
                  ) : null}

                  {isDod && (samNotices.length > 0 || samOpportunities.length > 0) ? (
                  <div className="dod-company-identity">
                    {samNotices.length > 0 ? (
                      <ul className="dod-sam-feed" aria-label={language === 'es' ? 'Avisos oficiales de SAM' : 'Official SAM notices'}>
                        {samNotices.map((notice) => (
                          <li key={`${notice.from_addr || ''}-${notice.subject || ''}`}>
                            <strong>{notice.subject || (language === 'es' ? 'Aviso' : 'Notice')}</strong>
                            {notice.from_addr ? ` · ${notice.from_addr}` : ''}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    {samOpportunities.length > 0 ? (
                      <ul className="dod-sam-feed" aria-label={language === 'es' ? 'Oportunidades SAM públicas' : 'Public SAM opportunities'}>
                        {samOpportunities.map((opp) => (
                          <li key={opp.notice_id || opp.title}>
                            {opp.public_url ? (
                              <a href={opp.public_url} target="_blank" rel="noreferrer">
                                {opp.title || opp.solicitation_number}
                              </a>
                            ) : (
                              <span>{opp.title || opp.solicitation_number}</span>
                            )}
                            {opp.defense_related ? (language === 'es' ? ' · Defensa' : ' · Defense') : ''}
                            {opp.set_aside ? ` · ${opp.set_aside}` : ''}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                  ) : null}
                </div>
                <div
                  role="tabpanel"
                  id="dod-view-checklist"
                  aria-labelledby="dod-tab-checklist"
                  hidden={dodWorkspaceView !== 'checklist'}
                  className="dod-workspace-panel"
                >
                  <section className="dod-checklist-page" id="dod-checklist" aria-labelledby="dod-checklist-heading">
                    <div className="dod-checklist-page-head">
                      <h3 id="dod-checklist-heading">
                        {language === 'es' ? 'Lista' : 'Checklist'}
                        <HelpBadge label={language === 'es' ? 'Qué es esta pestaña' : 'What this tab is'}>
                          <p>
                            {language === 'es'
                            ? 'Próximos pasos muestra las 3 tarjetas de trabajo. Esta pestaña es la lista completa, agrupada por sección a la izquierda.'
                            : 'Next steps shows the three job cards. This tab is the full list, grouped by section on the left.'}
                          </p>
                        </HelpBadge>
                      </h3>
                      <p className="dod-checklist-lede">
                        {language === 'es'
                          ? 'Lista completa de esta empresa. Abra una fila, haga el trabajo y márquela completa cuando tenga la prueba. Vuelva a Próximos pasos para el siguiente trabajo.'
                          : 'The full list for this company. Open a row, do the work, then mark it complete when you have the proof. Use Next steps for the next job.'}
                      </p>
                    </div>
                    <div className="dod-checklist-layout">
                      <nav className="dod-checklist-nav" aria-label={language === 'es' ? 'Secciones de la lista' : 'Checklist sections'}>
                        {catalog.pillars.map((pillar) => {
                          const progress = pillarProgress(pillar, state, gatedPlan);
                          const selected = (activePillar?.id || catalog.pillars[0]?.id) === pillar.id;
                          return (
                            <button
                              key={pillar.id}
                              type="button"
                              aria-current={selected ? 'true' : undefined}
                              className={`dod-checklist-nav-item${selected ? ' is-active' : ''}${
                                progress.total > 0 && progress.reviewed === progress.total
                                  ? ' is-complete'
                                  : progress.reviewed > 0
                                    ? ' has-progress'
                                    : ' is-empty'
                              }`}
                              onClick={() => setActivePillarId(pillar.id)}
                            >
                              <span>{pillar.label}</span>
                              <span>{progress.reviewed}/{progress.total}</span>
                            </button>
                          );
                        })}
                        {applicableDodGates.length ? (
                          <details className="dod-compact-acc dod-checklist-gates">
                            <summary>
                              {language === 'es' ? 'Qué bloquea el primer contrato' : 'What blocks the first award'}
                              <HelpBadge label={language === 'es' ? 'Acerca de estas puertas' : 'About these gates'}>
                                <p>
                                  {language === 'es'
                                    ? 'Solo se muestran las puertas que aplican al alcance actual. CUI y clasificado aparecen cuando el contrato lo exige.'
                                    : 'Only the gates for this job are listed. CUI and classified appear when a contract requires them.'}
                                </p>
                              </HelpBadge>
                            </summary>
                            <ul className="compliance-console-scorecard">
                              {applicableDodGates.map((gate) => {
                                const items = dodItemsForGate(definition, gate).filter((item) => itemIncludedInPlan(item, gatedPlan));
                                const reviewed = items.filter((item) => state.items[item.id]?.status === 'reviewed').length;
                                const pct = Math.round((reviewed / items.length) * 100);
                                const gaps = items.filter((item) => state.items[item.id]?.status === 'gap').length;
                                const tone = gaps > 0 ? 'gap' : reviewed === items.length ? 'reviewed' : 'in_progress';
                                return (
                                  <li key={gate}>
                                    <div className={`compliance-console-scorecard-item tone-${tone}`}>
                                      <span className="compliance-console-scorecard-label">{dodGateLabel(gate, language)}</span>
                                      <span className="compliance-console-scorecard-meta">
                                        {reviewed}/{items.length} · {pct}%
                                        {gaps > 0 ? ` · ${gaps} ${language === 'es' ? 'brechas' : 'gaps'}` : ''}
                                      </span>
                                      <span className="compliance-console-scorecard-bar" aria-hidden="true">
                                        <span style={{ width: `${pct}%` }} />
                                      </span>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          </details>
                        ) : null}
                      </nav>
                      <div className="dod-checklist-main">
                        {activePillar?.summary ? (
                          <p className="dod-checklist-summary">{activePillar.summary}</p>
                        ) : null}
                        <div className="compliance-console-item-list">
                          {(activePillar?.items || []).map((item) => {
                            const itemState = state.items[item.id];
                            const status = itemState?.status || 'not_started';
                            const isCurrent = focusChecklistItemId === item.id;
                            const isWaiting = checklistItemWaiting(catalog, state, item);
                            const waitLabel = isWaiting ? complianceWaitLabel(item, state, language) : '';
                            const completeLocked = isItemCompleteLocked(item, itemState, { uei: ueiValue, cage: cageValue });
                            const planLocked = writeLocked || !itemIncludedInPlan(item, gatedPlan);
                            const planMin = itemPlanMin(item);
                            const planLabel = planMin === 'basic' ? '' : PLAN_PUBLIC_NAME[planMin];
                            return (
                              <ComplianceItemRow
                                key={item.id}
                                item={item}
                                status={status}
                                notes={itemState?.notes || ''}
                                reviewedAt={itemState?.reviewedAt}
                                language={language}
                                kind={definition.kind}
                                forceOpen={isCurrent}
                                highlighted={isCurrent}
                                hideGateChip
                                currentJob={isCurrent}
                                waiting={isWaiting}
                                waitLabel={waitLabel}
                                reviewDisabled={completeLocked}
                                reviewDisabledReason={completeLocked ? itemCompleteLockedReason(item, language) : ''}
                                planLocked={planLocked}
                                planLabel={planLabel}
                                primaryReview
                                simpleNotes
                                showStatusMark
                                prerequisites={checklistDependencyStates(catalog, state, item)}
                                dependencyDepth={checklistDependencyDepth(catalog, item.id)}
                                prereqFacts={{ companyName: selectedCompany.name, ein: einValue }}
                                itemState={itemState}
                                onStatusChange={(nextStatus) => updateItem(item.id, { status: nextStatus })}
                                onNotesChange={(notes) => updateItem(item.id, { notes })}
                                onMarkReviewed={() => updateItem(item.id, {}, true)}
                                onToggleCopySaved={() => {
                                  const saved = Boolean(itemState?.copySavedAt) || status === 'reviewed';
                                  updateItem(item.id, {
                                    copySavedAt: saved ? null : new Date().toISOString(),
                                  });
                                }}
                                onStepProgressChange={(stepProgress) => updateItem(item.id, {
                                  status: status === 'not_started' ? 'in_progress' : status,
                                  stepProgress,
                                })}
                                onCompleteWithEvidence={(stepProgress) => updateItem(item.id, {
                                  stepProgress: {
                                    ...(itemState?.stepProgress || {}),
                                    ...stepProgress,
                                  },
                                }, true)}
                                registryProposals={registryProposals.filter((row) => row.control_id === item.id)}
                                onRegistryChanged={() => setRegistryTick((tick) => tick + 1)}
                                onAddCredentials={(portalId) => setCredentialFocusPortal(portalId || 'tx_comptroller')}
                                writeLocked={writeLocked}
                                apiBase={apiBase}
                                accessToken={accessToken}
                                agentSkill={isDod ? 'dod_gap' : isCloud ? 'cloud_review' : isOfac ? 'ofac_watch' : isWebsite ? 'website_scan' : undefined}
                                companyId={subscriptionAccountId}
                                legalName={selectedCompany?.name || ''}
                                ein={einValue}
                                calendarDate={(state.calendarEvents || []).find((event) => event.id === item.id && event.date && !event.skipped)?.date || ''}
                                onCalendarDate={(date) => {
                                  void persist({
                                    ...state,
                                    updatedAt: new Date().toISOString(),
                                    calendarEvents: upsertCalendarDate(state.calendarEvents || [], {
                                      id: item.id,
                                      title: item.title,
                                      date,
                                    }),
                                  });
                                }}
                                onApplyCalendarDates={(events) => {
                                  let nextEvents = [...(state.calendarEvents || [])];
                                  for (const event of events) {
                                    nextEvents = upsertCalendarDate(nextEvents, event);
                                  }
                                  const taskId = calendarTaskId(definition.kind);
                                  void persist({
                                    ...state,
                                    updatedAt: new Date().toISOString(),
                                    calendarEvents: nextEvents,
                                    items: {
                                      ...state.items,
                                      [taskId]: {
                                        ...(state.items[taskId] || { status: 'open', notes: '' }),
                                        status: 'done',
                                        notes: language === 'es'
                                          ? 'Fechas publicadas y cadencias claras aplicadas con Hazlo por mí.'
                                          : 'Published dates and clear cadences applied with Do it for me.',
                                      },
                                    },
                                  });
                                }}
                                existingCalendarEvents={state.calendarEvents || []}
                              />
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
              {isDod && selectedProject && !taskFocus ? (
                <details className="dod-compact-acc dod-scope-settings" open={Boolean(contractDraft)}>
                  <summary>
                    {language === 'es' ? 'Aviso de trabajo' : 'Job posting'}
                    {projectContracts.find((row) => row.id === taxonomy.activeContractId)?.name.trim()
                      ? ` · ${projectContracts.find((row) => row.id === taxonomy.activeContractId)?.name.trim()}`
                      : ''}
                  </summary>
                  <p className="compliance-console-company-hint">
                    {language === 'es'
                      ? 'Este paso va al final. El aviso dice qué candados pide el gobierno. Si aún no tiene un aviso, déjelo vacío.'
                      : 'This step comes last. The posting says which locks the government wants. If you do not have a posting yet, leave it empty.'}
                  </p>
                  <div className="dod-scope-picker dod-scope-picker-sub">
                    <label htmlFor={contractDraft ? 'dod-contract-name' : 'dod-contract-select'}>
                      {language === 'es' ? 'Qué aviso' : 'Which posting'}
                    </label>
                    {contractDraft ? (
                      <form className="dod-register-form" onSubmit={saveDodContract}>
                        <label htmlFor="dod-contract-name">
                          {language === 'es' ? 'Aviso o vehículo' : 'Notice or vehicle'}
                          <input
                            id="dod-contract-name"
                            value={contractDraft.name}
                            autoFocus
                            required
                            placeholder={language === 'es' ? 'p. ej. número del aviso' : 'e.g. solicitation number'}
                            onChange={(event) => setContractDraft({ name: event.target.value })}
                          />
                        </label>
                        <div className="compliance-console-company-row">
                          <button type="submit" className="dashboard-btn dashboard-btn-primary">
                            {language === 'es' ? 'Guardar' : 'Save'}
                          </button>
                          <button type="button" className="dashboard-security-inline-btn" onClick={() => setContractDraft(null)}>
                            {language === 'es' ? 'Cancelar' : 'Cancel'}
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div className="compliance-console-company-row">
                        <select
                          id="dod-contract-select"
                          value={taxonomy.activeContractId || ''}
                          onChange={(event) => selectDodContractId(event.target.value)}
                        >
                          <option value="">{language === 'es' ? 'Ninguno todavía' : 'None yet'}</option>
                          {projectContracts.map((contract) => (
                            <option key={contract.id} value={contract.id}>
                              {contract.name.trim() || (language === 'es' ? 'Nombre requerido' : 'Name required')}
                            </option>
                          ))}
                        </select>
                        <button type="button" className="dashboard-security-inline-btn" onClick={addDodContract}>
                          {language === 'es' ? 'Agregar aviso' : 'Add posting'}
                        </button>
                      </div>
                    )}
                  </div>
                  {syncError ? <p className="dashboard-form-error" role="status">{syncError}</p> : null}
                </details>
              ) : null}
              </>
            ) : null}

            </>
        </>
        {(isCloud || isDod || isOfac || isWebsite || isBusiness) && gatewayBaseUrl && accessToken && hasNamedCompany ? (
          <FeatureAgentGates
            feature={
              isDod
                ? 'dod-contracts-compliance'
                : isCloud
                  ? 'cloud-compliance'
                  : isOfac
                    ? 'ofac-compliance'
                    : isBusiness
                      ? 'business-creation-compliance'
                      : 'website-compliance'
            }
            language={language}
            gatewayBaseUrl={gatewayBaseUrl}
            accessToken={String(accessToken)}
            companyId={subscriptionAccountId}
            readOnly={writeLocked}
            focusPortalId={credentialFocusPortal}
            onOpenSources={() => setSourcesOpenSignal((n) => n + 1)}
            onOpenTask={(id) => {
              const match = orderedBoard.find((row) => row.item.id === id);
              if (match) {
                setOpenBoardSectionId(match.pillarId);
              }
              setFocusDecisionId(id);
              setSoloTask(true);
              setLaneFilter(['ready', 'blocked', 'done']);
              setDodWorkspaceView('checklist');
              setTaskAssist({ id, mode: 'do' });
            }}
          />
        ) : null}
        {hasNamedCompany ? (
        <div className="dashboard-action-buttons dod-console-footer">
          <button
            type="button"
            className="dashboard-btn dashboard-btn-primary"
            onClick={() => { void persist({ ...state, updatedAt: new Date().toISOString() }); }}
          >
            {language === 'es' ? 'Guardar progreso' : 'Save progress'}
          </button>
          <button
            type="button"
            className="dod-console-reset"
            onClick={resetAll}
          >
            {language === 'es' ? 'Reiniciar lista' : 'Reset checklist'}
          </button>
        </div>
        ) : null}
        {saveFeedback ? (
          <p className={`dashboard-form-success${saveBusy ? ' is-busy' : ''}`} role="status" aria-live="polite">
            {saveBusy ? <span className="compliance-inline-spinner" aria-hidden="true" /> : null}
            {saveFeedback}
          </p>
        ) : null}
        {isWebsite && hasNamedCompany ? (
          <p className="compliance-console-mvp-note">
            {language === 'es'
              ? 'Escaneo real de homepage vía gateway (privacidad/cookies). Checklist completo + sincronización cuando hay sesión. Motor WCAG/seguridad completo fuera de alcance.'
              : 'Live homepage scan via gateway (privacy/cookies). Full checklist + sync when signed in. Full WCAG engine remains out of scope.'}
          </p>
        ) : null}
      </article>
    </section>
  );
};

export default ComplianceConsolePanel;
